/**
 * App shell and screen controllers.
 *
 * The whole UI renders in ONE language (see i18n.ts) chosen by the user, and the
 * board can draw traditional, simplified, or Latin-letter pieces (pieces.ts).
 * Every game screen supports read-only history review: you can step back to any
 * earlier position without altering the game.
 */

import {
  Color,
  GameResult,
  Game,
  START_FEN,
  coordToSquare,
  fileOf,
  PieceType,
  inCheck,
  isCheckmate,
  isSquareAttacked,
  isStalemate,
  legalMoves,
  moveToUci,
  opponent,
  parseFen,
  rankOf,
  toFen,
  type Move,
  type Position,
} from "@xq/rules";
import { endSlug } from "./endreason.js";
import { repetitionWarning, type RepetitionWarning } from "./repwarn.js";
import { pieceSet } from "./pieces.js";
import { LEVELS, type Level } from "@xq/engine";
import type { ColorPref, GameSnapshot, LiveGame, Seek, ServerMsg, TimeControlId } from "@xq/protocol";
import { DEFAULT_TC, TIME_CONTROLS, timeControl } from "@xq/protocol";
import { BoardView, repaintBoards } from "./board.js";
import { BOARD_THEMES, getBoardTheme, setBoardTheme } from "./board-themes.js";
import { Net } from "./net.js";
import { LESSONS, LESSON_GROUPS, applyLessonOverrides, doneSet, markDone as markDoneLocal, type Lesson, type Quiz } from "./lessons.js";
import { PUZZLES, applyPuzzleOverrides, solvedSet, markSolved } from "./puzzles.js";
import { dailyState, liveStreak, puzzleFor, recordDailySolve, solvedToday, today } from "./daily.js";
import { reviewGame, type GameReview, type Verdict } from "./review.js";
import { getLang, getSiteLocale, langChosen, pick, setLang, t, type Lang } from "./i18n.js";
import {
  adoptServerChoice, answered as feedbackAnswered, remember as rememberPrompt,
  snooze as snoozePrompt, startFeedbackPrompt,
} from "./feedback-prompt.js";
import * as account from "./account.js";
import { initCinematic, dismissLoader, victoryBurst, getWeather, setWeather, getTimeOfDay, setTimeOfDay, type Weather, type TimeOfDay } from "./cinematic.js";
import { getPieceStyle, setPieceStyle, type PieceStyle } from "./pieces.js";
import { playCapture, playCheck, playEnd, playMove, playNotify, playTick, soundEnabled, speak, stopSpeaking, toggleSound, unlockAudio } from "./sound.js";
import { animOn, hintsOn, setAnim, setHints, setVoice, toggleHints, toggleVoice, voiceOn } from "./prefs.js";
import { moveToChinese, moveToSpokenEn } from "./notation.js";
import { initAnalytics, pageView, reportError, screen as trackScreen, track } from "./analytics.js";
import { disablePush, enablePush, pushState, pushSupported, syncPushSubscription, type PushState } from "./push.js";
import { currentPath, navigate, page, setStuckPath, start as startRouter, type Route } from "./router.js";

declare const __BUILD__: string;

/**
 * Static art is cached for a month by nginx, so a redeploy that shrinks or
 * changes an image would otherwise not reach anyone who has already visited.
 * The build stamp in the URL is what makes a new deploy take effect at once.
 */
const ASSET_V = `?v=${__BUILD__}`;
/* Told to the analytics tracker, which is shared with the content site and so
   cannot read this app's compile-time constant. Every error it reports then
   carries the build that threw it, which is the first thing anybody asks after
   a deploy. */
(globalThis as { __XQ_BUILD__?: string }).__XQ_BUILD__ = `app ${__BUILD__}`;

/**
 * The logo, in the variant the current theme can actually show.
 *
 * The artwork is drawn for a light ground: the brush "X" and the word XIANGQI
 * are near-black, so on the dark theme they sank into the background and the
 * mark read as a floating red "M". `-dark` versions recolour just the
 * achromatic brushwork to cream and leave the cinnabar and gold alone.
 *
 * Chosen in JS rather than with two <img> tags, because a hidden <img> is still
 * downloaded — this way a visitor fetches one logo, not both.
 */
function logoSrc(base: "logo-full" | "logo-mark" | "logo-nav"): string {
  return `${base}${getTheme() === "dark" ? "-dark" : ""}.png${ASSET_V}`;
}

/**
 * The header's icons, drawn rather than typed. They used to be the text glyphs
 * ☀ ☰ and the 🔔 emoji, which take whatever font the device has: three different
 * weights, a colour emoji beside two monochrome glyphs, and edges that go soft at
 * 20px. These share one 24-unit grid, one stroke weight and the button's own
 * colour, so they match each other and stay crisp at any size.
 */
const NAV_ICON = {
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6"/>',
  moon: '<path d="M20.5 13.6A8.5 8.5 0 1 1 10.4 3.5a6.8 6.8 0 0 0 10.1 10.1z"/>',
  bell: '<path d="M6 9.5a6 6 0 0 1 12 0c0 6.5 2.6 8 2.6 8H3.4S6 16 6 9.5"/><path d="M10.2 20.8a2 2 0 0 0 3.6 0"/>',
  menu: '<path d="M4 7h16M4 12h16M4 17h16"/>',
} as const;
function navIcon(name: keyof typeof NAV_ICON): HTMLElement {
  return h("span", {
    class: "nav-ico", "aria-hidden": "true",
    html: `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${NAV_ICON[name]}</svg>`,
  });
}

/** Re-point every logo after the theme changes, without a full repaint. */
function paintLogos(): void {
  document.querySelectorAll<HTMLImageElement>("img[data-logo]").forEach((img) => {
    const base = img.dataset.logo as "logo-full" | "logo-mark" | "logo-nav";
    const next = logoSrc(base);
    if (!img.src.endsWith(next)) img.src = next;
  });
}

/**
 * A link to the reference site (the apex domain), tagged with where it was
 * clicked so the two halves of the funnel can be told apart in analytics.
 *
 * The content site is trilingual — English, Simplified and Traditional — so
 * this honours the tree the reader actually came from. Sending a Chinese
 * reader to the English tree is a worse dead end than it looks: the content
 * site has no auto-redirect, so they would have to find the language switcher
 * themselves.
 */
function siteUrl(path: string, from: string): string {
  // getSiteLocale, not getLang: the app UI has no Traditional mode, but the
  // content site does, and a reader who came from /zh-hant should go back to
  // /zh-hant rather than being dropped into the Simplified tree.
  const locale = getSiteLocale();
  const prefix = locale === "en" ? "" : `/${locale}`;
  const u = new URL(prefix + (path === "/" ? "" : path) || "/", "https://xiangqimaster.com");
  u.searchParams.set("from", from);
  // The site remembers ?lang= as the reader's choice, so a reader who picked a
  // language in Settings is not redirected to another one there (layout.mjs).
  // Only a real choice is passed: a detected language is the site's to detect.
  if (langChosen()) u.searchParams.set("lang", locale);
  return u.toString();
}

/**
 * The home hero board. WebP where it is understood, JPEG for everything else —
 * `<picture>` is the one place we can offer a fallback, so it is the one place
 * the smaller format is safe to use. Dimensions are stated to reserve the space
 * before the bytes land, which is what keeps the hero from shifting (CLS).
 */
function heroBoardImage(): HTMLElement {
  const pic = document.createElement("picture");
  const webp = document.createElement("source");
  webp.type = "image/webp";
  webp.srcset = `board-start.webp${ASSET_V}`;
  pic.append(webp, h("img", {
    src: `board-start.jpg${ASSET_V}`,
    alt: t("heroBoardAlt"),
    width: "900",
    height: "900",
    decoding: "async",
    fetchpriority: "high",
  }));
  return pic;
}

/**
 * Every page of the site, in one place. The nav links, the route table and the
 * screens themselves all read from here, so a link, a deep link and the address
 * bar can never disagree about where a page lives.
 */
const PATH = {
  home: "/",
  play: "/play",
  bot: "/play/computer",
  private: "/play/private",
  game: "/game",
  botGame: "/game/computer",
  replay: (id: string) => `/game/${encodeURIComponent(id)}`,
  join: (code: string) => `/join/${encodeURIComponent(code)}`,
  watch: "/watch",
  watchGame: (id: string) => `/watch/${encodeURIComponent(id)}`,
  learn: "/learn",
  lessons: "/learn/lessons",
  lesson: (id: string) => `/learn/lessons/${encodeURIComponent(id)}`,
  puzzles: "/learn/puzzles",
  puzzle: (id: string) => `/learn/puzzles/${encodeURIComponent(id)}`,
  daily: "/daily",
  rules: "/learn/rules",
  analysis: "/analysis",
  leaderboard: "/leaderboard",
  friends: "/friends",
  profile: "/profile",
  player: (name: string) => `/player/${encodeURIComponent(name)}`,
  settings: "/settings",
  messages: "/messages",
  thread: (user: string) => `/messages/${encodeURIComponent(user)}`,
  login: "/login",
  signup: "/signup",
  forgot: "/forgot",
  verify: "/verify",
} as const;

const app = document.getElementById("app")!;
// The palace photo only sits behind actual board screens; menus stay clean.
function screen(name: string) {
  trackScreen(name);
  if (name !== "friends") friendsRefresh = null; // stop stale friend-list repaints off-screen
  // The home hero and the Play hub both read the lobby feed; anywhere else we
  // unsubscribe so the server stops pushing updates we cannot paint.
  if (name !== "play" && name !== "home") {
    if (lobbyTarget) { lobbyTarget = null; net?.send({ t: "lobby", on: false }); }
  }
  // Same for the two spectating subscriptions: the list of games in play, and
  // the game itself. Leaving the page stops the feed rather than leaving the
  // server pushing positions at a screen that is no longer there.
  if (name !== "watch") {
    if (liveGamesTarget) { liveGamesTarget = null; net?.send({ t: "liveGames", on: false }); }
  }
  if (name !== "watch_game") { watchView?.close(); watchView = null; }
  document.documentElement.dataset.view = (name.startsWith("game_") || name === "lesson") ? "game" : "menu";
  // The exact screen, not just the board-or-menu question `view` answers. The
  // feedback prompt needs to tell a puzzle from a leaderboard, and reading one
  // attribute is cheaper than exporting a getter through this module.
  document.documentElement.dataset.screen = name;
  // The phone chrome — player rows, the live strip, the dock, the panel as a
  // sheet — belongs to the screens you PLAY on. A lesson also sets data-view
  // to "game" for the backdrop, and its panel must stay a panel.
  document.documentElement.dataset.dock = name.startsWith("game_") ? "on" : "";
  pageIn();
}

/**
 * Replay the page-entry animation (the rules are in the stylesheet).
 *
 * The class comes off again once the animation has run, and that is not
 * housekeeping: the keyframes end on `transform: none` with fill-mode `both`,
 * and a filled animation beats a declared rule — leave it on and every card's
 * `:hover` lift is dead for the rest of the visit.
 */
/**
 * Placeholder rows for a list that has been asked for and has not arrived.
 * A bare "…" made a fast connection look broken and a slow one look empty;
 * rows of the right shape say "this is a list, and it is coming".
 */
function skeletonRows(n: number): HTMLElement[] {
  return Array.from({ length: n }, () => h("div", { class: "skeleton skeleton-row" }));
}

let pageInTimer = 0;
function pageIn() {
  app.classList.remove("page-in");
  void app.offsetWidth; // force a restart when the same screen repaints
  app.classList.add("page-in");
  clearTimeout(pageInTimer);
  pageInTimer = window.setTimeout(() => app.classList.remove("page-in"), 900);
}

/**
 * Enter a page: its analytics name, the URL it lives at, and the title that
 * goes in the browser tab and the history entry. Every screen calls this as it
 * paints, which is what keeps the address bar honest whether the screen was
 * reached by a link, a button, the back button or a cold deep link.
 */
function enter(name: string, path: string, title: string, noindex = false) {
  // Any other screen being painted means the bot board is no longer on display.
  if (name !== "game_bot") localGame = null;
  // Leaving Settings with edits that were never saved discards them.
  if (name !== "settings" && settingsBaseline) {
    const b = settingsBaseline;
    settingsBaseline = null;
    if (JSON.stringify(captureSettings()) !== JSON.stringify(b)) revertSettings(b);
    settingsDraftEmail = null;
    settingsDraftDaily = null;
  }
  screen(name);
  page(path, title, noindex);
}
function markDone(id: string) {
  markDoneLocal(id);
  void account.markLessonDone(id); // no-op when signed out
}
const RESUME_BOT = "xq-resume-bot";
const LAST_MODE = "xq-last-mode";
const rememberMode = (m: "online" | "friends") => {
  try { localStorage.setItem(LAST_MODE, m); } catch { /* ignore */ }
};

// ---- preferred pool (remembered between visits) ----
const TC_KEY = "xq-tc";
const RATED_KEY = "xq-rated";
function getTc(): TimeControlId {
  try { return timeControl(localStorage.getItem(TC_KEY)).id; } catch { return DEFAULT_TC; }
}
function setTc(v: TimeControlId) {
  try { localStorage.setItem(TC_KEY, v); } catch { /* ignore */ }
}
function getRated(): boolean {
  try { return localStorage.getItem(RATED_KEY) !== "0"; } catch { return true; }
}
function setRated(v: boolean) {
  try { localStorage.setItem(RATED_KEY, v ? "1" : "0"); } catch { /* ignore */ }
}
const catKey = { bullet: "catBullet", blitz: "catBlitz", rapid: "catRapid", classical: "catClassical" } as const;
/** "5+0 Blitz" — the pool as players talk about it. */
const tcLabel = (id: TimeControlId | undefined) => {
  const c = timeControl(id);
  return `${c.id} ${t(catKey[c.cat])}`;
};
let net: Net | null = null;
let nickname = localStorage.getItem("xq-name") || "Guest";

// ---- colour theme (light "Ivory Atelier" | dark "Imperial Nightfall") ----
type Theme = "light" | "dark";
function getTheme(): Theme {
  try { return localStorage.getItem("xq-theme") === "dark" ? "dark" : "light"; } catch { return "light"; }
}
function setTheme(v: Theme) {
  try { localStorage.setItem("xq-theme", v); } catch { /* ignore */ }
  document.documentElement.dataset.theme = v;
  // The logo art differs per theme, and the switch can happen on any screen —
  // doing it here means no call site can forget.
  paintLogos();
}

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<Record<string, unknown>> = {},
  ...kids: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (k === "class") el.className = String(v);
    else if (k === "html") el.innerHTML = String(v);
    else if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (v !== undefined && v !== null) el.setAttribute(k, String(v));
  }
  el.append(...kids);
  return el;
}

/**
 * The dropdown chevron. It was a "▾" text glyph at 9-11px, which each font
 * draws at its own size and height: in the nav it came out as a speck sitting
 * above the label's centre line, and "Play" and "Learn" barely read as menus.
 * The same 12x12 stroke the content site's header uses, so the two headers
 * match; it inherits the button's colour and turns with `.open .caret`.
 */
const caretIcon = (): HTMLElement =>
  h("span", {
    class: "caret", "aria-hidden": "true",
    html: '<svg viewBox="0 0 12 12" width="12" height="12" focusable="false"><path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  });

const clockStr = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const coord = (m: Move) =>
  `${"abcdefghi"[fileOf(m.from)]}${rankOf(m.from)}–${"abcdefghi"[fileOf(m.to)]}${rankOf(m.to)}`.toUpperCase();
/** The same thing for a move that only exists as UCI, as the engine reports it. */
const uciCoord = (uci: string) =>
  uci.length === 4 ? `${uci.slice(0, 2)}–${uci.slice(2)}`.toUpperCase() : uci.toUpperCase();
const countPieces = (p: Position) => {
  let n = 0;
  for (let i = 0; i < 90; i++) if (p.pieceAt(i)) n++;
  return n;
};
// Starting count of each piece type per side: King, Advisor, Elephant, Horse, Rook, Cannon, Pawn.
const INITIAL_COUNTS = [1, 2, 2, 2, 2, 2, 5];

/** Pieces of `color` that have been captured, drawn as a small row of discs. */
function fillTray(el: HTMLElement, pos: Position, color: Color) {
  const have = new Array(7).fill(0) as number[];
  for (let sq = 0; sq < 90; sq++) {
    const p = pos.pieceAt(sq);
    if (p && p.color === color) have[p.type]! += 1;
  }
  const set = pieceSet();
  const glyphs = color === Color.Red ? set.red : set.black;
  el.replaceChildren();
  let n = 0;
  for (let ty = 6; ty >= 0; ty--) {
    for (let i = 0; i < INITIAL_COUNTS[ty]! - have[ty]!; i++) {
      el.append(h("span", { class: `cap ${color === Color.Red ? "cap-red" : "cap-black"}` }, glyphs[ty]!));
      n++;
    }
  }
  if (n === 0) el.append(h("span", { class: "tray-empty" }, "—"));
}

/** Replay the first `ply` moves from the initial position. */
function replayTo(moves: Move[], ply: number): Position {
  const p = parseFen(START_FEN);
  for (let i = 0; i < Math.min(ply, moves.length); i++) p.makeMove(moves[i]!);
  return p;
}

// Keyboard history navigation, owned by whichever game screen is active.
type NavCmd = -1 | 1 | "start" | "live";
let navHandler: ((c: NavCmd) => void) | null = null;
document.addEventListener("keydown", (e) => {
  if (!navHandler) return;
  if (e.key === "ArrowLeft") { navHandler(-1); e.preventDefault(); }
  else if (e.key === "ArrowRight") { navHandler(1); e.preventDefault(); }
  else if (e.key === "Home") { navHandler("start"); e.preventDefault(); }
  else if (e.key === "End") { navHandler("live"); e.preventDefault(); }
});

// ---------------------------------------------------------------- modal
type Action = [label: string, fn: () => void, primary?: boolean];
function closeModal() {
  document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
}
function showModal(
  title: string, sub: string, actions: Action[],
  tone: "win" | "lose" | "draw" | "info" = "info",
  /**
   * An optional line BELOW the buttons. A secondary offer added as a fifth action
   * competes with the primary one and wraps the row on a phone; as a quiet line
   * underneath it is available without being in the way.
   */
  foot?: Node,
) {
  closeModal();
  const box = h("div", { class: `modal tone-${tone}` },
    h("div", { class: "modal-title" }, title),
    h("div", { class: "modal-sub" }, sub),
    h("div", { class: "modal-actions" }, ...actions.map(([l, fn, pri]) => h("button", { class: "btn " + (pri ? "primary" : "ghost"), onclick: () => { closeModal(); fn(); } }, l))),
    ...(foot ? [h("div", { class: "modal-foot" }, foot)] : []),
  );
  document.body.append(h("div", { class: "modal-backdrop", onclick: (e: Event) => { if (e.target === e.currentTarget) closeModal(); } }, box));
}

/* ------------------------------------------------------- the feedback prompt */

/**
 * Is now somebody else's moment?
 *
 * Everything on this list is a thing a person is in the middle of, where an
 * interruption costs them something they cannot get back: a move in a live
 * game, a puzzle they were about to solve, a sentence they were typing. The
 * prompt waits rather than cancelling, so a bad moment only delays it.
 */
function feedbackMomentIsBad(): boolean {
  const s = document.documentElement.dataset.screen ?? "";
  if (s.startsWith("game_") || s === "lesson" || s === "puzzles" || s === "daily"
      || s === "matchmaking" || s === "resuming" || s === "watch_game") return true;
  // Never stack on top of another dialog, including the game-over modal.
  if (document.querySelector(".modal-backdrop")) return true;
  const el = document.activeElement as HTMLElement | null;
  if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return true;
  return false;
}

/** Store the answer here and, when there is an account, on the account. */
function rememberFeedbackChoice(choice: "never" | "sent"): void {
  rememberPrompt(choice);
  void account.setFeedbackPrompt(choice === "never" ? "declined" : "submitted");
}

/**
 * The ask itself: three answers, and two of them cost one click.
 *
 * Escape and a click outside both mean "not now" rather than nothing, because
 * a dialog that can be dismissed without answering would ask again in fifteen
 * minutes and feel broken. Only the explicit "No, thanks" is permanent.
 */
function showFeedbackAsk(): void {
  track("feedback_prompt_shown", {});
  const close = () => { document.removeEventListener("keydown", onKey, true); closeModal(); };
  const later = () => { track("feedback_prompt_dismissed", {}); snoozePrompt(); close(); };
  const never = () => { track("feedback_prompt_declined", {}); rememberFeedbackChoice("never"); close(); };
  const yes = () => { track("feedback_prompt_accepted", {}); close(); showFeedback("prompt"); };
  // Choosing the Discord over the form is not a "no": it is the same answer by
  // another road, so the prompt is only snoozed and the dialog gets out of the way.
  const discord = () => { snoozePrompt(); close(); };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); later(); return; }
    if (e.key !== "Tab") return;
    // Focus trap: a dialog you can Tab out of leaves a keyboard user stranded
    // behind a backdrop they cannot see past.
    const focusable = [...box.querySelectorAll<HTMLElement>("button, a[href]")];
    if (!focusable.length) return;
    const first = focusable[0]!, last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const yesBtn = h("button", { class: "btn primary", type: "button", onclick: yes }, t("fbAskYes"));
  const box = h("div", {
    class: "modal tone-info fb-ask", role: "dialog", "aria-modal": "true",
    "aria-labelledby": "fb-ask-title", "aria-describedby": "fb-ask-sub",
  },
    h("div", { class: "modal-title", id: "fb-ask-title" }, t("fbAskTitle")),
    h("div", { class: "modal-sub", id: "fb-ask-sub" }, t("fbAskSub")),
    h("div", { class: "modal-actions" },
      h("button", { class: "btn ghost", type: "button", onclick: never }, t("fbAskNever")),
      h("button", { class: "btn ghost", type: "button", onclick: later }, t("fbAskLater")),
      yesBtn,
    ),
    feedbackDiscord("feedback_ask", discord),
  );
  document.body.append(h("div", {
    class: "modal-backdrop",
    onclick: (e: Event) => { if (e.target === e.currentTarget) later(); },
  }, box));
  document.addEventListener("keydown", onKey, true);
  yesBtn.focus();
}

/**
 * The feedback box.
 *
 * Open to anyone, signed in or not: the reports worth having most often come from
 * someone whose experience went wrong before they ever made an account, and a
 * sign-in wall filters out precisely those. The server records the account only
 * when one happens to be present.
 *
 * Sent along with the message: the current path, because "it broke" without a
 * page is unanswerable, and the build, because the first question about any bug is
 * which version saw it. No IP is stored anywhere — it would not help us answer
 * anybody, and it would turn a suggestion box into a record of who complained.
 */
/**
 * The quiet offer to tell us, for the end of a game.
 *
 * This is when someone is most willing to say what went wrong: it just went
 * wrong and they are already looking at a dialog. A link rather than a button, so
 * it never competes with Rematch or Review, and it passes the result through so a
 * report arrives with its context already attached.
 */
/**
 * The way into the Discord from inside the feedback dialogs.
 *
 * Some people would rather talk than fill in a form, and a problem is often
 * sorted faster in a conversation than in one message with no reply address.
 * Drawn hidden and filled in once the invite arrives, so a deployment without
 * one shows nothing rather than a dead link. Following it counts as having been
 * asked, so the separate Discord invitation never repeats the offer.
 */
function feedbackDiscord(where: "feedback" | "feedback_ask", after?: () => void): HTMLElement {
  const slot = h("div", { class: "fb-discord hide" });
  void account.discordInvite().then((invite) => {
    if (!invite) return;
    const go = h("a", { class: "fb-discord-go", href: invite, target: "_blank", rel: "noopener" }, t("fbDiscordJoin"));
    go.addEventListener("click", () => {
      track("discord_join", { why: where });
      markDiscordAsked();
      after?.();
    });
    slot.append(
      h("span", { class: "fb-discord-badge", "aria-hidden": "true", html: CHAT_ICON }),
      h("span", { class: "fb-discord-copy" },
        h("span", { class: "fb-discord-title" }, t("fbDiscordTitle")),
        h("span", { class: "fb-discord-text" }, t("fbDiscordText")),
      ),
      go,
    );
    slot.classList.remove("hide");
  });
  return slot;
}

/** A speech bubble: "come and talk", without borrowing anybody's logo. */
const CHAT_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 10h.01M12 10h.01M16 10h.01" stroke-width="3"/></svg>';

function feedbackFoot(outcome: string, mode: string): Node {
  const link = h("button", { class: "linklike", type: "button" }, t("fbFromGame"));
  link.addEventListener("click", () => { closeModal(); showFeedback(`${mode} game, ${outcome}`); });
  return link;
}

function showFeedback(context = "") {
  closeModal();
  const KINDS: [string, string][] = [
    ["bug", t("fbBug")],
    ["idea", t("fbIdea")],
    ["praise", t("fbPraise")],
    ["other", t("fbOther")],
  ];
  let kind = "bug";

  const kindRow = h("div", { class: "fb-kinds" },
    ...KINDS.map(([k, label]) => {
      const b = h("button", { class: "btn ghost xs" + (k === kind ? " on" : ""), type: "button" }, label);
      b.addEventListener("click", () => {
        kind = k;
        kindRow.querySelectorAll("button").forEach((x) => x.classList.remove("on"));
        b.classList.add("on");
      });
      return b;
    }),
  );

  /* The rating. Five radio buttons in a real fieldset rather than five styled
     divs, so a keyboard moves through them with the arrow keys and a screen
     reader announces "3 of 5" without being told to. Skippable: a rating
     nobody wanted to give is worse data than no rating. */
  let rating = 0;
  const rateButtons: HTMLButtonElement[] = [];
  const paintRating = () => rateButtons.forEach((b, i) => {
    const on = i < rating;
    b.classList.toggle("on", on);
    b.setAttribute("aria-checked", String(i + 1 === rating));
    b.textContent = on ? "★" : "☆";
  });
  const rateRow = h("div", {
    class: "fb-rate", role: "radiogroup", "aria-label": t("fbRate"),
  }, ...[1, 2, 3, 4, 5].map((n) => {
    const b = h("button", {
      class: "fb-star", type: "button", role: "radio", "aria-checked": "false",
      "aria-label": `${n} / 5${n === 1 ? ` — ${t("fbRate1")}` : n === 5 ? ` — ${t("fbRate5")}` : ""}`,
    }, "☆") as HTMLButtonElement;
    b.addEventListener("click", () => { rating = n; paintRating(); });
    b.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      rating = Math.min(5, Math.max(1, (rating || n) + (e.key === "ArrowRight" ? 1 : -1)));
      paintRating();
      rateButtons[rating - 1]?.focus();
    });
    rateButtons.push(b);
    return b;
  }));

  /* The honeypot. Hidden from people and from screen readers, left in the DOM
     for anything filling every field it finds. A real submission never sets it. */
  const trap = h("input", {
    class: "fb-trap", type: "text", name: "website", tabindex: "-1",
    autocomplete: "off", "aria-hidden": "true",
  }) as HTMLInputElement;

  const msg = h("textarea", {
    class: "fb-msg", rows: "5", maxlength: "6000",
    placeholder: t("fbPlaceholder"), "aria-label": t("fbImprove"),
  }) as HTMLTextAreaElement;
  const email = h("input", {
    class: "fb-email", type: "email", maxlength: "200", placeholder: t("fbEmailHint"),
    "aria-label": t("fbEmailHint"),
  }) as HTMLInputElement;
  const note = h("div", { class: "fb-note", "aria-live": "polite" });
  const send = h("button", { class: "btn primary", type: "button" }, t("fbSend"));

  const close = () => closeModal();
  send.addEventListener("click", async () => {
    const text = msg.value.trim();
    if (!text) { note.textContent = t("fbEmpty"); note.className = "fb-note warn"; msg.focus(); return; }
    send.toggleAttribute("disabled", true);
    note.className = "fb-note";
    note.textContent = t("fbSending");
    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Sent when there is one, so the message can be attributed; never required.
          ...(account.getToken() ? { Authorization: `Bearer ${account.getToken()}` } : {}),
        },
        body: JSON.stringify({
          kind,
          message: text,
          email: email.value.trim(),
          rating,
          // Attached rather than asked for: three more questions to answer
          // would cost more replies than the answers are worth.
          lang: getLang(),
          device: `${matchMedia("(pointer: coarse)").matches ? "touch" : "pointer"} ${innerWidth}x${innerHeight}`,
          website: trap.value,
          // Sent alongside, never typed into the field: putting our words in the
          // box would mean the person has to delete them before writing theirs.
          page: context ? `${location.pathname} (${context})` : location.pathname,
          client: `app ${__BUILD__}`,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      track("feedback_sent", { kind, rating });
      // Somebody who has written to us is not asked again — here, and on their
      // other devices once they sign in. The server records it too when the
      // request carried a token; this covers the signed-out case.
      rememberFeedbackChoice("sent");
      note.className = "fb-note ok";
      note.textContent = t("fbThanks");
      send.remove();
      setTimeout(close, 1600);
    } catch {
      // The text stays on screen. Eating a long message silently is the one
      // failure a feedback box must not have.
      send.toggleAttribute("disabled", false);
      note.className = "fb-note warn";
      note.textContent = t("fbFailed");
    }
  });

  const box = h("div", { class: "modal tone-info fb-modal" },
    h("div", { class: "modal-title" }, t("fbTitle")),
    h("div", { class: "modal-sub" }, t("fbSub")),
    h("div", { class: "fb-rate-row" },
      h("div", { class: "fb-rate-head" },
        h("span", { class: "fb-rate-label" }, t("fbRate")),
        h("span", { class: "fb-rate-skip" }, t("fbRateSkip")),
      ),
      rateRow,
    ),
    kindRow,
    msg,
    email,
    trap,
    note,
    h("div", { class: "modal-actions" }, h("button", { class: "btn ghost", type: "button", onclick: close }, t("cancel")), send),
    feedbackDiscord("feedback"),
  );
  document.body.append(h("div", { class: "modal-backdrop", onclick: (e: Event) => { if (e.target === e.currentTarget) close(); } }, box));
  msg.focus();
}

/**
 * The invitation to the Discord.
 *
 * Shown once per browser and never again, whether it was accepted or waved away
 * -- a prompt that reappears is an irritation, and an irritation is not an
 * invitation. It waits for a natural pause (a game just finished, or a minute of
 * reading) rather than interrupting a board, it is silent on a deployment with no
 * invite configured, and closing it counts as an answer.
 */
const DISCORD_ASKED = "xq-discord-asked";

function discordAsked(): boolean {
  try { return localStorage.getItem(DISCORD_ASKED) === "1"; } catch { return true; }
}
function markDiscordAsked(): void {
  try { localStorage.setItem(DISCORD_ASKED, "1"); } catch { /* private window: it will ask once more */ }
}

/** Offer the Discord, if there is one and we have not asked before. */
function maybeAskDiscord(why: "game_end" | "dwell"): void {
  if (discordAsked()) return;
  // Never over a live board or another modal: the point is a pause, not an
  // interruption.
  if (remote?.liveSnapshot() || document.querySelector(".modal-backdrop")) return;
  void account.discordInvite().then((invite) => {
    if (!invite || discordAsked()) return;
    if (remote?.liveSnapshot() || document.querySelector(".modal-backdrop")) return;
    markDiscordAsked();
    track("discord_prompt", { why });
    showDiscordInvite(invite, why);
  });
}

/**
 * The invitation card. Its own dialog rather than showModal's, which is sized
 * for a game result and stretched this one line of copy into a banner across
 * the page. The join is a real link, so it opens a tab the way any link does.
 */
function showDiscordInvite(invite: string, why: string): void {
  closeModal();
  const dismiss = () => { track("discord_dismiss", { why }); closeModal(); };
  const join = h("a", { class: "fb-discord-go", href: invite, target: "_blank", rel: "noopener" }, t("discordJoin"));
  join.addEventListener("click", () => { track("discord_join", { why }); closeModal(); });
  const box = h("div", {
    class: "modal tone-info dc-modal", role: "dialog", "aria-modal": "true", "aria-labelledby": "dc-title",
  },
    h("div", { class: "dc-badge", "aria-hidden": "true", html: CHAT_ICON }),
    h("div", { class: "modal-title", id: "dc-title" }, t("discordTitle")),
    h("div", { class: "modal-sub" }, t("discordBody")),
    h("div", { class: "modal-actions" },
      h("button", { class: "btn ghost", type: "button", onclick: dismiss }, t("discordNo")),
      join,
    ),
  );
  document.body.append(h("div", {
    class: "modal-backdrop",
    onclick: (e: Event) => { if (e.target === e.currentTarget) dismiss(); },
  }, box));
}

/**
 * Arm the quiet trigger: someone who has been reading for a minute is a better
 * person to ask than someone who has just arrived.
 */
function armDiscordPrompt(): void {
  if (discordAsked()) return;
  // Tried again periodically rather than exactly once: the first opportunity
  // often arrives while a board or a result modal is open, which are precisely
  // the moments not to interrupt. It stops the moment the question is asked.
  const attempt = () => {
    if (discordAsked()) return;
    maybeAskDiscord("dwell");
    setTimeout(attempt, 45_000);
  };
  setTimeout(attempt, 60_000);
}

// ---------------------------------------------------------------- navigation
/**
 * One entry in a nav dropdown. The same list drives the desktop menu and the
 * mobile drawer, so "all the ways to play" can never drift between the two.
 */
interface MenuEntry {
  /** i18n key for the label */
  key: string;
  /** i18n key for the one-line description under the label */
  sub?: string;
  /** Chinese glyph used as the menu icon */
  glyph: string;
  /** The page this entry opens. Entries with a path render as real links. */
  path?: string;
  /** An entry that performs an action instead of opening a page. */
  run?: () => void;
}

/**
 * Everything you can do from "Play". The computer and the lobby open for
 * anyone; quick match, a private room and friends still ask for an account,
 * each at the point it actually needs one.
 */
function playEntries(): MenuEntry[] {
  const live = remote?.liveSnapshot();
  return [
    ...(live ? [{ key: "rejoin", sub: "rejoinSub", glyph: "戰", path: PATH.game }] : []),
    { key: "quickMatch", sub: "quickMatchSub", glyph: "快", run: () => requireAuth(() => startOnline()) },
    { key: "openChallenges", sub: "lobbySub", glyph: "榜", path: PATH.play },
    { key: "watchTitle", sub: "watchSub", glyph: "觀", path: PATH.watch },
    { key: "playBot", sub: "playBotSub", glyph: "帥", path: PATH.bot },
    { key: "playFriends", sub: "playFriendsSub", glyph: "炮", path: PATH.private },
    { key: "friends", sub: "friendsSub", glyph: "馬", path: PATH.friends },
  ];
}

/** Study is open to everyone — no account needed to read or train. */
function learnEntries(): MenuEntry[] {
  return [
    { key: "lessonsTitle", sub: "lessonsSub", glyph: "課", path: PATH.lessons },
    { key: "puzzles", sub: "puzzlesSub", glyph: "題", path: PATH.puzzles },
    { key: "analysis", sub: "analysisSub", glyph: "研", path: PATH.analysis },
    { key: "guideTitle", sub: "guideSub", glyph: "書", path: PATH.rules },
    { key: "learnTitle", sub: "learnSub", glyph: "學", path: PATH.learn },
  ];
}

function accountEntries(): MenuEntry[] {
  return [
    { key: "profile", sub: "profileSub", glyph: "士", path: PATH.profile },
    { key: "friends", sub: "friendsSub", glyph: "馬", path: PATH.friends },
    { key: "messages", sub: "messagesSub", glyph: "信", path: PATH.messages },
    { key: "settings", sub: "settingsSub", glyph: "設", path: PATH.settings },
  ];
}

// Only one menu is open at a time; a click anywhere else or Escape closes it.
let openMenu: HTMLElement | null = null;
function closeMenus() {
  openMenu?.classList.remove("open");
  openMenu?.querySelector("[aria-expanded]")?.setAttribute("aria-expanded", "false");
  openMenu = null;
  document.querySelector(".nav-drawer.open")?.classList.remove("open");
}
document.addEventListener("click", (e) => {
  const target = e.target as Node;
  if (openMenu && !openMenu.contains(target)) closeMenus();
  // The drawer is not a `.nav-item`, so it needs its own outside-click dismissal.
  const drawer = document.querySelector(".nav-drawer.open");
  if (drawer && !drawer.contains(target)) drawer.classList.remove("open");
});
document.addEventListener("keydown", (e) => {
  if ((e as KeyboardEvent).key !== "Escape") return;
  closeMenus();
});

/**
 * A menu row. Entries that open a page are anchors — they show their address,
 * open in a new tab on ctrl/middle click, and are followed by the router on a
 * plain click. Only entries that *do* something (queue for a match) are buttons.
 */
function menuButton(e: MenuEntry, run: (fn: () => void) => () => void): HTMLElement {
  const inner = [
    h("span", { class: "menu-glyph" }, e.glyph),
    h("span", { class: "menu-text" },
      h("span", { class: "menu-label" }, t(e.key)),
      ...(e.sub ? [h("span", { class: "menu-sub" }, t(e.sub))] : []),
    ),
  ];
  return e.path
    ? h("a", { class: "menu-item", href: e.path }, ...inner)
    : h("button", { class: "menu-item", onclick: run(e.run ?? (() => {})) }, ...inner);
}

/**
 * Pull the rating down again and update the header chip in place. Called when a
 * game finishes, because that is exactly when the number changes.
 */
// ---------------------------------------------------------------- notifications
/**
 * The bell in the header. Counts live in one place and every screen that draws
 * a header picks them up, so a notification arriving mid-game still shows.
 */
let inbox: account.Inbox = { notifications: [], unread: 0, unreadMessages: 0 };
let inboxLoaded = false;

function paintBellBadges(): void {
  const total = inbox.unread;
  for (const el of document.querySelectorAll(".bell-badge")) {
    el.textContent = total > 9 ? "9+" : String(total);
    (el as HTMLElement).classList.toggle("hide", total === 0);
  }
}

async function loadInbox(force = false): Promise<void> {
  if (!account.isLoggedIn()) { inbox = { notifications: [], unread: 0, unreadMessages: 0 }; paintBellBadges(); return; }
  if (inboxLoaded && !force) { paintBellBadges(); return; }
  inbox = await account.fetchNotifications();
  inboxLoaded = true;
  paintBellBadges();
}

const NOTIFY_TEXT: Record<account.NotifyKind, string> = {
  friend_request: "nFriendRequest", friend_accepted: "nFriendAccepted",
  challenge: "nChallenge", message: "nMessage",
};
const NOTIFY_GLYPH: Record<account.NotifyKind, string> = {
  friend_request: "馬", friend_accepted: "馬", challenge: "帥", message: "信",
};
const NOTIFY_PATH: Record<account.NotifyKind, (n: account.Notification) => string> = {
  friend_request: () => PATH.friends,
  friend_accepted: () => PATH.friends,
  challenge: () => PATH.play,
  message: (n) => PATH.thread(n.fromUser),
};

/** One row in the bell menu. */
function notificationRow(n: account.Notification): HTMLElement {
  return h("a", {
    class: "notif" + (n.read ? "" : " unread"),
    href: NOTIFY_PATH[n.kind](n),
    onclick: () => { void account.markNotificationsRead([n.id]).then((u) => { inbox.unread = u; n.read = true; paintBellBadges(); }); },
  },
    h("span", { class: "notif-glyph" }, NOTIFY_GLYPH[n.kind]),
    h("span", { class: "notif-body" },
      h("span", { class: "notif-text" }, h("b", {}, n.fromName), " ", t(NOTIFY_TEXT[n.kind])),
      ...(n.text ? [h("span", { class: "notif-preview" }, n.text)] : []),
      h("span", { class: "notif-ago" }, timeAgo(n.at)),
    ),
  );
}

async function syncRating(): Promise<void> {
  if (!account.isLoggedIn()) return;
  const rating = await account.refreshRating();
  if (rating == null) return;
  for (const el of document.querySelectorAll(".acct-chip-rating")) {
    el.textContent = String(rating);
    (el as HTMLElement).classList.remove("hide");
  }
}

function header(active = "") {
  closeMenus();
  // Nav actions respect the sticky-room lock: mid-game they prompt instead of leaving.
  const go = (fn: () => void) => () => { closeMenus(); if (gameLive()) { guardLeaveGame(fn); return; } unlockAudio(); fn(); };
  // A menu entry may itself be "return to your live game", so it bypasses the guard.
  const run = (fn: () => void) => () => { closeMenus(); unlockAudio(); fn(); };

  const link = (key: string, id: string, path: string) =>
    h("a", { class: "nav-link" + (active === id ? " on" : ""), href: path }, t(key));

  /**
   * A top-level nav item. The label itself is a link to that section's own page
   * — clicking "Play" goes to /play, it does not just unfurl a menu — and the
   * caret beside it opens the shortcut menu for the pages underneath.
   */
  const dropdown = (key: string, id: string, path: string, entries: MenuEntry[], align: "left" | "right" = "left") => {
    const item = h("div", { class: "nav-item" });
    const label = h("a", { class: "nav-link" + (active === id ? " on" : ""), href: path }, t(key));
    const btn = h("button", {
      class: "caret-btn", "aria-haspopup": "true", "aria-expanded": "false", "aria-label": `${t(key)} ${t("menu")}`,
      onclick: (ev: Event) => {
        ev.stopPropagation();
        ev.preventDefault();
        const wasOpen = item.classList.contains("open");
        closeMenus();
        if (wasOpen) return;
        item.classList.add("open");
        btn.setAttribute("aria-expanded", "true");
        openMenu = item;
      },
    }, caretIcon());
    item.append(label, btn, h("div", { class: `nav-menu ${align}`, role: "menu" }, ...entries.map((e) => menuButton(e, run))));
    return item;
  };

  /**
   * A wide menu for the two sections that have enough behind them to need one.
   *
   * Columns with real headings, and a footer carrying something true rather
   * than filler — the live player counts under Play, your own progress under
   * Learn. A panel that only restates the links above it is just a longer list.
   */
  const megaDropdown = (
    key: string, id: string, path: string,
    columns: { title: string; items: MenuEntry[] }[],
    foot: HTMLElement,
  ) => {
    const item = h("div", { class: "nav-item" });
    const label = h("a", { class: "nav-link" + (active === id ? " on" : ""), href: path }, t(key));
    const btn = h("button", {
      class: "caret-btn", "aria-haspopup": "true", "aria-expanded": "false", "aria-label": `${t(key)} ${t("menu")}`,
      onclick: (ev: Event) => {
        ev.stopPropagation();
        ev.preventDefault();
        const wasOpen = item.classList.contains("open");
        closeMenus();
        if (wasOpen) return;
        item.classList.add("open");
        btn.setAttribute("aria-expanded", "true");
        openMenu = item;
      },
    }, caretIcon());
    item.append(label, btn,
      h("div", { class: "nav-menu mega", role: "menu" },
        h("div", { class: "mega-cols" },
          ...columns.map((c) => h("div", { class: "mega-col" },
            h("div", { class: "mega-col-title" }, c.title),
            ...c.items.map((e) => menuButton(e, run)),
          )),
        ),
        h("div", { class: "mega-foot" }, foot),
      ),
    );
    return item;
  };

  const themeBtn = h("button", {
    class: "icon-btn", title: t("theme"), "aria-label": t("theme"),
    onclick: (e: Event) => {
      const next: Theme = getTheme() === "dark" ? "light" : "dark";
      setTheme(next);
      (e.currentTarget as HTMLElement).replaceChildren(navIcon(next === "dark" ? "moon" : "sun"));
      track("theme_toggle", { theme: next });
    },
  }, navIcon(getTheme() === "dark" ? "moon" : "sun"));

  // ---- account cluster (right) ----
  const prof = account.currentProfile();
  // The bell sits beside the account chip and is only meaningful when signed in.
  const bell = account.isLoggedIn() ? [(() => {
    const item = h("div", { class: "nav-item" });
    const badge = h("span", { class: "bell-badge hide" }, "0");
    const list = h("div", { class: "notif-list" }, ...skeletonRows(3));
    const paintList = () => list.replaceChildren(
      ...(inbox.notifications.length
        ? inbox.notifications.slice(0, 12).map(notificationRow)
        : [h("div", { class: "dim" }, t("noNotifications"))]),
    );
    const btn = h("button", {
      class: "icon-btn bell", title: t("notifications"), "aria-label": t("notifications"), "aria-haspopup": "true",
      onclick: (ev: Event) => {
        ev.stopPropagation();
        const wasOpen = item.classList.contains("open");
        closeMenus();
        if (wasOpen) return;
        item.classList.add("open");
        openMenu = item;
        void loadInbox(true).then(paintList);
      },
    }, navIcon("bell"), badge);
    item.append(btn, h("div", { class: "nav-menu right notif-menu", role: "menu" },
      h("div", { class: "notif-head" },
        h("span", {}, t("notifications")),
        h("button", { class: "link-btn", onclick: (ev: Event) => {
          ev.stopPropagation();
          void account.markNotificationsRead().then((u) => {
            inbox.unread = u;
            for (const n of inbox.notifications) n.read = true;
            paintBellBadges(); paintList();
          });
        } }, t("markAllRead")),
      ),
      list,
      h("a", { class: "notif-foot", href: PATH.messages }, t("messages")),
    ));
    paintList();
    setTimeout(paintBellBadges, 0);
    return item;
  })()] : [];

  const accountCluster: HTMLElement[] = account.isLoggedIn()
    ? [(() => {
        const item = h("div", { class: "nav-item" });
        const initial = (prof?.displayName || nickname || "?").slice(0, 1).toUpperCase();
        const btn = h("button", {
          class: "acct-chip", "aria-haspopup": "true", "aria-expanded": "false",
          onclick: (ev: Event) => {
            ev.stopPropagation();
            const wasOpen = item.classList.contains("open");
            closeMenus();
            if (wasOpen) return;
            item.classList.add("open");
            btn.setAttribute("aria-expanded", "true");
            openMenu = item;
          },
        },
          h("span", { class: "acct-avatar" }, initial),
          h("span", { class: "acct-chip-name" }, prof?.displayName ?? nickname),
          // Always present, even before the profile lands, so the boot fetch and
          // end-of-game sync have something to fill in.
          h("span", { class: "acct-chip-rating" + (prof ? "" : " hide") }, prof ? String(prof.rating) : ""),
          caretIcon(),
        );
        item.append(btn, h("div", { class: "nav-menu right", role: "menu" },
          ...accountEntries().map((e) => menuButton(e, go)),
          h("div", { class: "menu-sep" }),
          h("button", { class: "menu-item danger", onclick: run(async () => {
            await account.logout();
            nickname = localStorage.getItem("xq-name") || "Guest";
            net?.reconnectFresh();
            showHome();
          }) },
            h("span", { class: "menu-glyph" }, "出"),
            h("span", { class: "menu-text" }, h("span", { class: "menu-label" }, t("logOut"))),
          ),
        ));
        return item;
      })()]
    : [
        h("a", { class: "btn ghost small", href: PATH.login }, t("logIn")),
        h("a", { class: "btn primary small", href: PATH.signup }, t("signUp")),
      ];

  // ---- mobile drawer: the same entries, flattened into sections ----
  const drawer = h("div", { class: "nav-drawer" },
    h("div", { class: "menu-head" }, t("navPlay")),
    ...playEntries().map((e) => menuButton(e, run)),
    h("div", { class: "menu-sep" }),
    h("div", { class: "menu-head" }, t("navLearn")),
    ...learnEntries().map((e) => menuButton(e, run)),
    h("div", { class: "menu-sep" }),
    h("div", { class: "menu-head" }, t("account")),
    menuButton({ key: "leaderboard", sub: "leaderboardSub", glyph: "將", path: PATH.leaderboard }, go),
    // Signed out, Settings is the one account entry that still applies.
    ...(account.isLoggedIn() ? accountEntries() : accountEntries().filter((e) => e.key === "settings")).map((e) => menuButton(e, go)),
    h("div", { class: "menu-sep" }),
    /* Always here, whatever the prompt has or has not asked. Somebody who
       declined the prompt has not lost the ability to tell us something; they
       have only asked us to stop interrupting. */
    menuButton({ key: "groupFeedback", sub: "groupFeedbackSub", glyph: "言", run: () => showFeedback("menu") }, run),
  );
  const burger = h("button", {
    class: "icon-btn nav-toggle", "aria-label": t("menu"),
    onclick: (ev: Event) => {
      ev.stopPropagation();
      const wasOpen = drawer.classList.contains("open");
      closeMenus();
      if (wasOpen) return;
      drawer.classList.add("open");
    },
  }, navIcon("menu"));

  const bar = h("header", { class: "site-header" },
    h("a", { class: "brand", href: PATH.home, "aria-label": t("brandName") },
      // The brand logo, from a copy pre-sized for the header (tools/make-logo-nav.py).
      // The 256px master is shrunk to ~40px by the browser, which is what made it
      // soft; 132px for a 42px slot is a 3x reduction on a 3x screen and stays sharp.
      h("img", { class: "logo-mark", "data-logo": "logo-nav", src: logoSrc("logo-nav"), alt: "", width: "42", height: "42" }),
      h("div", { class: "title", html: getLang() === "zh" ? '象棋<span class="seal">对弈</span>' : 'XIANGQI<span class="seal zh-hide">MASTER</span>' }),
    ),
    h("nav", { class: "nav-links" },
      (() => {
        const live = remote?.liveSnapshot();
        const play = playEntries();
        // "Back to your game" belongs at the top of the first column when there
        // is one; the rest splits into starting a game and finding people.
        const resume = play.filter((e) => e.key === "rejoin");
        const start = play.filter((e) => ["quickMatch", "openChallenges", "playBot", "playFriends"].includes(e.key));
        const people = play.filter((e) => e.key === "friends");
        return megaDropdown("navPlay", "play", PATH.play,
          [
            { title: t("megaStart"), items: [...resume, ...start] },
            { title: t("megaPeople"), items: [...people, { key: "leaderboard", sub: "leaderboardSub", glyph: "榜", path: PATH.leaderboard }] },
          ],
          // Real numbers, pushed by the lobby feed — not decoration.
          h("span", { class: "mega-stat" },
            live ? t("gameInProgress") : `${lobbyOnline} ${t("playersOnline")} · ${lobbyPlaying} ${t("gamesInPlay")}`),
        );
      })(),
      (() => {
        const done = doneSet().size, solved = solvedSet().size;
        const l = learnEntries();
        const pick2 = (keys: string[]) => l.filter((e) => keys.includes(e.key));
        return megaDropdown("navLearn", "learn", PATH.learn,
          [
            { title: t("megaTrain"), items: pick2(["lessonsTitle", "puzzles", "analysis"]) },
            { title: t("megaReference"), items: pick2(["guideTitle", "learnTitle"]) },
          ],
          h("span", { class: "mega-stat" },
            `${done}/${LESSONS.length} ${t("lessonsLabel")} · ${solved}/${PUZZLES.length} ${t("puzzlesLabel")}`),
        );
      })(),
      link("navRank", "rank", PATH.leaderboard),
    ),
    h("div", { class: "nav-actions" },
      themeBtn,
      ...bell,
      ...accountCluster,
      burger,
    ),
    drawer,
  );
  return bar;
}

/**
 * Playing requires an account: ratings, game history, friends and abuse control
 * all hang off one. Study (lessons, puzzles, analysis, rules) stays open.
 */
/**
 * How the server refers to us. Signed in that is `acct:<username>`, which is
 * what arrives on a chat line's `from`; the socket's own anonymous id is only
 * used for a guest. Comparing against the wrong one makes your own messages
 * look like someone else's.
 */
function myPlayerId(): string {
  const user = account.currentProfile()?.username;
  return user ? `acct:${user}` : (net?.playerId ?? "");
}

/** A piece's name in the current language, for feedback written about it. */
function pieceName(type: PieceType): string {
  const keys = ["pieceKing", "pieceAdvisor", "pieceElephant", "pieceHorse", "pieceRook", "pieceCannon", "pievePawn"];
  return t(keys[type] ?? "pieceRook");
}

function requireAuth(next: () => void) {
  if (!account.isLoggedIn()) {
    track("auth_gate", {});
    showAuthFor(next);
    return;
  }
  // Signed in, but the address is not confirmed yet. The server would refuse
  // this in a moment anyway; saying so before the round-trip is the difference
  // between an explanation and a failure.
  if (account.needsEmailVerification()) {
    track("verify_gate", {});
    promptVerify();
    return;
  }
  next();
}

/**
 * A one-line summary of a lesson, for the cards that list them.
 *
 * Sentence-splitting alone is not enough: an opening lesson starts "1. 炮二平五 —
 * …", and the first "sentence" would be the move number. So keep taking
 * sentences until there is something worth reading.
 */
function summarise(l: Lesson, max: number): string {
  const parts = pick(l.text).split(/(?<=[。.!?！？])\s*/);
  let out = "";
  for (const p of parts) {
    out = out ? `${out} ${p}` : p;
    if (out.trim().length >= 28) break;
  }
  out = out.trim();
  return out.length > max ? `${out.slice(0, max - 1)}…` : out;
}

/**
 * Where the visitor was headed when the account gate stopped them. Kept as a
 * path rather than a closure so it survives the login round-trip and a cold
 * deep link ("/profile" typed into the address bar) equally.
 */
let gateTarget: string | null = null;

/**
 * Wrap a page that shows somebody's account — rankings, profiles, game history,
 * friends, messages and settings. Signed out the page is not rendered at all:
 * the visitor is sent to the sign-in page, and signing in puts them back on the
 * page they asked for.
 *
 * Not for the boards. Playing the computer needs no account at all, and the
 * lobby is meant to be readable signed out — it carries its own sign-up prompt
 * and gates the two actions (post a seek, accept one) that really do need one.
 */
function gated(render: (p: Record<string, string>) => void): (p: Record<string, string>) => void {
  return (p) => {
    if (account.isLoggedIn()) { render(p); return; }
    gateTarget = currentPath();
    track("auth_gate", { path: gateTarget });
    showAuthFor();
  };
}

/** Send the visitor on after a successful sign-in / reset. */
function afterAuth(next?: () => void) {
  const to = gateTarget;
  gateTarget = null;
  if (to && to !== PATH.login && to !== PATH.signup && to !== PATH.forgot && to !== PATH.verify) { navigate(to, true); return; }
  if (next) next(); else showHome();
}

let netConnected = true;
const connPills: Set<HTMLElement> = new Set();
function paintConnPills() {
  for (const el of connPills) {
    el.textContent = netConnected ? t("connected") : t("reconnecting");
    el.className = "conn-pill " + (netConnected ? "ok" : "bad");
  }
}
function ensureNet(): Net {
  if (!net) {
    net = new Net(nickname);
    net.onMessage((m) => remote?.handle(m));
    net.onMessage((m) => watchView?.handle(m)); // the spectator board, when one is open
    net.onMessage(handleGlobal); // challenges, friend updates — independent of any active game
    net.onStatus((c) => { netConnected = c; paintConnPills(); });
  }
  net.setName(nickname);
  net.connect();
  return net;
}

function soundBtn() {
  const b = h("button", { class: "btn ghost small" }, soundEnabled() ? t("soundOn") : t("soundOff"));
  b.addEventListener("click", () => { const on = toggleSound(); b.textContent = on ? t("soundOn") : t("soundOff"); track("sound_toggle", { enabled: on }); });
  return b;
}

// ---------------------------------------------------------------- board tools
/** Repaints whichever board is on screen, so a toggle takes effect at once. */
let repaintBoard: (() => void) | null = null;

/**
 * Focus mode: the board and nothing else. It hides the site chrome and asks for
 * real fullscreen, and Escape (or leaving fullscreen) puts everything back.
 */
let focusMode = false;
function setFocus(on: boolean) {
  focusMode = on;
  document.documentElement.dataset.focus = on ? "on" : "off";
  try {
    if (on && !document.fullscreenElement) void document.documentElement.requestFullscreen?.().catch(() => { /* denied */ });
    if (!on && document.fullscreenElement) void document.exitFullscreen?.().catch(() => { /* ignore */ });
  } catch { /* fullscreen unsupported */ }
  // The board sizes itself off the viewport, so it has to be redrawn.
  setTimeout(() => repaintBoard?.(), 60);
}
document.addEventListener("fullscreenchange", () => {
  // Leaving fullscreen by any other route (Esc, F11, the browser UI) must not
  // leave the page stuck in a chrome-less state.
  if (!document.fullscreenElement && focusMode) { focusMode = false; document.documentElement.dataset.focus = "off"; document.querySelectorAll(".tool-btn.focus").forEach((b) => b.classList.remove("on")); repaintBoard?.(); }
});
document.addEventListener("keydown", (e) => {
  if ((e as KeyboardEvent).key === "Escape" && focusMode) setFocus(false);
});

/**
 * The switches that belong to the board itself: sound, spoken moves, piece
 * hints, focus mode and the board's look. They live on every board screen
 * because the moment you want one of them is mid-game, not three pages away in
 * Settings.
 */
/**
 * Line icons for the board screens' side panel, one family drawn on a 24-unit
 * grid with the same stroke. The panel used emoji (🔊 🗣 💡 ⛶ 🎨 ⧉ 👁), which
 * every platform draws in its own colours and sizes — some as grey smudges —
 * so no two controls looked like they belonged together.
 */
const GP_ICONS = {
  sound: `<path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4z"/><path d="M15.5 9a4 4 0 0 1 0 6"/><path d="M18 6.5a7.5 7.5 0 0 1 0 11"/>`,
  voice: `<rect x="9" y="3.5" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0"/><path d="M12 17v3.5"/>`,
  hints: `<path d="M9.4 17h5.2"/><path d="M10.2 20h3.6"/><path d="M12 3.4a5.6 5.6 0 0 0-3.3 10.1c.5.4.8 1 .8 1.6h5c0-.6.3-1.2.8-1.6A5.6 5.6 0 0 0 12 3.4Z"/>`,
  focus: `<path d="M4 9V4h5"/><path d="M20 9V4h-5"/><path d="M4 15v5h5"/><path d="M20 15v5h-5"/>`,
  palette: `<path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.2 0 1.8-.8 1.8-1.7 0-1.2-1-1.6-1-2.6 0-.9.7-1.6 1.6-1.6h2.1a4 4 0 0 0 4-4C20.5 6.7 16.7 3.5 12 3.5Z"/><circle cx="7.6" cy="11" r="1.1" fill="currentColor" stroke="none"/><circle cx="10" cy="7.3" r="1.1" fill="currentColor" stroke="none"/><circle cx="14.5" cy="7.3" r="1.1" fill="currentColor" stroke="none"/>`,
  flag: `<path d="M5.5 21V4"/><path d="M5.5 4.5h12l-2.2 4 2.2 4h-12"/>`,
  draw: `<path d="M6 9.5h12"/><path d="M6 14.5h12"/>`,
  rematch: `<path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3"/><path d="M19.5 4.5v4.5H15"/>`,
  home: `<path d="M4 11.5 12 5l8 6.5"/><path d="M6.5 10v9.5h11V10"/>`,
  copy: `<rect x="8.5" y="8.5" width="11" height="11" rx="2.2"/><path d="M15.5 8.5V6.2c0-.9-.8-1.7-1.7-1.7H6.2c-.9 0-1.7.8-1.7 1.7v7.6c0 .9.8 1.7 1.7 1.7h2.3"/>`,
  check: `<path d="m5 12.5 4.5 4.5L19 7.5"/>`,
  eye: `<path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"/><circle cx="12" cy="12" r="2.8"/>`,
  userPlus: `<circle cx="9.5" cy="8" r="3.5"/><path d="M3 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5"/><path d="M19 8v6"/><path d="M16 11h6"/>`,
  send: `<path d="M4.5 11.5 20 4.5l-6.5 15.5-2.7-6.3-6.3-2.2Z"/><path d="m10.8 13.7 4.6-4.6"/>`,
  first: `<path d="M6.5 5.5v13"/><path d="m17.5 6-6.5 6 6.5 6"/>`,
  prev: `<path d="m14.5 6-6 6 6 6"/>`,
  next: `<path d="m9.5 6 6 6-6 6"/>`,
  last: `<path d="M17.5 5.5v13"/><path d="m6.5 6 6.5 6-6.5 6"/>`,
  undo: `<path d="M9 14 4.5 9.5 9 5"/><path d="M4.5 9.5h10a5 5 0 0 1 0 10H11"/>`,
  plus: `<path d="M12 5v14"/><path d="M5 12h14"/>`,
} as const;

function ico(name: keyof typeof GP_ICONS): HTMLElement {
  return h("span", {
    class: "ico", "aria-hidden": "true",
    html: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${GP_ICONS[name]}</svg>`,
  });
}

/** A copy-to-clipboard button that answers with a tick, then goes back. */
function copyBtn(label: string, text: () => string, onCopied?: () => void): HTMLButtonElement {
  const b = h("button", { class: "gp-copy", type: "button", title: label, "aria-label": label }, ico("copy"), h("span", {}, t("copy"))) as HTMLButtonElement;
  b.addEventListener("click", async () => {
    try {
      await navigator.clipboard?.writeText(text());
      b.replaceChildren(ico("check"), h("span", {}, t("copiedShort")));
      b.classList.add("done");
      onCopied?.();
    } catch { /* clipboard refused: leave the button as it was */ }
    setTimeout(() => { b.replaceChildren(ico("copy"), h("span", {}, t("copy"))); b.classList.remove("done"); }, 1500);
  });
  return b;
}

function boardTools(): HTMLElement {
  const tog = (on: boolean, glyph: keyof typeof GP_ICONS, label: string, cls: string, fn: () => boolean) => {
    const b = h("button", { class: `tool-btn ${cls}` + (on ? " on" : ""), title: label, "aria-label": label, "aria-pressed": String(on) }, ico(glyph));
    b.addEventListener("click", () => {
      const now = fn();
      b.classList.toggle("on", now);
      b.setAttribute("aria-pressed", String(now));
    });
    return b;
  };
  return h("div", { class: "board-tools" },
    tog(soundEnabled(), "sound", t("sound"), "sound", () => { const v = toggleSound(); track("sound_toggle", { enabled: v }); return v; }),
    tog(voiceOn(), "voice", t("voiceMoves"), "voice", () => {
      const v = toggleVoice();
      if (!v) stopSpeaking(); else speak(t("voiceMoves"), getLang());
      track("voice_toggle", { enabled: v });
      return v;
    }),
    tog(hintsOn(), "hints", t("pieceHints"), "hints", () => { const v = toggleHints(); repaintBoard?.(); track("hints_toggle", { enabled: v }); return v; }),
    tog(focusMode, "focus", t("focusMode"), "focus", () => { setFocus(!focusMode); track("focus_toggle", { enabled: focusMode }); return focusMode; }),
    // Not a toggle: it opens the style picker over the board.
    h("button", { class: "tool-btn style", type: "button", title: t("boardTheme"), "aria-label": t("boardTheme"),
      onclick: () => { if (focusMode) setFocus(false); showBoardStyle(); } }, ico("palette")),
  );
}

// ------------------------------------------------- one player, at the board
/**
 * A player's row beside the board: who they are, what they have taken off, and
 * their clock — on ONE line.
 *
 * This replaces the old pair of rows (a name-and-clock bar, then a separate
 * capture tray underneath). Two rows said no more than one and cost about
 * sixty pixels of board on a phone, where the board is the whole point.
 */
function playerStrip(o: { at: "top" | "bottom"; name: Node | string; badge?: string; tray: HTMLElement; clock?: HTMLElement }): HTMLElement {
  const text = typeof o.name === "string" ? o.name : (o.name.textContent ?? "");
  return h("div", { class: `pstrip pstrip-${o.at}` },
    h("div", { class: "pavatar", "aria-hidden": "true" }, [...text.trim()][0]?.toUpperCase() ?? "?"),
    h("div", { class: "pmeta" },
      h("div", { class: "pwho" },
        typeof o.name === "string" ? h("b", {}, o.name) : o.name,
        ...(o.badge ? [h("span", { class: "prating" }, o.badge)] : []),
      ),
      o.tray,
    ),
    ...(o.clock ? [o.clock] : []),
  );
}

// ------------------------------------------------------------- phone chrome
/**
 * Below 900px the side panel cannot sit beside the board, and stacking it
 * under the board is what made the phone layout bad: the move list, the
 * history buttons, resign, offer-draw and the whole chat lived under the fold,
 * reachable only by scrolling the board off the screen with a clock running.
 *
 * So on a phone the panel becomes a sheet and this bar is what opens it. Five
 * fixed targets under the board — step back, step forward, moves, a screen's
 * own fourth button (chat, or a hint against the bot), and everything else —
 * and the page itself never scrolls.
 */
type SheetTab = "moves" | "chat" | "more";

/** The sheet on screen right now, so a repaint can leave it where it was. */
let sheetTab: SheetTab | null = null;

function dockIcon(paths: string): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "22");
  svg.setAttribute("height", "22");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.7");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  svg.innerHTML = paths;
  return svg;
}

const DOCK_ICONS = {
  back: `<path d="M15 5 8 12l7 7"/>`,
  fwd: `<path d="m9 5 7 7-7 7"/>`,
  moves: `<path d="M8 6h12"/><path d="M8 12h12"/><path d="M8 18h12"/><circle cx="4.2" cy="6" r=".9" fill="currentColor" stroke="none"/><circle cx="4.2" cy="12" r=".9" fill="currentColor" stroke="none"/><circle cx="4.2" cy="18" r=".9" fill="currentColor" stroke="none"/>`,
  chat: `<path d="M20 12.5a7.5 7.5 0 0 1-11 6.6L4.5 20.5l1.4-4.3A7.5 7.5 0 1 1 20 12.5Z"/>`,
  hint: `<path d="M9.4 17h5.2"/><path d="M10.2 20h3.6"/><path d="M12 3.4a5.6 5.6 0 0 0-3.3 10.1c.5.4.8 1 .8 1.6h5c0-.6.3-1.2.8-1.6A5.6 5.6 0 0 0 12 3.4Z"/>`,
  more: `<circle cx="5.5" cy="12" r="1.3" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none"/><circle cx="18.5" cy="12" r="1.3" fill="currentColor" stroke="none"/>`,
} as const;

/** One target in the dock. Labels are short because they sit under the icon. */
function dockBtn(icon: keyof typeof DOCK_ICONS, label: string, onclick: () => void): HTMLButtonElement {
  return h("button", { class: "dock-btn", "aria-label": label, onclick },
    dockIcon(DOCK_ICONS[icon]),
    h("span", {}, label),
  );
}

interface DockParts {
  /** The bar itself. */
  bar: HTMLElement;
  /** The dimmed backdrop behind an open sheet. */
  scrim: HTMLElement;
  /** The head that names the open sheet and closes it. */
  head: HTMLElement;
  /** Open a section of the panel, or close the sheet with null. */
  show: (tab: SheetTab | null) => void;
}

/**
 * Builds the dock, the sheet's head and the backdrop for one board screen. The
 * panel is the SAME element the desktop shows beside the board — a phone only
 * changes where it sits and which of its sections is on show at a time.
 */
function boardDock(
  host: HistoryHost,
  panel: HTMLElement,
  slot4: HTMLButtonElement,
  slot4Tab: SheetTab | null,
  onShow?: (tab: SheetTab | null) => void,
): DockParts {
  const scrim = h("div", { class: "sheet-scrim" });
  const title = h("span", { class: "sheet-title" });
  const closeBtn = h("button", { class: "sheet-close", "aria-label": t("sheetClose") }, "✕");
  const head = h("div", { class: "sheet-head" }, h("div", { class: "sheet-grab" }), title, closeBtn);

  const step = (d: -1 | 1) => {
    const cur = host.viewPly ?? host.moves.length;
    host.setView(d === -1 ? Math.max(0, cur - 1) : Math.min(host.moves.length, cur + 1));
  };
  const back = dockBtn("back", t("dockBack"), () => step(-1));
  const fwd = dockBtn("fwd", t("dockFwd"), () => step(1));
  const moves = dockBtn("moves", t("movesTitle"), () => show(sheetTab === "moves" ? null : "moves"));
  const more = dockBtn("more", t("dockMore"), () => show(sheetTab === "more" ? null : "more"));
  const tabbed: [HTMLButtonElement, SheetTab][] = [[moves, "moves"], [more, "more"]];
  if (slot4Tab) tabbed.push([slot4, slot4Tab]);

  const TITLES: Record<SheetTab, () => string> = {
    moves: () => t("movesTitle"),
    chat: () => t("chatTitle"),
    more: () => t("sheetMore"),
  };

  const show = (tab: SheetTab | null) => {
    sheetTab = tab;
    panel.dataset.tab = tab ?? "";
    panel.classList.toggle("open", tab !== null);
    scrim.classList.toggle("on", tab !== null);
    title.textContent = tab ? TITLES[tab]() : "";
    for (const [b, id] of tabbed) b.classList.toggle("on", id === tab);
    onShow?.(tab);
  };
  show(null);

  closeBtn.addEventListener("click", () => show(null));
  scrim.addEventListener("click", () => show(null));

  return { bar: h("nav", { class: "board-dock" }, back, fwd, moves, slot4, more), scrim, head, show };
}

/**
 * The always-visible line under the board on a phone: whose move it is, what
 * needs saying about it, and as many of the latest moves as the screen has
 * room for. Tapping it opens the full move list.
 *
 * On a tall phone this fills what would otherwise be dead space between the
 * board and the dock; on a short one it collapses to the status line alone.
 */
function liveStrip(status: HTMLElement, sub: HTMLElement, moves: HTMLElement, open: () => void): HTMLElement {
  return h("div", { class: "live-strip" },
    h("button", { class: "live-head", onclick: open },
      status, sub, h("span", { class: "live-caret", "aria-hidden": "true" }, "⌃"),
    ),
    moves,
  );
}

/**
 * The invite code of a private game, with one click to copy the join link —
 * the same link the waiting screen offered, still to hand once play has begun.
 */
function roomCodeChip(code: string): HTMLElement {
  const link = `${location.origin}${PATH.join(code)}`;
  return h("div", { class: "code-chip" },
    h("span", { class: "code-chip-label" }, t("roomLabel")),
    h("span", { class: "code-chip-value" }, code),
    copyBtn(t("copy"), () => link),
  );
}

/** "Add <opponent> as a friend" — one button, and it reports what happened. */
function addFriendRow(username: string, displayName: string): HTMLElement {
  const msg = h("span", { class: "friend-msg" });
  const btn = h("button", { class: "btn small ghost" }, ico("userPlus"), h("span", {}, t("addFriend")));
  const row = h("div", { class: "opp-row" },
    h("span", { class: "opp-avatar", "aria-hidden": "true" }, [...displayName.trim()][0]?.toUpperCase() ?? "?"),
    h("a", { class: "opp-link", href: PATH.player(username) }, displayName),
    btn, msg,
  );
  btn.addEventListener("click", async () => {
    if (!account.isLoggedIn()) { showAuthFor(() => showHome()); return; }
    btn.setAttribute("disabled", "true");
    const r = await account.requestFriend(username);
    msg.textContent = r.ok ? t("requestSent") : (r.error ?? "");
    msg.className = "friend-msg" + (r.ok ? " ok" : " bad");
    if (r.ok) { btn.remove(); track("friend_request_in_game", {}); } else btn.removeAttribute("disabled");
  });
  return row;
}

/** Reads a move out loud, in the language the interface is in. */
function announceMove(before: Position, m: Move) {
  if (!voiceOn()) return;
  const lang = getLang();
  // Moves are read in Chinese or English; a Vietnamese reader hears the English reading.
  speak(lang === "zh" ? moveToChinese(before, m) : moveToSpokenEn(before, m), lang === "zh" ? "zh" : "en");
}

/** Small segmented control. */
function seg(opts: [string, string][], get: () => string, set: (v: string) => void) {
  const wrap = h("div", { class: "seg" });
  const paint = () => wrap.querySelectorAll("button").forEach((b) => b.classList.toggle("on", (b as HTMLElement).dataset.v === get()));
  for (const [v, label] of opts) wrap.append(h("button", { class: "seg-btn", "data-v": v, onclick: () => { set(v); paint(); } }, label));
  paint();
  return wrap;
}

// ---------------------------------------------------------------- Home
/** The bot game on screen, and whether it is still being played. */
let localGame: LocalGame | null = null;
// Not tied to the address bar: on the back button the URL has already changed
// by the time we are asked, and the board is still the screen on display.
const botLive = () => !!localGame?.isLive();
/** Any game the player is inside right now, online or against the engine. */
const gameLive = () => !!remote?.liveSnapshot() || botLive();

/** While a game is live, keep the player on their board; exit only via resign. */
function guardLeaveGame(next?: () => void) {
  const s = remote?.liveSnapshot();
  if (!s) {
    // A game against the computer is held to the same rule: the board stays
    // until the player explicitly exits it. `next` is where they were headed.
    if (!botLive()) return;
    showModal(t("leaveGameTitle"), t("leaveBotBody"), [
      [t("keepPlaying"), () => {}, true],
      [t("exitGame"), () => { localGame?.abandon(); (next ?? showHome)(); }],
    ], "info");
    return;
  }
  remote?.reopen(); // ensure the board is showing behind the prompt
  showModal(t("leaveGameTitle"), t("leaveGameBody"), [
    [t("keepPlaying"), () => remote?.reopen(), true],
    [t("resign"), () => { net?.send({ t: "resign", gameId: s.gameId }); }],
  ], "info");
}

function showHome() {
  // A game in progress is a private, sticky room: you stay on your board until
  // you explicitly exit. Any attempt to wander home asks first.
  if (gameLive()) { guardLeaveGame(showHome); return; }
  closeModal();
  navHandler = null;
  enter("home", PATH.home, t("pageHome"));
  // The lobby feed powers the live "N online · M in play" counter in the hero.
  ensureNet();
  net!.send({ t: "lobby", on: true });

  const done = doneSet().size;
  const solved = solvedSet().size;
  const signedIn = account.isLoggedIn();

  /** A destination card: glyph, title, one line, and an optional progress meter. */
  // The meta pill lives inside the text column: kept alongside it, a long
  // description gets squeezed into a two-character-wide ribbon.
  // `target` is either the page the tile opens (a real link) or an action.
  //
  // `variant` is what stops eleven destinations from reading as one undammed
  // list: "lead" is the primary action of its section and is built to look it,
  // "slim" is a secondary destination that should not shout. `bar` turns a
  // count into an actual meter — "0/20" as a number says far less than a bar
  // that is visibly near-empty.
  const tile = (
    title: string, sub: string, glyph: string, target: string | (() => void),
    meta?: string, locked = false, onOpen?: () => void,
    variant: "" | "lead" | "wide" | "slim" = "", bar?: [number, number],
  ) =>
    h(typeof target === "string" ? "a" : "button", {
      class: "tile" + (locked ? " locked" : "") + (variant ? ` tile-${variant}` : ""),
      ...(typeof target === "string" ? { href: target } : {}),
      onclick: () => { unlockAudio(); onOpen?.(); if (typeof target !== "string") target(); },
    },
      h("span", { class: "tile-glyph" }, glyph),
      h("span", { class: "tile-text" },
        h("span", { class: "tile-title" },
          title,
          ...(locked ? [h("span", { class: "tile-lock", title: t("signInToPlay") }, t("signInShort"))] : []),
        ),
        h("span", { class: "tile-sub" }, sub),
        ...(bar
          ? [h("span", { class: "tile-meter", role: "img", "aria-label": `${bar[0]} / ${bar[1]}` },
              h("span", { class: "tile-bar" },
                h("span", { class: "tile-bar-fill", style: `width:${bar[1] ? Math.round((bar[0] / bar[1]) * 100) : 0}%` })),
              h("span", { class: "tile-bar-num" }, `${bar[0]}/${bar[1]}`))]
          : meta ? [h("span", { class: "tile-meta" }, meta)] : []),
      ),
      h("span", { class: "tile-arrow" }, "→"),
    );

  const section = (label: string, note: string, ...kids: Node[]) =>
    h("section", { class: "home-section" },
      h("div", { class: "section-head" },
        h("h2", { class: "section-title" }, label),
        h("span", { class: "section-note" }, note),
      ),
      ...kids,
    );

  const heroStats = h("div", { class: "hero-stats" }, h("span", { class: "pulse" }), h("span", { class: "hs-text" }, t("liveNow")));
  lobbyTarget = { stats: heroStats.querySelector(".hs-text") as HTMLElement };

  app.replaceChildren(
    header(),
    h("div", { class: "home" },
      // The board itself is the most persuasive thing on the page, so it sits
      // beside the pitch rather than being hidden behind a click.
      h("section", { class: "hero" },
        h("div", { class: "hero-copy" },
          // The wordmark is already in the header, three centimetres up. A
          // second copy of it here was the first thing on the page on a phone,
          // pushing the sentence that says what this *is* below the fold.
          h("span", { class: "hero-eyebrow" }, t("heroEyebrow")),
          h("h1", { class: "hero-head", html: t("heroHead") }),
          h("p", { class: "hero-sub" }, t("heroSub")),
          h("div", { class: "hero-cta" },
            h("a", { class: "btn primary lg", href: PATH.play, onclick: () => { unlockAudio(); track("select_mode", { mode: "play_hub" }); } },
              signedIn ? t("playNow") : t("playNowFree")),
            h("a", { class: "btn ghost lg", href: PATH.learn, onclick: () => unlockAudio() }, t("learnXiangqi")),
          ),
          heroStats,
        ),
        h("div", { class: "hero-art" }, heroBoardImage()),
      ),

      liveGameBanner(),
      verifyBanner(),

      // Bento, not a uniform grid: the one thing most visitors came to do —
      // get into a game against a person — is a tall card holding its own
      // column, and the three alternatives share the other two. Ranked by what
      // people actually want, the page can be read at a glance instead of
      // scanned rectangle by rectangle.
      section(t("sectionPlay"), signedIn ? t("sectionPlaySubIn") : t("sectionPlaySub"),
        h("div", { class: "tile-grid bento" },
          tile(t("quickMatch"), t("quickMatchSub"), "快", () => { track("select_mode", { mode: "quick" }); requireAuth(() => startOnline()); }, tcLabel(getTc()), !signedIn, undefined, "lead"),
          // Neither of these is locked any more: the lobby reads signed out,
          // and the computer needs no account. Quick match and the private
          // room below still do, and still say so.
          tile(t("openChallenges"), t("lobbySub"), "榜", PATH.play, undefined, false, () => track("select_mode", { mode: "play_hub" }), "wide"),
          tile(t("playBot"), t("playBotSub"), "帥", PATH.bot, undefined, false, () => track("select_mode", { mode: "bot" })),
          tile(t("playFriends"), t("playFriendsSub"), "炮", PATH.private, undefined, !signedIn, () => track("select_mode", { mode: "friends" })),
        ),
      ),

      section(t("sectionLearn"), t("sectionLearnSub"),
        h("div", { class: "tile-grid cols-4" },
          // The daily leads, and says at a glance whether it is still owed --
          // a reason to come back is worth nothing if it is three clicks down.
          tile(t("dailyTitle"), t("dailySub"), "日", PATH.daily,
            solvedToday() ? `${t("dailyDoneShort")} · ${t("dailyStreak")} ${liveStreak()}` : t("dailyGo"),
            false, () => track("select_mode", { mode: "daily" })),
          tile(t("lessonsTitle"), t("lessonsSub"), "課", PATH.lessons, undefined, false, undefined, "", [done, LESSONS.length]),
          tile(t("puzzles"), t("puzzlesSub"), "題", PATH.puzzles, undefined, false, undefined, "", [solved, PUZZLES.length]),
          tile(t("analysis"), t("analysisSub"), "研", PATH.analysis),
          tile(t("guideTitle"), t("guideSub"), "書", PATH.rules),
        ),
      ),

      // Rankings, profiles and friends are all somebody's account, so signed
      // out they carry the same SIGN IN chip as the play tiles rather than
      // walking the visitor into a wall. They are also nobody's reason for
      // visiting, so they read as a quiet strip rather than as three more
      // full-height cards competing with the board.
      section(t("sectionCommunity"), signedIn ? t("sectionCommunitySub") : t("gateBody"),
        h("div", { class: "tile-grid cols-3 strip" },
          tile(t("leaderboard"), t("leaderboardSub"), "將", PATH.leaderboard, undefined, !signedIn, undefined, "slim"),
          tile(t("profile"), t("profileSub"), "士", PATH.profile, undefined, !signedIn, undefined, "slim"),
          tile(t("friends"), t("friendsSub"), "馬", PATH.friends, undefined, !signedIn, undefined, "slim"),
        ),
      ),

      h("footer", { class: "site-footer" },
        h("div", { class: "foot-links" },
          h("a", { class: "foot-link", href: PATH.rules }, t("guideTitle")),
          h("a", { class: "foot-link", href: PATH.lessons }, t("lessonsTitle")),
          h("a", { class: "foot-link", href: PATH.puzzles }, t("puzzles")),
          h("a", { class: "foot-link", href: PATH.analysis }, t("analysis")),
          h("a", { class: "foot-link", href: PATH.settings }, `⚙ ${t("settings")}`),
        ),
        h("span", { class: "build-stamp", html: `build <b>${__BUILD__}</b>` }),
      ),
    ),
  );
  renderLobby(null);

  const join = new URLSearchParams(location.search).get("join");
  if (join) {
    history.replaceState(null, "", location.pathname);
    joinRoom(join.toUpperCase());
  }
}

/**
 * If a server game is still running, surface it everywhere: a banner on the
 * home screen and a popup when the app boots, so a stray Back never loses it.
 */
function liveGameBanner(): HTMLElement {
  const wrap = h("div", { class: "live-banner hide" });
  const paint = () => {
    const s = remote?.liveSnapshot();
    if (!s) { wrap.className = "live-banner hide"; return; }
    wrap.className = "live-banner";
    wrap.replaceChildren(
      h("span", { class: "pulse" }),
      h("span", { class: "lb-text" }, `${t("gameInProgress")} · ${s.opponentName}`),
      h("a", { class: "btn small primary", href: PATH.game }, t("rejoin")),
      h("button", { class: "btn small ghost", onclick: () => confirmResign(() => net?.send({ t: "resign", gameId: s.gameId })) }, t("resign")),
    );
  };
  paint();
  setTimeout(paint, 400); // the socket may still be connecting on first paint
  liveBannerRepaint = paint;
  return wrap;
}
let liveBannerRepaint: (() => void) | null = null;

/**
 * A standing reminder for an account that has not confirmed its address.
 *
 * Deliberately a banner and not a wall: everything on these pages except a game
 * against a person still works, so they render as normal and this sits above
 * them. It reuses the live-game banner's styling -- same shape, same slot in the
 * layout -- and renders as nothing once the address is confirmed.
 */
function verifyBanner(): HTMLElement {
  if (!account.needsEmailVerification()) return h("div", { class: "live-banner hide" });
  return h("div", { class: "live-banner" },
    h("span", { class: "lb-text" }, t("verifyBannerText")),
    h("a", { class: "btn small primary", href: PATH.verify }, t("verifyBannerCta")),
  );
}

// ---------------------------------------------------------------- Leaderboard
function showLeaderboard() {
  closeModal();
  enter("leaderboard", PATH.leaderboard, t("leaderboard"));
  navHandler = null;
  ensureNet();
  // The table is a websocket round trip away. A single "…" made a fast
  // connection look broken and a slow one look empty; six shimmering rows are
  // the right shape and say the list is on its way.
  const body = h("div", { class: "lb-table" },
    ...Array.from({ length: 6 }, () => h("div", { class: "skeleton skeleton-row" })));
  app.replaceChildren(
    header("rank"),
    h("div", { class: "lessons lb-page" },
      h("header", { class: "set-head" },
        h("div", { class: "set-head-text" },
          h("h1", { class: "set-head-title" }, t("leaderboard")),
          h("p", { class: "set-head-sub" }, t("leaderboardIntro")),
        ),
      ),
      body,
      h("div", { class: "lb-note" }, t("leaderboardNote")),
      h("div", { class: "setup-actions" }, h("a", { class: "btn ghost", href: PATH.home }, t("home"))),
    ),
  );
  leaderboardTarget = body;
  net!.send({ t: "leaderboard" });
}
let leaderboardTarget: HTMLElement | null = null;
/**
 * The last rows the server sent.
 *
 * The table marks your own row, which needs the profile -- and the rows arrive
 * over the websocket while the profile arrives over HTTP, so either can win.
 * Keeping them means the table can simply be drawn again once we know who we
 * are, instead of the highlight being a race nobody notices losing.
 */
let lastLeaderRows: Parameters<typeof renderLeaderboard>[0] | null = null;

function renderLeaderboard(rows: { rank: number; name: string; user?: string; rating: number; games: number; wins: number; losses: number; draws: number }[]) {
  lastLeaderRows = rows;
  if (!leaderboardTarget) return;
  const note = leaderboardTarget.parentElement?.querySelector(".lb-note");
  note?.classList.toggle("hide", rows.length === 0);
  if (!rows.length) {
    leaderboardTarget.replaceChildren(h("div", { class: "empty-state" },
      h("div", { class: "empty-glyph" }, "將"),
      h("div", { class: "empty-title" }, t("noRatedGames")),
      h("div", { class: "empty-sub" }, t("noRatedGamesSub")),
      h("a", { class: "btn primary", href: PATH.play }, t("playNow")),
    ));
    return;
  }
  const me = account.currentProfile()?.username ?? null;
  const mine = me ? rows.find((r) => r.user === me) ?? null : null;

  /** One player as a row. A row with an account behind it opens their profile. */
  const row = (r: typeof rows[number]) => {
    const isMe = !!me && r.user === me;
    return h(r.user ? "a" : "div", {
      class: "lb-row" + (r.user ? " link" : "") + (isMe ? " me" : ""),
      ...(r.user ? { href: PATH.player(r.user) } : {}),
    },
      h("span", { class: "lb-rank" }, String(r.rank)),
      h("span", { class: "lb-who" },
        h("span", { class: "lb-name" }, r.name, ...(isMe ? [h("i", { class: "lb-you" }, t("youTag"))] : [])),
        // A bar says "wins most of them" faster than three numbers do, and the
        // numbers are still there underneath for anyone who wants them.
        h("span", { class: "lb-record" },
          h("span", { class: "lb-bar", title: `${r.wins}W / ${r.losses}L / ${r.draws}D` },
            h("i", { class: "lb-w", style: `width:${r.games ? (r.wins / r.games) * 100 : 0}%` }),
            h("i", { class: "lb-d", style: `width:${r.games ? (r.draws / r.games) * 100 : 0}%` }),
          ),
          h("span", { class: "lb-record-n" }, `${r.wins}–${r.losses}–${r.draws}`),
        ),
      ),
      h("span", { class: "lb-num" }, String(r.games)),
      h("span", { class: "lb-num strong" }, String(r.rating)),
    );
  };

  const head = h("div", { class: "lb-row lb-head" },
    h("span", { class: "lb-rank" }, "#"),
    h("span", { class: "lb-who" }, t("player")),
    h("span", { class: "lb-num" }, t("games")),
    h("span", { class: "lb-num" }, t("rating")));

  // The top three get a podium. It is the part of a leaderboard people come to
  // look at, and three rows of a table is not a result, it is a list.
  const top = rows.slice(0, 3);
  const podium = top.length === 3
    ? h("div", { class: "lb-podium" },
        // Second, first, third — the shape a podium actually is.
        ...[1, 0, 2].map((i) => {
          const r = top[i]!;
          return h(r.user ? "a" : "div", {
            class: `lb-step lb-step-${r.rank}` + (r.user ? " link" : "") + (me && r.user === me ? " me" : ""),
            ...(r.user ? { href: PATH.player(r.user) } : {}),
          },
            h("span", { class: "lb-medal" }, ["金", "銀", "銅"][r.rank - 1]!),
            h("span", { class: "lb-step-name" }, r.name),
            h("span", { class: "lb-step-rating" }, String(r.rating)),
            h("span", { class: "lb-step-games" }, `${r.games} ${t("gamesLower")}`),
          );
        }))
    : null;

  leaderboardTarget.replaceChildren(
    ...(podium ? [podium] : []),
    head,
    ...rows.slice(podium ? 3 : 0).map(row),
    // The server sends fifty. Being signed in and not among them is worth
    // saying, because the alternative is a page that silently omits you.
    ...(me && !mine ? [h("div", { class: "lb-absent" }, t("notRankedYet"))] : []),
  );
}

/**
 * Sign in / create an account. `next` is where the visitor was headed when the
 * gate stopped them, so a blocked "Play" lands on the board they asked for.
 */
/**
 * Google's "G", inline. Their branding terms ask for this mark on the button,
 * and it is the one asset here that must not be redrawn in the site's palette —
 * so it keeps its own four colours in both themes.
 */
const GOOGLE_G_SVG = `<svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false">
<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
</svg>`;

/**
 * Open whichever of the two forms this visitor most likely needs: "Log in" for
 * someone who has signed in on this device before, "Sign up" for everyone else.
 * Every gate goes through here.
 */
function showAuthFor(next?: () => void) {
  showAuth(account.knownOnThisDevice() ? "login" : "signup", next);
}

/** The server's username rule, mirrored so the form can say so before sending. */
const USERNAME_CHARS = /^[a-z0-9_\p{Unified_Ideograph}]+$/iu;
function usernameProblem(raw: string): boolean {
  const name = raw.normalize("NFKC").trim();
  const n = [...name].length;
  return !USERNAME_CHARS.test(name) || n > 16 || n < (/\p{Unified_Ideograph}/u.test(name) ? 2 : 3);
}

/** A refusal from the account API, in the reader's language when we know it. */
function authErrorText(e: unknown): string {
  const code = e instanceof account.ApiError ? e.code : "";
  const key = `authErr_${code}`;
  return code && t(key) !== key ? t(key) : (e as Error).message;
}

function showAuth(mode: "login" | "signup", next?: () => void, prefill = "") {
  closeModal();
  enter(mode === "signup" ? "signup" : "login", mode === "signup" ? PATH.signup : PATH.login,
    mode === "signup" ? t("signupTitle") : t("loginTitle"));
  navHandler = null;
  // Signing in also takes the email address, which is longer than any username.
  const uname = h("input", {
    class: "auth-input", type: "text", maxlength: mode === "signup" ? "16" : "120",
    placeholder: mode === "signup" ? t("username") : t("usernameOrEmail"), autocomplete: "username",
  }) as HTMLInputElement;
  uname.value = prefill;
  const email = h("input", { class: "auth-input", type: "email", maxlength: "120", placeholder: t("email"), autocomplete: "email" }) as HTMLInputElement;
  const pass = h("input", { class: "auth-input", type: "password", placeholder: t("password"), autocomplete: mode === "signup" ? "new-password" : "current-password" }) as HTMLInputElement;
  const disp = h("input", { class: "auth-input", type: "text", maxlength: "16", placeholder: t("displayName") }) as HTMLInputElement;
  const msg = h("div", { class: "auth-msg", "aria-live": "polite" });
  // The rule, said up front: the server used to be the first to mention it, in
  // English, after the whole form had been filled in.
  const rule = h("div", { class: "auth-hint" }, t("usernameRule"));
  if (mode === "signup") {
    uname.addEventListener("input", () => {
      const bad = uname.value.trim() !== "" && usernameProblem(uname.value) && [...uname.value.trim()].length >= 2;
      rule.classList.toggle("bad", bad);
    });
  }
  // A Google round-trip that came back with something to say (an error, or a
  // cancelled consent screen) reports it here. Read once, so it does not
  // reappear when the visitor flips between Log in and Sign up.
  if (oauthMessage) {
    msg.className = "auth-msg bad";
    msg.textContent = oauthMessage;
    oauthMessage = "";
  }
  let busy = false;
  const submit = async () => {
    if (busy) return;
    const name = uname.value.trim();
    if (!name || !pass.value) {
      msg.className = "auth-msg bad"; msg.textContent = t("authFillBoth");
      (name ? pass : uname).focus();
      return;
    }
    if (mode === "signup" && usernameProblem(name)) {
      msg.className = "auth-msg bad"; msg.textContent = t("authErr_username_invalid");
      rule.classList.add("bad");
      uname.focus();
      return;
    }
    busy = true;
    submitBtn.toggleAttribute("disabled", true);
    msg.className = "auth-msg"; msg.textContent = "…";
    try {
      const local = [...doneSet()];
      const prof = mode === "signup"
        ? await account.signup(name, pass.value, disp.value.trim() || name, email.value.trim(), local)
        : await account.login(name, pass.value, local);
      // Pull the merged lesson set back into local storage.
      for (const id of prof.lessonsDone) markDoneLocal(id);
      nickname = prof.displayName;
      net?.reconnectFresh(); // re-hello with the token so rating binds to the account
      track(mode === "signup" ? "sign_up" : "login", {});
      // A fresh sign-up has a code already in the post (the server sends it on
      // the way out), and an older account that never confirmed still owes one.
      // Either way the next screen is the one that explains it, not the lobby.
      if (prof.emailVerified === false) { showVerify(); return; }
      afterAuth(next);
    } catch (e) {
      busy = false;
      submitBtn.toggleAttribute("disabled", false);
      msg.className = "auth-msg bad";
      msg.replaceChildren(authErrorText(e));
      // Nobody is called that: almost always someone who meant to sign up. Hand
      // them the form with the name already in it, rather than a dead end.
      if (e instanceof account.ApiError && e.code === "no_account" && !usernameProblem(name)) {
        msg.append(" ", h("button", {
          class: "linklike auth-create", type: "button",
          onclick: () => showAuth("signup", next, name),
        }, t("authCreateNamed").replace("{name}", name)));
      }
      if (e instanceof account.ApiError && e.code === "email_taken") {
        msg.append(" ", h("button", {
          class: "linklike auth-create", type: "button", onclick: () => showAuth("login", next),
        }, t("logIn")));
      }
    }
  };
  for (const input of [uname, email, pass, disp]) {
    input.addEventListener("keydown", (e: KeyboardEvent) => { if (e.key === "Enter" && !e.isComposing) { e.preventDefault(); void submit(); } });
  }
  const submitBtn = h("button", { class: "btn primary wide", type: "button", onclick: submit }, mode === "signup" ? t("signUp") : t("logIn"));
  const perk = (glyph: string, key: string) =>
    h("div", { class: "perk" }, h("span", { class: "perk-glyph" }, glyph), h("span", {}, t(key)));

  // A visitor sent here by the gate is told why, and where they will land.
  const blocked = gateTarget && gateTarget !== PATH.login && gateTarget !== PATH.signup;

  /**
   * "Continue with Google" — a real link, because it is a real navigation to
   * the server, which redirects on to Google. It starts hidden and is revealed
   * only once the server confirms it has credentials, so a deployment without
   * them shows nothing rather than a button that dead-ends.
   *
   * Where the visitor was headed is kept in `gateTarget`, which lives in this
   * document and would not survive the round-trip through Google — so it is
   * parked in sessionStorage and picked back up when the handoff returns.
   */
  const googleRow = h("div", { class: "auth-google hide" },
    h("div", { class: "auth-or" }, h("span", {}, t("orDivider"))),
    h("a", {
      class: "btn ghost wide google-btn",
      href: account.GOOGLE_START,
      onclick: () => { try { sessionStorage.setItem("xq-oauth-next", gateTarget ?? ""); } catch { /* ignore */ } },
    },
      h("span", { class: "google-g", html: GOOGLE_G_SVG }),
      h("span", {}, t("continueWithGoogle")),
    ),
  );
  void account.googleEnabled().then((on) => { if (on) googleRow.classList.remove("hide"); });

  app.replaceChildren(
    header(),
    h("div", { class: "auth-wrap" },
      h("div", { class: "setup auth-card" },
        ...(blocked || next
          ? [h("div", { class: "gate-note" },
              h("span", { class: "gate-glyph" }, "鎖"),
              h("span", { class: "gate-text" },
                h("b", {}, t("gateTitle")),
                h("span", {}, blocked ? `${t("gateWhere")} ${gateTarget}` : t("signInToPlay")),
              ),
            )]
          : []),
        h("h2", {}, mode === "signup" ? t("signupTitle") : t("loginTitle")),
        h("p", { class: "setup-p" }, mode === "signup" ? t("signupSub") : t("loginSub")),
        uname,
        ...(mode === "signup" ? [rule, email, disp] : []),
        pass,
        ...(mode === "login" ? [h("a", { class: "auth-forgot", href: PATH.forgot }, t("forgotLink"))] : []),
        msg,
        h("div", { class: "setup-actions" }, submitBtn),
        googleRow,
        // Sign-up only: this is the moment the agreement is actually made, and
        // it was not being shown at all. Real links to the content site's
        // pages rather than copies — one set of terms, in the reader's
        // language, and no second copy to fall out of date.
        ...(mode === "signup" ? [h("p", { class: "auth-agree" },
          t("agreePre"),
          h("a", { href: siteUrl("/terms", "signup"), target: "_blank", rel: "noopener" }, t("termsLink")),
          t("agreeMid"),
          h("a", { href: siteUrl("/privacy", "signup"), target: "_blank", rel: "noopener" }, t("privacyLink")),
          t("agreePost"),
        )] : []),
        // Keeps `next`, so the gate still delivers you where you were headed.
        h("div", { class: "auth-switch", onclick: () => showAuth(mode === "signup" ? "login" : "signup", next, uname.value.includes("@") ? "" : uname.value.trim()) },
          mode === "signup" ? t("haveAccount") : t("needAccount")),
      ),
      h("div", { class: "auth-perks" },
        h("div", { class: "moves-title" }, t("whyAccount")),
        perk("分", "perkRating"),
        perk("史", "perkHistory"),
        perk("馬", "perkFriends"),
        perk("課", "perkProgress"),
        h("a", { class: "btn ghost small", href: PATH.home }, t("back")),
      ),
    ),
  );
  pass.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") submit(); });
  uname.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") pass.focus(); });
  uname.focus();
}

/**
 * Forgotten password, in three steps on one page: ask for the address, type the
 * six-digit code that lands in the inbox, choose a new password. The code is
 * checked by the server (five tries, ten minutes) and the ticket it hands back
 * — not the code — is what authorises the change, so a code cannot be replayed
 * once it has been spent.
 */
function showForgot() {
  closeModal();
  navHandler = null;
  enter("forgot", PATH.forgot, t("forgotTitle"), true);

  let email = "";
  let ticket = "";
  const card = h("div", { class: "setup auth-card" });
  const msg = h("div", { class: "auth-msg" });
  const say = (text: string, bad = false) => { msg.className = "auth-msg" + (bad ? " bad" : " ok"); msg.textContent = text; };
  const clearMsg = () => { msg.className = "auth-msg"; msg.textContent = ""; };

  const steps = (n: 1 | 2 | 3) =>
    h("div", { class: "step-row" }, ...[1, 2, 3].map((i) =>
      h("span", { class: "step-dot" + (i === n ? " on" : i < n ? " done" : "") }, i < n ? "✓" : String(i))));

  // ---- step 1: which mailbox ----
  const askEmail = () => {
    const input = h("input", { class: "auth-input", type: "email", maxlength: "120", placeholder: t("email"), autocomplete: "email", value: email }) as HTMLInputElement;
    const go = async () => {
      email = input.value.trim();
      if (!email) { say(t("emailRequired"), true); input.focus(); return; }
      say(t("sending"));
      btn.setAttribute("disabled", "true");
      try {
        await account.requestResetCode(email);
        track("password_reset_request", {});
        askCode(true);
      } catch (e) {
        say((e as Error).message, true);
        btn.removeAttribute("disabled");
      }
    };
    const btn = h("button", { class: "btn primary wide", onclick: go }, t("sendCode"));
    card.replaceChildren(
      steps(1),
      h("h2", {}, t("forgotTitle")),
      h("p", { class: "setup-p" }, t("forgotSub")),
      input,
      msg,
      h("div", { class: "setup-actions" }, btn),
      h("a", { class: "auth-switch", href: PATH.login }, t("backToLogin")),
    );
    clearMsg();
    input.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") void go(); });
    input.focus();
  };

  // ---- step 2: the code from the inbox ----
  const askCode = (justSent: boolean) => {
    const input = h("input", {
      class: "auth-input otp-input", type: "text", inputmode: "numeric", maxlength: "6",
      placeholder: "000000", autocomplete: "one-time-code",
    }) as HTMLInputElement;
    const go = async () => {
      const code = input.value.replace(/\D/g, "");
      if (code.length !== 6) { say(t("codeLabel"), true); return; }
      say(t("sending"));
      try {
        ticket = await account.verifyResetCode(email, code);
        askPassword();
      } catch (e) {
        say((e as Error).message, true);
        input.select();
      }
    };
    const resend = h("button", { class: "btn ghost", onclick: async () => {
      resend.setAttribute("disabled", "true");
      try { await account.requestResetCode(email); say(t("codeSent")); } catch (e) { say((e as Error).message, true); }
      // The server refuses a second code within the minute; match that here so
      // the button cannot promise something it will not deliver.
      let left = 60;
      const tick = setInterval(() => {
        left -= 1;
        resend.textContent = left > 0 ? `${t("resendCode")} (${left})` : t("resendCode");
        if (left <= 0) { clearInterval(tick); resend.removeAttribute("disabled"); }
      }, 1000);
    } }, t("resendCode"));
    card.replaceChildren(
      steps(2),
      h("h2", {}, t("codeSent")),
      h("p", { class: "setup-p" }, t("codeSentSub").replace("{email}", email)),
      input,
      msg,
      h("div", { class: "setup-actions" },
        h("button", { class: "btn primary wide", onclick: go }, t("verifyCode")),
      ),
      h("div", { class: "auth-alt" }, resend, h("button", { class: "btn ghost", onclick: askEmail }, t("back"))),
      h("div", { class: "auth-hint" }, t("checkSpam")),
    );
    if (justSent) say(t("codeSent")); else clearMsg();
    input.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") void go(); });
    input.focus();
  };

  // ---- step 3: the new password ----
  const askPassword = () => {
    const p1 = h("input", { class: "auth-input", type: "password", placeholder: t("newPassword"), autocomplete: "new-password" }) as HTMLInputElement;
    const p2 = h("input", { class: "auth-input", type: "password", placeholder: t("confirmPassword"), autocomplete: "new-password" }) as HTMLInputElement;
    const go = async () => {
      if (p1.value.length < 8) { say(t("passwordShort"), true); return; }
      if (p1.value !== p2.value) { say(t("passwordsDiffer"), true); p2.focus(); return; }
      say(t("sending"));
      try {
        const prof = await account.resetPassword(ticket, p1.value);
        nickname = prof.displayName;
        for (const id of prof.lessonsDone) markDoneLocal(id);
        net?.reconnectFresh(); // the old token is dead; re-hello with the new one
        track("password_reset_done", {});
        showNotice(t("resetDone"));
        afterAuth();
      } catch (e) {
        say((e as Error).message, true);
      }
    };
    card.replaceChildren(
      steps(3),
      h("h2", {}, t("newPasswordTitle")),
      h("p", { class: "setup-p" }, t("newPasswordSub")),
      p1, p2,
      msg,
      h("div", { class: "setup-actions" }, h("button", { class: "btn primary wide", onclick: go }, t("savePassword"))),
    );
    clearMsg();
    p2.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") void go(); });
    p1.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") p2.focus(); });
    p1.focus();
  };

  app.replaceChildren(
    header(),
    h("div", { class: "auth-wrap" },
      card,
      h("div", { class: "auth-perks" },
        h("div", { class: "moves-title" }, t("resetHowTitle")),
        h("div", { class: "perk" }, h("span", { class: "perk-glyph" }, "信"), h("span", {}, t("resetStep1"))),
        h("div", { class: "perk" }, h("span", { class: "perk-glyph" }, "碼"), h("span", {}, t("resetStep2"))),
        h("div", { class: "perk" }, h("span", { class: "perk-glyph" }, "鎖"), h("span", {}, t("resetStep3"))),
        h("a", { class: "btn ghost small", href: PATH.login }, t("backToLogin")),
      ),
    ),
  );
  askEmail();
}

/**
 * Confirm the address a new account signed up with.
 *
 * One step rather than the reset flow's three: the account is already signed in,
 * so there is nothing to identify and nothing to authorise afterwards — the
 * right code simply opens the games up. The screen is reachable on its own at
 * /verify, which is where both the banner and the blocked-action prompt send
 * people, and it is careful to say what still works while they are waiting: a
 * new player who cannot find the mail should not conclude the site is broken.
 */
function showVerify() {
  closeModal();
  navHandler = null;
  enter("verify", PATH.verify, t("verifyTitle"), true);

  const prof = account.currentProfile();
  const addr = prof?.email ?? "";
  const msg = h("div", { class: "auth-msg" });
  const say = (text: string, bad = false) => { msg.className = "auth-msg" + (bad ? " bad" : " ok"); msg.textContent = text; };

  const input = h("input", {
    class: "auth-input otp-input", type: "text", inputmode: "numeric", maxlength: "6",
    placeholder: "000000", autocomplete: "one-time-code",
  }) as HTMLInputElement;

  const go = async () => {
    const code = input.value.replace(/\D/g, "");
    if (code.length !== 6) { say(t("codeLabel"), true); return; }
    say(t("sending"));
    try {
      await account.confirmEmail(code);
      track("email_verified", {});
      // The socket caches nothing about verification, but the server checks the
      // account on every message, so there is no reconnect to do here.
      showNotice(t("verifyDone"));
      afterAuth();
    } catch (e) {
      say((e as Error).message, true);
      input.select();
    }
  };

  const resend = h("button", { class: "btn ghost", onclick: async () => {
    resend.setAttribute("disabled", "true");
    try {
      const already = await account.sendVerification();
      if (already) { showNotice(t("verifyDone")); afterAuth(); return; }
      say(t("verifySent"));
    } catch (e) {
      say((e as Error).message, true);
    }
    // The server refuses a second code inside the minute, so the button counts
    // down rather than promising something it will not deliver.
    let left = 60;
    const tick = setInterval(() => {
      left -= 1;
      resend.textContent = left > 0 ? `${t("resendCode")} (${left})` : t("resendCode");
      if (left <= 0) { clearInterval(tick); resend.removeAttribute("disabled"); }
    }, 1000);
  } }, t("resendCode"));

  app.replaceChildren(
    header(),
    h("div", { class: "auth-wrap" },
      h("div", { class: "setup auth-card" },
        h("h2", {}, t("verifyTitle")),
        h("p", { class: "setup-p" }, t("verifySub").replace("{email}", addr)),
        input,
        msg,
        h("div", { class: "setup-actions" },
          h("button", { class: "btn primary wide", onclick: go }, t("verifyCta")),
        ),
        h("div", { class: "auth-alt" }, resend, h("a", { class: "btn ghost", href: PATH.home }, t("verifyLater"))),
        h("div", { class: "auth-hint" }, t("verifyNoCode")),
      ),
      h("div", { class: "auth-perks" },
        h("div", { class: "moves-title" }, t("verifyHowTitle")),
        h("div", { class: "perk" }, h("span", { class: "perk-glyph" }, "信"), h("span", {}, t("verifyStep1"))),
        h("div", { class: "perk" }, h("span", { class: "perk-glyph" }, "碼"), h("span", {}, t("verifyStep2"))),
        h("div", { class: "perk" }, h("span", { class: "perk-glyph" }, "開"), h("span", {}, t("verifyStep3"))),
        h("div", { class: "auth-hint" }, t("verifyMeanwhile")),
      ),
    ),
  );
  input.focus();
}

/**
 * The server refused something because the address is not confirmed.
 *
 * Handled in one place so that every blocked action — quick pair, a seek, a
 * private room, a challenge, the chat — ends at the same explanation instead of
 * at whatever wording the socket happened to send. Returns true when it has
 * dealt with the message, which tells the board and lobby handlers to leave it
 * alone rather than also flashing a raw error.
 */
function handledUnverified(m: ServerMsg): boolean {
  if (m.t !== "error" || m.code !== "email_unverified") return false;
  promptVerify();
  return true;
}

/** Explain the gate and offer the one screen that clears it. */
function promptVerify() {
  closeModal();
  showModal(t("verifyGateTitle"), t("verifyGateBody"), [
    [t("verifyCta"), () => navigate(PATH.verify), true],
    [t("verifyLater"), () => {}],
  ], "info");
}

/**
 * The push-notification switch.
 *
 * Not a segmented control like the rest of the page: turning it on has to ask
 * the browser for permission, that request is only honoured from a real click,
 * and it can fail in ways worth saying out loud (permission blocked, no service
 * worker, push not configured on the server). So it is one button that reports
 * what actually happened.
 */
function pushControls(): Node[] {
  const btn = h("button", { class: "btn small" }, "…");
  const note = h("span", { class: "friend-msg" });
  let state: PushState = "off";
  let busy = false;

  const paint = () => {
    btn.textContent = state === "on" ? t("optOn") : state === "blocked" ? t("pushBlocked") : t("pushEnable");
    btn.className = "btn small" + (state === "on" ? " primary" : "");
    btn.toggleAttribute("disabled", busy || state === "unsupported" || state === "blocked");
    note.className = "friend-msg" + (state === "blocked" ? " bad" : "");
    note.textContent = state === "unsupported" ? t("pushUnsupported")
      : state === "blocked" ? t("pushBlockedNote")
      : state === "on" ? t("pushOnNote") : "";
  };

  btn.addEventListener("click", async () => {
    if (busy) return;
    busy = true; paint();
    if (state === "on") {
      state = await disablePush();
      track("push_disabled", {});
    } else {
      const r = await enablePush();
      state = r.state;
      track("push_enabled", { ok: r.ok, error: r.error ?? "" });
      if (!r.ok && r.error) {
        note.className = "friend-msg bad";
        note.textContent = r.error === "server" ? t("pushServerOff") : t("pushFailed");
        busy = false;
        btn.toggleAttribute("disabled", false);
        return;
      }
    }
    busy = false;
    paint();
  });

  if (!pushSupported()) { state = "unsupported"; paint(); }
  else void pushState().then((s) => { state = s; paint(); });
  return [btn, note];
}

// ---------------------------------------------------------------- Settings
/**
 * Settings are edited, then saved. Each control still applies at once — the
 * board beside the style list is a live preview, and you cannot judge a theme
 * you cannot see — but nothing is kept until Save is pressed. Leaving the page
 * or closing the tab without saving puts every preference back as it was.
 *
 * `settingsBaseline` is what is saved: taken when the page is entered, retaken
 * on Save, and dropped on the way out. It survives the page re-rendering itself
 * (a language change does), so an edit cannot become "saved" by accident.
 */
interface SettingsSnapshot {
  boardTheme: string; pieces: PieceStyle; theme: Theme; time: TimeOfDay; weather: Weather;
  lang: Lang; hints: boolean; anim: boolean; voice: boolean; sound: boolean; email: boolean; daily: boolean;
}
let settingsBaseline: SettingsSnapshot | null = null;
/** The email switch is a server call, so it is held back until Save rather than previewed. */
let settingsDraftEmail: boolean | null = null;
let settingsDraftDaily: boolean | null = null;

function captureSettings(): SettingsSnapshot {
  return {
    boardTheme: getBoardTheme().id, pieces: getPieceStyle(), theme: getTheme(),
    time: getTimeOfDay(), weather: getWeather(), lang: getLang(),
    hints: hintsOn(), anim: animOn(), voice: voiceOn(), sound: soundEnabled(),
    email: settingsDraftEmail ?? (account.currentProfile()?.emailNotifications !== false),
    daily: settingsDraftDaily ?? (account.currentProfile()?.dailyReminders !== false),
  };
}

/** Put every preference back as it was when the page was entered. */
function revertSettings(to: SettingsSnapshot) {
  setBoardTheme(to.boardTheme); setPieceStyle(to.pieces); setTheme(to.theme);
  setTimeOfDay(to.time); setWeather(to.weather);
  if (getLang() !== to.lang) setLang(to.lang);
  setHints(to.hints); setAnim(to.anim); setVoice(to.voice);
  if (soundEnabled() !== to.sound) toggleSound();
  settingsDraftEmail = null;
  settingsDraftDaily = null;
  repaintBoards();
}

/**
 * Twenty-seven board styles will not fit in a segmented control, and a list of
 * names tells you nothing about how a board looks. Each option is a swatch
 * built from that theme's own colours — frame, surface, and the two piece
 * faces — so the grid reads as a shelf of boards rather than a dropdown.
 *
 * Shared by the Settings page and the picker on the board itself; `onPick`
 * repaints whatever boards the caller is showing.
 */
function boardStyleGrid(onPick: () => void): HTMLElement {
  const grid = h("div", { class: "theme-grid" });
  const paint = () => {
    const active = getBoardTheme().id;
    grid.replaceChildren(...BOARD_THEMES.map((b) => {
      const sw = b.board
        ? h("span", { class: "sw", style: `background:linear-gradient(135deg,${b.board.frameHi},${b.board.frameLo})` },
            h("span", { class: "sw-face", style: `background:linear-gradient(160deg,${b.board.surfHi},${b.board.surfLo})` },
              h("i", { style: `background:${b.red!.faceHi};border-color:${b.red!.bezelMid}` }),
              h("i", { style: `background:${b.blk!.faceHi};border-color:${b.blk!.bezelMid}` }),
            ))
        : h("span", { class: "sw sw-photo" },
            h("span", { class: "sw-face" },
              h("i", { style: "background:#8d2a20;border-color:#d9b45a" }),
              h("i", { style: "background:#241a12;border-color:#d9b45a" }),
            ));
      return h("button", {
        class: "theme-chip" + (b.id === active ? " on" : ""),
        type: "button",
        "aria-pressed": String(b.id === active),
        // The theme's own name. `note` is a design note written for us, in English
        // only, and it was showing as the tooltip in every language.
        title: pick(b.name),
        onclick: () => {
          track("board_theme_change", { theme: b.id });
          setBoardTheme(b.id);
          paint();
          onPick();
        },
      }, sw, h("span", { class: "theme-name" }, pick(b.name)));
    }));
  };
  paint();
  return grid;
}

/** The four piece sets, as one segmented control. */
function pieceStyleSeg(onPick: () => void): HTMLElement {
  return seg([["trad", t("piecesTrad")], ["simp", t("piecesSimp")], ["western", t("piecesWestern")], ["symbol", t("piecesSymbol")]],
    () => getPieceStyle(), (v) => { track("piece_style_change", { style: v }); setPieceStyle(v as PieceStyle); onPick(); });
}

/**
 * The board style, chosen from the board.
 *
 * A player asked whether the board's look could be changed at all — the 27
 * styles were there, but only on the Settings page, three clicks from any game.
 * Here a pick applies to the board underneath at once and is kept: the click
 * is the decision, so there is no Save step as there is on Settings.
 */
function showBoardStyle() {
  closeModal();
  track("board_style_open", {});
  const close = () => { document.removeEventListener("keydown", onKey, true); closeModal(); };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); close(); } };
  document.addEventListener("keydown", onKey, true);
  const box = h("div", { class: "modal tone-info board-style", role: "dialog", "aria-modal": "true", "aria-labelledby": "bs-title" },
    h("div", { class: "modal-title", id: "bs-title" }, t("boardTheme")),
    h("div", { class: "modal-sub" }, t("dBoardTheme")),
    boardStyleGrid(() => repaintBoards()),
    h("div", { class: "bs-pieces" }, h("span", { class: "bs-label" }, t("pieces")), pieceStyleSeg(() => repaintBoards())),
    h("div", { class: "modal-actions" }, h("button", { class: "btn primary", type: "button", onclick: close }, t("close"))),
  );
  document.body.append(h("div", { class: "modal-backdrop", onclick: (e: Event) => { if (e.target === e.currentTarget) close(); } }, box));
}

/**
 * Preferences live on their own page rather than on the home screen — a landing
 * page covered in segmented controls is the main reason the app read as
 * unfinished — and on a page rather than a pop-over, so it can be linked to.
 */
function showSettings() {
  closeModal();
  navHandler = null;
  enter("settings", PATH.settings, t("settings"));
  if (!settingsBaseline) settingsBaseline = captureSettings();

  // Save sits at the top right of the page header, where the title is, so it is
  // found without scrolling past every group. It is live only while there is
  // something to save.
  const saveBtn = h("button", { class: "btn primary set-save", type: "button", disabled: "" }, t("settingsSave"));
  const saveNote = h("span", { class: "set-save-note", "aria-live": "polite" });
  const isDirty = () => !!settingsBaseline && JSON.stringify(captureSettings()) !== JSON.stringify(settingsBaseline);
  const refreshSave = (justSaved = false) => {
    const d = isDirty();
    saveBtn.toggleAttribute("disabled", !d);
    saveNote.classList.toggle("warn", d);
    saveNote.textContent = d ? t("settingsUnsaved") : justSaved ? t("settingsSaved") : "";
  };
  saveBtn.addEventListener("click", async () => {
    if (!isDirty()) return;
    saveBtn.toggleAttribute("disabled", true);
    if (settingsDraftEmail !== null) {
      const want = settingsDraftEmail;
      await account.setEmailNotifications(want);
      settingsDraftEmail = null;
    }
    if (settingsDraftDaily !== null) {
      const want = settingsDraftDaily;
      await account.setDailyReminders(want);
      settingsDraftDaily = null;
    }
    settingsBaseline = captureSettings();
    track("settings_save", {});
    refreshSave(true);
    setTimeout(() => { if (!isDirty()) refreshSave(); }, 2400);
  });

  /**
   * One preference: what it is called, what it actually does, and the control.
   *
   * The description is not decoration. "Piece hints" and "Animations" mean
   * nothing until you are told that one draws legal-move dots and marks pieces
   * under attack while the other only slides them — and a settings page whose
   * labels you have to guess at is a settings page people leave alone.
   */
  const row = (label: string, desc: string, ...controls: Node[]) =>
    h("div", { class: "settings-row" },
      h("span", { class: "set-text" },
        h("span", { class: "set-label" }, label),
        desc ? h("span", { class: "set-desc" }, desc) : h("span", { class: "set-desc" }),
      ),
      h("div", { class: "set-controls" }, ...controls),
    );

  /** A titled group, marked with a glyph so the sections are told apart at a glance. */
  const group = (glyph: string, title: string, sub: string, ...rows: Node[]) =>
    h("section", { class: "set-group" },
      h("div", { class: "set-group-head" },
        h("span", { class: "set-group-glyph" }, glyph),
        h("span", { class: "set-group-titles" },
          h("h3", { class: "set-group-title" }, title),
          sub ? h("span", { class: "set-group-sub" }, sub) : h("span", {}),
        ),
      ),
      h("div", { class: "panel-card set-card" }, ...rows),
    );

  // A board style is impossible to choose from a word, so the setting shows one.
  // The preview is a real BoardView in the opening position — exactly what a
  // game will look like, pieces, grid, river and all.
  const previewHost = h("div", { class: "board-preview" });
  const preview = new BoardView(previewHost, () => { /* preview only */ });
  const paintPreview = () => preview.setState({
    pos: parseFen(START_FEN),
    lastMove: null,
    selectable: null,
    viewer: Color.Red,
    hideDanger: true,
  });
  paintPreview();

  const themeGrid = boardStyleGrid(() => { paintPreview(); repaintBoards(); });
  app.replaceChildren(
    header(),
    h("div", { class: "lessons settings-page" },
      // A page header of its own rather than the lessons list's, so the page
      // states what it is and that nothing here is permanent.
      h("header", { class: "set-head" },
        h("div", { class: "set-head-text" },
          h("h1", { class: "set-head-title" }, t("settings")),
          h("p", { class: "set-head-sub" }, t("settingsIntro")),
        ),
        h("div", { class: "set-head-actions" }, saveNote, saveBtn),
      ),

      // The board comes first: it is the thing people came here to change, and
      // the preview makes the whole page make sense at a glance.
      h("section", { class: "set-group set-board" },
        h("div", { class: "set-group-head" },
          h("span", { class: "set-group-glyph" }, "盤"),
          h("span", { class: "set-group-titles" },
            h("h3", { class: "set-group-title" }, t("boardTheme")),
            h("span", { class: "set-group-sub" }, t("dBoardTheme")),
          ),
        ),
        h("div", { class: "panel-card set-card board-picker" },
          previewHost,
          themeGrid,
        ),
      ),

      group("色", t("groupAppearance"), t("groupAppearanceSub"),
        row(t("pieces"), t("dPieces"), pieceStyleSeg(() => { paintPreview(); repaintBoards(); })),
        row(t("theme"), t("dTheme"), seg([["light", t("themeLight")], ["dark", t("themeDark")]], () => getTheme(),
          (v) => { setTheme(v as Theme); track("theme_toggle", { theme: v }); })),
        row(t("scene"), t("dScene"),
          seg([["day", t("sceneDay")], ["night", t("sceneNight")]], () => getTimeOfDay(), (v) => setTimeOfDay(v as TimeOfDay)),
          seg([["clear", t("weatherClear")], ["rain", t("weatherRain")], ["snow", t("weatherSnow")]], () => getWeather(), (v) => setWeather(v as Weather)),
        ),
        row(t("language"), t("dLanguage"), seg([["zh", "中文"], ["en", "English"], ["vi", "Tiếng Việt"]], () => getLang(),
          (v) => { track("language_change", { lang: v }); setLang(v as Lang); void account.reportTimezone(); showSettings(); })),
      ),

      // How the board behaves while you play — the same switches the game
      // panel carries, so a preference set here is the one you see at the board.
      group("弈", t("groupPlay"), t("groupPlaySub"),
        row(t("pieceHints"), t("dHints"), seg([["on", t("optOn")], ["off", t("optOff")]], () => (hintsOn() ? "on" : "off"),
          (v) => { setHints(v === "on"); repaintBoard?.(); paintPreview(); track("hints_toggle", { enabled: v === "on" }); })),
        row(t("animations"), t("dAnim"), seg([["on", t("optOn")], ["off", t("optOff")]], () => (animOn() ? "on" : "off"),
          (v) => setAnim(v === "on"))),
        row(t("sound"), t("dSound"), soundBtn()),
        row(t("voiceMoves"), t("dVoice"), seg([["on", t("optOn")], ["off", t("optOff")]], () => (voiceOn() ? "on" : "off"),
          (v) => { setVoice(v === "on"); if (v === "on") speak(t("voiceMoves"), getLang()); else stopSpeaking(); })),
      ),

      // Email is opt-out, so the switch has to be somewhere obvious. Push is
      // opt-IN — it needs a permission the browser will only grant from a click.
      ...(account.isLoggedIn() ? [group("戶", t("groupAccount"), t("groupAccountSub"),
        row(t("emailNotifications"), t("dEmail"),
          seg([["on", t("optOn")], ["off", t("optOff")]],
            () => (captureSettings().email ? "on" : "off"),
            (v) => { settingsDraftEmail = v === "on"; })),
        row(t("dailyReminders"), t("dDaily"),
          seg([["on", t("optOn")], ["off", t("optOff")]],
            () => (captureSettings().daily ? "on" : "off"),
            (v) => { settingsDraftDaily = v === "on"; })),
        row(t("pushNotifications"), t("dPush"), ...pushControls()),
      )] : []),

      group("言", t("groupFeedback"), t("groupFeedbackSub"),
        row(t("fbTitle"), t("fbSub"),
          h("button", { class: "btn ghost", type: "button", onclick: () => showFeedback() }, t("fbOpen"))),
      ),

      h("div", { class: "setup-actions" }, h("a", { class: "btn ghost", href: PATH.home }, t("home"))),
    ),
  );
  // Any click on the page may have changed a preference; look once it has landed.
  app.querySelector(".settings-page")?.addEventListener("click", (e) => {
    if ((e.target as Element | null)?.closest(".set-save")) return; // Save reports its own result
    setTimeout(() => refreshSave(), 0);
  });
  refreshSave();
}

// ---------------------------------------------------------------- Bot setup
/**
 * The five engine levels, as five opponents.
 *
 * "Difficulty: Easy / Medium / Hard / Master" told a visitor nothing about what
 * they were about to play. A named opponent with a character does, and picking
 * one is a decision rather than a setting.
 *
 * No invented ratings. Everything factual on these cards is read out of the
 * engine's own LEVELS table below, so it cannot drift away from what the engine
 * actually does when somebody retunes it -- including the deliberate mistake
 * rate, which is a real number in that table and the most useful thing a
 * beginner can know about their opponent.
 */
const OPPONENTS: { level: Level; glyph: string; nameKey: string; styleKey: string }[] = [
  { level: "beginner", glyph: "\u65b0", nameKey: "botBeginnerName", styleKey: "botBeginnerStyle" },
  { level: "easy", glyph: "\u5b66", nameKey: "botEasyName", styleKey: "botEasyStyle" },
  { level: "medium", glyph: "\u8336", nameKey: "botMediumName", styleKey: "botMediumStyle" },
  { level: "hard", glyph: "\u68cb", nameKey: "botHardName", styleKey: "botHardStyle" },
  { level: "master", glyph: "\u56fd", nameKey: "botMasterName", styleKey: "botMasterStyle" },
];

/**
 * The opponent to offer first: the last one this player picked, else the
 * Newcomer. It used to be "hard" for everybody, so a newcomer who simply
 * pressed Play met a nine-ply search that never errs on purpose, and wrote in
 * to say the AI could not be beaten. Then it was the Student (easy) -- and a
 * player wrote in again (2026-10-19, on a phone): "the AI is too hard, I can't
 * win at all". The Student still punishes every hanging piece; the Newcomer
 * (beginner, one ply) does not, and is where a first game starts now. A player
 * who already chose the Student keeps it.
 */
const BOT_LEVEL = "xq-bot-level";
function preferredBotLevel(): Level {
  try {
    const v = localStorage.getItem(BOT_LEVEL);
    if (v && OPPONENTS.some((o) => o.level === v)) return v as Level;
  } catch { /* storage unavailable */ }
  return "beginner";
}
function rememberBotLevel(level: Level): void {
  try { localStorage.setItem(BOT_LEVEL, level); } catch { /* storage unavailable */ }
}

/** A saved computer game, if one was left unfinished. */
function resumableBotGame(): { level: Level; human: Color; moves: Move[] } | null {
  try {
    const raw = JSON.parse(localStorage.getItem(RESUME_BOT) ?? "null");
    if (!raw || !Array.isArray(raw.moves) || raw.moves.length === 0) return null;
    return raw as { level: Level; human: Color; moves: Move[] };
  } catch {
    return null;
  }
}

function showBotSetup() {
  closeModal();
  navHandler = null;
  enter("bot_setup", PATH.bot, t("botTitle"));
  let level: Level = preferredBotLevel();
  let side: "red" | "black" | "random" = "red";

  const grid = h("div", { class: "opp-grid" });
  const paintGrid = () => {
    grid.replaceChildren(...OPPONENTS.map((o) => {
      const p = LEVELS[o.level];
      // Plies, not "moves": depth 2 is one move each, and rounding that up to
      // "thinks 2 moves ahead" would be a small lie on the card that matters
      // most to a beginner.
      const facts = [
        `${p.maxDepth} ${t(p.maxDepth === 1 ? "ply" : "plies")}`,
        `${(p.moveTimeMs / 1000).toFixed(p.moveTimeMs < 1000 ? 2 : 0)}s`,
        ...(p.blunder > 0 ? [`${Math.round(p.blunder * 100)}% ${t("botSlips")}`] : []),
      ];
      return h("button", {
        class: "opp" + (o.level === level ? " on" : ""),
        type: "button",
        "aria-pressed": String(o.level === level),
        onclick: () => { level = o.level; paintGrid(); },
      },
        h("span", { class: "opp-face" }, o.glyph),
        h("span", { class: "opp-body" },
          h("span", { class: "opp-name" }, t(o.nameKey)),
          h("span", { class: "opp-style" }, t(o.styleKey)),
          h("span", { class: "opp-facts" }, ...facts.map((f) => h("i", {}, f))),
        ),
      );
    }));
  };
  paintGrid();

  const start = () => {
    unlockAudio();
    const c = side === "random" ? (Math.random() < 0.5 ? Color.Red : Color.Black) : side === "red" ? Color.Red : Color.Black;
    try { localStorage.removeItem(RESUME_BOT); } catch { /* ignore */ }
    rememberBotLevel(level);
    new LocalGame(level, c);
  };

  // A half-finished game is the one thing somebody arriving here might want
  // more than a new one, and it was reachable only by guessing the URL.
  const saved = resumableBotGame();
  const resume = saved
    ? h("div", { class: "opp-resume" },
        h("span", { class: "opp-resume-text" },
          h("b", {}, t("resumeBotTitle")),
          h("span", {}, `${t(saved.level)} \u00b7 ${saved.moves.length} ${t("movesLower")}`),
        ),
        h("button", { class: "btn primary", onclick: () => { unlockAudio(); new LocalGame(saved.level, saved.human, saved.moves); } }, t("resumeBot")),
        h("button", { class: "btn ghost", onclick: () => {
          try { localStorage.removeItem(RESUME_BOT); } catch { /* ignore */ }
          showBotSetup();
        } }, t("discardBot")),
      )
    : null;

  app.replaceChildren(
    header("play"),
    h("div", { class: "lessons opp-page" },
      h("header", { class: "set-head" },
        h("div", { class: "set-head-text" },
          h("h1", { class: "set-head-title" }, t("botTitle")),
          h("p", { class: "set-head-sub" }, t("botIntro")),
        ),
      ),
      ...(resume ? [resume] : []),
      grid,
      h("div", { class: "opp-side" },
        h("span", { class: "setup-label" }, t("yourSide")),
        seg([["red", t("sideRed")], ["black", t("sideBlack")], ["random", t("sideRandom")]], () => side, (v) => (side = v as typeof side)),
      ),
      h("div", { class: "setup-actions" },
        h("a", { class: "btn ghost", href: PATH.play }, t("back")),
        h("button", { class: "btn primary", onclick: start }, t("start")),
      ),
      h("p", { class: "opp-note" }, t("botNote")),
    ),
  );
}

// ---------------------------------------------------------------- Play hub (lobby)
/**
 * The Play screen. Choosing "Play" must never dump you onto a board: this is
 * where you pick *how* you want to play — a pool to be paired in, an open
 * challenge posted by (or to) another player, the engine, or a private room.
 *
 * While it is open the client subscribes to the server lobby, so the open
 * challenge list and the online/in-play counters stay live.
 */
let lobbyTarget: { seeks?: HTMLElement; stats?: HTMLElement; count?: HTMLElement } | null = null;

function showPlay() {
  if (gameLive()) { guardLeaveGame(showPlay); return; }
  closeModal();
  navHandler = null;
  enter("play", PATH.play, t("navPlay"));
  ensureNet();
  net!.send({ t: "lobby", on: true });

  const signedIn = account.isLoggedIn();
  let rated = getRated();
  let color: ColorPref = "random";

  // -- quick pair: one button per pool --
  const tcGrid = h("div", { class: "tc-grid" });
  for (const c of TIME_CONTROLS) {
    tcGrid.append(h("button", { class: "tc-card" + (c.id === getTc() ? " on" : ""), onclick: () => { unlockAudio(); startOnline(c.id, rated); } },
      h("span", { class: "tc-id" }, c.id),
      h("span", { class: "tc-cat" }, t(catKey[c.cat])),
    ));
  }

  const ratedSeg = seg([["rated", t("rated")], ["casual", t("casual")]],
    () => (rated ? "rated" : "casual"),
    (v) => { rated = v === "rated"; setRated(rated); });
  const colorSeg = seg([["red", t("sideRed")], ["random", t("sideRandom")], ["black", t("sideBlack")]],
    () => color, (v) => (color = v as ColorPref));

  const seekList = h("div", { class: "seek-list" }, ...skeletonRows(3));
  const seekCount = h("span", { class: "progress" }, "");
  const stats = h("span", { class: "lobby-stats" }, "");
  lobbyTarget = { seeks: seekList, stats, count: seekCount };

  const card = (title: string, sub: string, glyph: string, path: string, mode: string) =>
    h("a", { class: "mode-card", href: path, onclick: () => { unlockAudio(); track("select_mode", { mode }); } },
      h("div", { class: "mode-glyph" }, glyph),
      h("div", { class: "mode-text" }, h("div", { class: "mode-title" }, title), h("div", { class: "mode-sub" }, sub)),
    );

  app.replaceChildren(
    header("play"),
    h("div", { class: "hub play-hub" },
      h("div", { class: "lessons-head" }, h("h2", {}, t("playTitle")), stats),
      liveGameBanner(),
      verifyBanner(),
      ...(signedIn ? [] : [h("div", { class: "panel-card join-card" },
        h("div", { class: "join-card-main" },
          h("div", { class: "join-card-title" }, t("accountRequired")),
          h("div", { class: "join-card-sub" }, t("accountRequiredSub")),
        ),
        h("div", { class: "join-card-actions" },
          h("a", { class: "btn primary", href: PATH.signup }, t("signUp")),
          h("a", { class: "btn ghost", href: PATH.login }, t("logIn")),
        ),
      )]),

      h("div", { class: "play-main" },
      h("div", { class: "panel-card hub-card" },
        h("div", { class: "hub-card-head" },
          h("div", { class: "moves-title" }, t("quickPair")),
          ratedSeg,
        ),
        tcGrid,
        h("div", { class: "hub-note" }, t("quickPairNote")),
      ),

      h("div", { class: "panel-card hub-card" },
        h("div", { class: "hub-card-head" },
          h("div", { class: "moves-title" }, t("openChallenges")),
          seekCount,
        ),
        seekList,
        h("div", { class: "seek-form" },
          h("span", { class: "set-label" }, t("yourSide")), colorSeg,
          h("button", { class: "btn primary", onclick: () => {
            if (!account.isLoggedIn()) { showAuthFor(() => showPlay()); return; }
            unlockAudio();
            ensureNet();
            setRated(rated);
            remote = new RemoteGame("online");
            rememberMode("online");
            track("seek_posted", { tc: getTc(), rated });
            net!.send({ t: "postSeek", tc: getTc(), rated, color });
          } }, t("postSeek")),
        ),
        h("div", { class: "hub-note" }, t("openChallengesNote")),
        h("div", { class: "hub-note bots-note" }, t("botsNotice")),
      ),
      ),

      h("div", { class: "play-side" },
        h("div", { class: "section-head" }, h("h2", { class: "section-title" }, t("moreWays"))),
        h("div", { class: "mode-grid stack" },
          card(t("playBot"), t("playBotSub"), "帥", PATH.bot, "bot"),
          card(t("playFriends"), t("playFriendsSub"), "炮", PATH.private, "friends"),
          card(t("watchTitle"), t("watchSub"), "觀", PATH.watch, "watch"),
          card(t("friends"), t("friendsSub"), "馬", PATH.friends, "friend_list"),
        ),
      ),
    ),
  );
  renderLobby(null);
}

/**
 * The last counts the lobby sent. Kept so the Play menu can show something true
 * the moment it opens, instead of waiting for the next push.
 */
let lobbyOnline = 0;
let lobbyPlaying = 0;

/** Paint the open-challenge board. `null` means "nothing received yet". */
function renderLobby(msg: { seeks: Seek[]; online: number; playing: number } | null) {
  if (msg) { lobbyOnline = msg.online; lobbyPlaying = msg.playing; }
  const target = lobbyTarget;
  if (!target) return;
  if (!msg) return; // keep the placeholder until the first lobby message lands
  if (target.stats) target.stats.textContent = `${msg.online} ${t("playersOnline")} · ${msg.playing} ${t("gamesInPlay")}`;
  if (target.count) target.count.textContent = String(msg.seeks.length);
  if (!target.seeks) return; // the home hero only wants the counters
  if (!msg.seeks.length) {
    target.seeks.replaceChildren(h("div", { class: "dim" }, t("noOpenChallenges")));
    return;
  }
  const colorGlyph: Record<ColorPref, string> = { red: "帥", black: "將", random: "?" };
  target.seeks.replaceChildren(...msg.seeks.map((s) =>
    h("div", { class: "seek-row" + (s.mine ? " mine" : "") },
      h("span", { class: "seek-color " + s.color }, colorGlyph[s.color]),
      h("span", { class: "seek-name" }, s.mine ? t("yourChallenge") : s.name),
      h("span", { class: "seek-rating" }, String(s.rating)),
      h("span", { class: "seek-tc" }, tcLabel(s.tc)),
      h("span", { class: "seek-mode" }, s.rated ? t("rated") : t("casual")),
      s.mine
        ? h("button", { class: "btn small ghost", onclick: () => net?.send({ t: "cancelSeek" }) }, t("cancel"))
        : h("button", { class: "btn small primary", onclick: () => {
            if (!account.isLoggedIn()) { showAuthFor(() => showPlay()); return; }
            unlockAudio();
            ensureNet();
            remote = new RemoteGame("online");
            rememberMode("online");
            track("seek_accepted", { tc: s.tc, rated: s.rated });
            net!.send({ t: "acceptSeek", id: s.id });
          } }, t("acceptChallenge")),
    ),
  ));
}

// ---------------------------------------------------------------- Friends / online
function showFriendsSetup() {
  if (!account.isLoggedIn()) { showAuthFor(() => showFriendsSetup()); return; }
  closeModal();
  enter("friends_setup", PATH.private, t("friendsTitle"));
  navHandler = null;
  let tc = getTc();
  const codeInput = h("input", { class: "code-input", type: "text", maxlength: "5", placeholder: t("roomCodePlaceholder") }) as HTMLInputElement;
  app.replaceChildren(
    header("play"),
    h("div", { class: "setup" },
      h("h2", {}, t("friendsTitle")),
      h("p", { class: "setup-p" }, t("friendsDesc")),
      h("div", { class: "setup-row" }, h("span", { class: "setup-label" }, t("timeControl")),
        seg(TIME_CONTROLS.map((c) => [c.id, c.id] as [string, string]), () => tc, (v) => { tc = v as TimeControlId; setTc(tc); })),
      h("div", { class: "setup-actions" }, h("button", { class: "btn primary", onclick: () => createRoom(tc) }, t("createRoom"))),
      h("div", { class: "join-row" }, codeInput, h("button", { class: "btn", onclick: () => joinRoom(codeInput.value.trim().toUpperCase()) }, t("join"))),
      h("div", { class: "setup-actions" }, h("a", { class: "btn ghost", href: PATH.play }, t("back"))),
    ),
  );
}

function overlay(msg: string, extra?: Node) {
  navHandler = null;
  const box = h("div", { class: "setup" }, h("h2", {}, msg));
  if (extra) box.append(extra);
  box.append(h("div", { class: "setup-actions" }, h("button", { class: "btn ghost", onclick: () => { net?.send({ t: "cancelQueue" }); showHome(); } }, t("cancel"))));
  app.replaceChildren(header(), box);
}

let remote: RemoteGame | null = null;
function startOnline(tc: TimeControlId = getTc(), rated: boolean = getRated()) {
  if (!account.isLoggedIn()) { showAuthFor(() => startOnline(tc, rated)); return; }
  rememberMode("online"); ensureNet(); screen("matchmaking"); track("matchmaking_start", { tc, rated });
  setTc(tc); setRated(rated);
  remote = new RemoteGame("online");
  net!.send({ t: "queue", tc, rated });
  searchingOverlay(tc, rated);
}

function searchingOverlay(tc: TimeControlId = getTc(), rated: boolean = getRated()) {
  const t0 = Date.now();
  const timeEl = h("span", { class: "search-time" }, "0s");
  const fallback = h("button", { class: "btn primary hide", onclick: () => {
    net?.send({ t: "cancelQueue" });
    const c = Math.random() < 0.5 ? Color.Red : Color.Black;
    // The player's own level, not a fixed "hard" they never chose.
    new LocalGame(preferredBotLevel(), c);
  } }, t("playBotInstead"));
  const aloneNote = h("p", { class: "dim hide" }, t("searchingAlone"));
  const box = h("div", { class: "setup" },
    h("h2", {}, t("searching")),
    h("div", { class: "tc-pill-row" }, h("span", { class: "tc-pill" }, `${tcLabel(tc)} · ${rated ? t("rated") : t("casual")}`)),
    h("p", { class: "search-elapsed" }, h("span", {}, `${t("elapsed")} `), timeEl),
    h("p", { class: "dim" }, t("seekWaitingNote")),
    h("p", { class: "dim bots-note" }, t("botsNotice")),
    aloneNote,
    h("div", { class: "setup-actions" }, fallback,
      h("button", { class: "btn ghost", onclick: () => { net?.send({ t: "cancelQueue" }); showPlay(); } }, t("cancel"))),
  );
  app.replaceChildren(header("play"), box);
  const timer = setInterval(() => {
    if (!box.isConnected) { clearInterval(timer); return; } // matched or navigated away
    const secs = Math.floor((Date.now() - t0) / 1000);
    timeEl.textContent = `${secs}s`;
    if (secs >= 12) { fallback.classList.remove("hide"); aloneNote.classList.remove("hide"); }
  }, 500);
}
function createRoom(tc: TimeControlId = getTc()) {
  if (!account.isLoggedIn()) { showAuthFor(() => createRoom(tc)); return; }
  rememberMode("friends"); unlockAudio(); ensureNet(); track("room_created", { tc });
  remote = new RemoteGame("friends");
  net!.send({ t: "createRoom", tc });
  overlay(t("creating"));
}
function joinRoom(code: string) {
  if (!code) return;
  // A shared invite link is the one path that reaches this while signed out.
  if (!account.isLoggedIn()) { showAuthFor(() => joinRoom(code)); return; }
  rememberMode("friends"); unlockAudio(); ensureNet(); track("room_joined"); remote = new RemoteGame("friends");
  remote.setRoomCode(code); // so the guest's board shows the code too
  net!.send({ t: "joinRoom", code });
  overlay(`${t("joining")} ${code}…`);
}

// ---------------------------------------------------------------- shared history UI
interface HistoryHost {
  moves: Move[];
  viewPly: number | null; // null = live
  setView(ply: number | null): void;
}

function historyBar(host: HistoryHost) {
  const btn = (icon: "first" | "prev" | "next" | "last", title: string, fn: () => void) =>
    h("button", { class: "nav-btn", title, "aria-label": title, onclick: fn }, ico(icon));
  return h("div", { class: "hist-nav" },
    btn("first", t("navStart"), () => host.setView(0)),
    btn("prev", t("navPrev"), () => host.setView(Math.max(0, (host.viewPly ?? host.moves.length) - 1))),
    btn("next", t("navNext"), () => host.setView(Math.min(host.moves.length, (host.viewPly ?? host.moves.length) + 1))),
    btn("last", t("navLive"), () => host.setView(null)),
  );
}

/** How each verdict is written next to the move, and what colour it takes. */
const VERDICT_MARK: Record<Verdict, { sym: string; cls: string }> = {
  best: { sym: "\u2605", cls: "v-best" },
  good: { sym: "", cls: "" },
  inaccuracy: { sym: "?!", cls: "v-inacc" },
  mistake: { sym: "?", cls: "v-mistake" },
  blunder: { sym: "??", cls: "v-blunder" },
};

function renderMoveList(el: HTMLElement, host: HistoryHost, marks?: Map<number, Verdict>) {
  el.replaceChildren();
  // On a phone this list is a third of the screen from the first paint, and
  // before Red has moved there is nothing to put in it.
  if (host.moves.length === 0) {
    el.append(h("div", { class: "moves-empty" }, t("noMovesYet")));
    return;
  }
  const current = host.viewPly ?? host.moves.length;
  for (let i = 0; i < host.moves.length; i += 2) {
    const cell = (idx: number) => {
      if (!host.moves[idx]) return h("span", { class: "mv" }, "");
      const v = marks?.get(idx);
      const mark = v ? VERDICT_MARK[v] : null;
      const c = h("span", { class: "mv" + (idx + 1 === current ? " on" : ""), onclick: () => host.setView(idx + 1) },
        coord(host.moves[idx]!),
        ...(mark && mark.sym ? [h("i", { class: `vmark ${mark.cls}` }, mark.sym)] : []));
      return c;
    };
    el.append(h("div", { class: "row" },
      h("span", { class: "num" }, `${i / 2 + 1}.`),
      cell(i), cell(i + 1),
    ));
  }
  const active = el.querySelector<HTMLElement>(".mv.on");
  if (active) {
    // Scroll the list, never the page. `scrollIntoView` walks up every
    // scrollable ancestor, and on a phone the game layout is stacked and the
    // document itself scrolls — so after each move the viewport was yanked away
    // from the board and down to the move list, mid-game.
    const want = active.offsetTop - el.clientHeight / 2 + active.offsetHeight / 2;
    el.scrollTop = Math.max(0, Math.min(want, el.scrollHeight - el.clientHeight));
  } else {
    el.scrollTop = el.scrollHeight;
  }
}

// ---------------------------------------------------------------- Repetition warning
/*
 * "长将，请变着": the warning before a repetition loses the game. A player lost
 * a rated game by perpetual check (DP3EUT6KK2, 2026-10-25) and asked why --
 * the ruling was right, and nothing had said it was coming. repwarn.ts decides
 * when it applies; this paints it, on both the online board and the computer
 * board, above the board where the eye already is, and as an alert so a screen
 * reader says it when it appears.
 */
let repWarnMemo: { key: string; value: RepetitionWarning | null } = { key: "", value: null };

/** repetitionWarning(), remembered for the last move list: render() runs on every clock tick and click. */
function repWarningFor(moves: readonly Move[], side: Color): RepetitionWarning | null {
  const key = `${side}:${moves.map((m) => m.from * 90 + m.to).join(",")}`;
  if (repWarnMemo.key !== key) repWarnMemo = { key, value: repetitionWarning(moves, side) };
  return repWarnMemo.value;
}

function repWarnBox(): HTMLElement {
  return h("div", { class: "rep-warn", role: "alert" });
}

/** Show, change or clear the warning. Text is only rewritten on a change, so a screen reader hears it once. */
function paintRepWarn(el: HTMLElement | undefined, kind: RepetitionWarning | null, mode: string) {
  if (!el) return;
  const want = kind ?? "";
  if ((el.dataset.kind ?? "") === want) return;
  el.dataset.kind = want;
  el.className = "rep-warn" + (kind ? " on" : "");
  el.textContent = kind ? t(kind === "perpetual-check" ? "repWarnCheck" : "repWarnChase") : "";
  if (kind) track("repetition_warning", { mode, kind });
}

/** The short form, for the one-line status under the turn. */
const repWarnShort = (kind: RepetitionWarning) => t(kind === "perpetual-check" ? "repWarnCheckShort" : "repWarnChaseShort");

// ---------------------------------------------------------------- Local (bot) game
class LocalGame implements HistoryHost {
  private game = new Game(parseFen(START_FEN));
  moves: Move[] = [];
  viewPly: number | null = null;
  private historyFens: string[] = [];
  private worker!: Worker;
  private board!: BoardView;
  private thinking = false;
  private hint: Move | null = null;
  private statusEl!: HTMLElement;
  private subEl!: HTMLElement;
  private movesEl!: HTMLElement;
  private bannerEl!: HTMLElement;
  private trayTop!: HTMLElement;
  private trayBottom!: HTMLElement;
  /** The same status and moves again, for the strip a phone keeps on screen. */
  private mStatusEl!: HTMLElement;
  private mSubEl!: HTMLElement;
  private mMovesEl!: HTMLElement;
  private stripTop!: HTMLElement;
  private stripBottom!: HTMLElement;
  private repWarnEl!: HTMLElement;

  private reviewTracked = false;
  private firstMoveTracked = false;

  /**
   * The result of THIS game, repetition included.
   *
   * Game.status() is position-local on purpose, so it never sees a repetition.
   * A real game has to, and a game against the computer is a real game: until
   * this existed, a perpetual check or chase against the engine simply carried
   * on forever, while the same position between two people was adjudicated.
   */
  private result(): GameResult {
    return this.game.outcome();
  }

  /** Why the game ended: the slug for analytics, and the sentence for the modal. */
  private endReason(): { slug: string; text: string } {
    const slug = endSlug(this.game) ?? "draw";
    const text: Record<string, string> = {
      checkmate: t("reasonCheckmate"),
      stalemate: t("reasonStalemate"),
      "perpetual-check": t("reasonPerpetualCheck"),
      "perpetual-chase": t("reasonPerpetualChase"),
      "threefold-repetition": t("reasonRepetition"),
      draw: t("reasonDraw"),
    };
    return { slug, text: text[slug] ?? t("reasonDraw") };
  }

  constructor(private level: Level, private human: Color, resumeMoves?: Move[]) {
    enter("game_bot", PATH.botGame, `${t("bot")} · ${t("liveGame")}`);
    localGame = this;
    setStuckPath(PATH.botGame); // the back button is refused while this is live
    if (!resumeMoves?.length) {
      track("game_start", { mode: "bot", level, side: human === Color.Red ? "red" : "black" });
    }
    this.spawnWorker();
    // Replay a game that was in progress before a page refresh.
    for (const m of resumeMoves ?? []) {
      this.historyFens.push(toFen(this.game.position));
      this.game.play(m);
      this.moves.push(m);
    }
    this.renderScreen();
    if (this.result() === GameResult.Ongoing && this.game.position.sideToMove !== this.human) this.triggerBot();
  }

  /** True until somebody wins, loses or draws. */
  isLive(): boolean { return this.result() === GameResult.Ongoing; }

  /** The player chose to exit: stop the engine and forget the saved board. */
  abandon() {
    this.worker.terminate();
    try { localStorage.removeItem(RESUME_BOT); } catch { /* ignore */ }
    if (localGame === this) localGame = null;
  }

  /** Persist just enough to rebuild this game after a refresh. */
  private persist() {
    try {
      if (this.result() !== GameResult.Ongoing || this.moves.length === 0) localStorage.removeItem(RESUME_BOT);
      else localStorage.setItem(RESUME_BOT, JSON.stringify({ level: this.level, human: this.human, moves: this.moves }));
    } catch { /* storage full or unavailable */ }
  }

  setView(ply: number | null) {
    this.viewPly = ply === null || ply >= this.moves.length ? null : Math.max(0, ply);
    if (this.viewPly !== null && !this.reviewTracked) { this.reviewTracked = true; track("history_review", { mode: "bot" }); }
    this.render();
  }

  private renderScreen() {
    const boardHost = h("div", { class: "board-wrap" });
    this.statusEl = h("div", { class: "status" });
    this.subEl = h("div", { class: "substatus" });
    this.movesEl = h("div", { class: "moves" });
    this.bannerEl = h("div", { class: "review-banner" });
    this.repWarnEl = repWarnBox();
    this.trayTop = h("div", { class: "tray" });
    this.trayBottom = h("div", { class: "tray" });
    const label = { beginner: t("beginner"), easy: t("easy"), medium: t("medium"), hard: t("hard"), master: t("master") }[this.level];
    // Second copies for the strip a phone keeps under the board — see the same
    // pair on the online game.
    this.mStatusEl = h("div", { class: "status" });
    this.mSubEl = h("div", { class: "substatus" });
    this.mMovesEl = h("div", { class: "moves" });
    this.stripTop = playerStrip({ at: "top", name: t("bot"), badge: label, tray: this.trayTop });
    this.stripBottom = playerStrip({ at: "bottom", name: nickname, tray: this.trayBottom });

    const panel = h("div", { class: "panel gp" },
      h("div", { class: "pg pg-more" },
        h("div", { class: "panel-top" }, h("span", { class: "vs-label" }, `${t("bot")} · ${label}`)),
        h("div", { class: "gp-status" }, this.statusEl, this.subEl),
        h("div", { class: "buttons gp-actions" },
          h("button", { class: "btn gp-act", onclick: () => this.askHint() }, ico("hints"), h("span", {}, t("hint"))),
          h("button", { class: "btn gp-act", onclick: () => this.undo() }, ico("undo"), h("span", {}, t("undo"))),
        ),
        h("div", { class: "gp-tools-row" }, boardTools()),
        h("div", { class: "buttons gp-actions gp-actions-2" },
          h("button", { class: "btn gp-act", onclick: () => { this.worker.terminate(); new LocalGame(this.level, this.human); } }, ico("plus"), h("span", {}, t("newGame"))),
        ),
        // Mid-game this asks first (showHome is guarded); a finished game just leaves.
        h("button", { class: "gp-leave", type: "button", onclick: () => { if (this.isLive()) { guardLeaveGame(); return; } this.abandon(); showHome(); } }, ico("home"), h("span", {}, t("home"))),
      ),
      h("div", { class: "pg pg-moves" },
        h("div", { class: "gp-sec-head" },
          h("div", { class: "moves-title" }, t("movesTitle")),
          historyBar(this),
        ),
        this.movesEl,
      ),
    );

    // There is no chat against the engine, so the fourth dock target is the
    // one thing you actually reach for here: ask it what it would play.
    const hintBtn = dockBtn("hint", t("hint"), () => { dock.show(null); this.askHint(); });
    const dock = boardDock(this, panel, hintBtn, null);
    panel.prepend(dock.head);

    app.replaceChildren(
      header(),
      h("div", { class: "game-layout" },
        h("div", { class: "board-col" },
          this.stripTop,
          this.bannerEl,
          this.repWarnEl,
          boardHost,
          this.stripBottom,
          liveStrip(this.mStatusEl, this.mSubEl, this.mMovesEl, () => dock.show("moves")),
        ),
        panel,
        dock.scrim,
        dock.bar,
      ),
    );
    this.board = new BoardView(boardHost, (m) => this.onHuman(m));
    this.board.orientation = this.human;
    repaintBoard = () => this.render();
    navHandler = (c) => {
      const cur = this.viewPly ?? this.moves.length;
      if (c === -1) this.setView(Math.max(0, cur - 1));
      else if (c === 1) this.setView(Math.min(this.moves.length, cur + 1));
      else if (c === "start") this.setView(0);
      else this.setView(null);
    };
    this.render();
  }

  private render() {
    const live = this.viewPly === null;
    const pos = live ? this.game.position : replayTo(this.moves, this.viewPly!);
    const over = this.result() !== GameResult.Ongoing;
    const selectable = live && !over && !this.thinking && pos.sideToMove === this.human ? this.human : null;
    const shownLast = live ? this.moves[this.moves.length - 1] ?? null : this.viewPly! > 0 ? this.moves[this.viewPly! - 1]! : null;
    this.board.setState({ pos, lastMove: shownLast, selectable, viewer: this.human, hint: live ? this.hint : null });
    fillTray(this.trayTop, pos, this.human);              // what the bot has captured
    fillTray(this.trayBottom, pos, opponent(this.human)); // what you have captured

    this.bannerEl.className = "review-banner" + (live ? "" : " on");
    this.bannerEl.replaceChildren();
    if (!live) {
      this.bannerEl.append(
        h("span", {}, `${t("reviewMode")} — ${this.viewPly}/${this.moves.length}`),
        h("button", { class: "btn small", onclick: () => this.setView(null) }, t("toLive")),
      );
    }

    const turn = pos.sideToMove === Color.Red ? t("redToMove") : t("blackToMove");
    this.statusEl.innerHTML = `<span class="dot ${pos.sideToMove === Color.Red ? "red" : "black"}"></span>${over && live ? t("gameOver") : turn}`;
    this.mStatusEl.innerHTML = this.statusEl.innerHTML;
    const res = this.result();
    // Only on your own turn, on the live board: it is about the move you are
    // about to make, and a reviewed position is not one you can play from.
    const warn = live && !over && !this.thinking && pos.sideToMove === this.human ? repWarningFor(this.moves, this.human) : null;
    paintRepWarn(this.repWarnEl, warn, "bot");
    if (live && res !== GameResult.Ongoing) {
      this.say(res === GameResult.RedWins ? t("redWins") : res === GameResult.BlackWins ? t("blackWins") : t("draw"), true);
    } else if (live && this.thinking) {
      this.say(t("botThinking"));
    } else if (inCheck(pos, pos.sideToMove)) {
      this.say(t("check"), true);
    } else if (warn) {
      this.say(repWarnShort(warn), true);
    } else {
      this.say(live && pos.sideToMove === this.human ? t("yourMove") : "");
    }
    const mine = pos.sideToMove === this.human;
    this.stripTop.classList.toggle("turn", live && !over && !mine);
    this.stripBottom.classList.toggle("turn", live && !over && mine);
    renderMoveList(this.movesEl, this);
    renderMoveList(this.mMovesEl, this);
  }

  /** One line about the game, written to both copies of the substatus. */
  private say(text: string, alert = false) {
    for (const el of [this.subEl, this.mSubEl]) {
      el.className = "substatus" + (alert ? " alert" : "");
      el.textContent = text;
    }
  }

  private apply(m: Move) {
    const wasCapture = !!this.game.position.pieceAt(m.to);
    announceMove(this.game.position, m); // named from the position before the move
    this.historyFens.push(toFen(this.game.position));
    this.game.play(m);
    this.moves.push(m);
    this.viewPly = null;
    this.hint = null;
    this.persist();
    const pos = this.game.position;
    if (this.result() === GameResult.Ongoing) {
      if (inCheck(pos, pos.sideToMove)) playCheck();
      else if (wasCapture) playCapture();
      else playMove();
    }
  }

  private maybeEnd(): boolean {
    const res = this.result();
    if (res === GameResult.Ongoing) return false;
    const pos = this.game.position;
    const humanWon = (res === GameResult.RedWins && this.human === Color.Red) || (res === GameResult.BlackWins && this.human === Color.Black);
    const draw = res === GameResult.Draw;
    playEnd(draw ? null : humanWon);
    if (humanWon) victoryBurst();
    try { localStorage.removeItem(RESUME_BOT); } catch { /* ignore */ }
    const why = this.endReason();
    track("game_end", { mode: "bot", level: this.level, result: draw ? "draw" : humanWon ? "win" : "lose", reason: why.slug, moves: this.moves.length });
    const how = why.text;
    showModal(draw ? t("draw") : humanWon ? t("youWin") : t("youLose"), how, [
      [t("newGame"), () => { this.worker.terminate(); new LocalGame(this.level, this.human); }, true],
      // Worth offering only when there is a game to look at: reviewing four
      // moves tells nobody anything.
      ...(this.moves.length >= 6
        ? [[t("reviewGame"), () => { this.worker.terminate(); this.handOverForReview(); }] as Action]
        : []),
      // No "Review board" here: it only closed the modal, which every other
      // action does too, and beside a button that actually reviews the game it
      // read as the one that did nothing -- because it was.
      [t("closeModal"), () => {}],
      [t("home"), () => { this.worker.terminate(); showHome(); }],
    ], draw ? "draw" : humanWon ? "win" : "lose",
      feedbackFoot(draw ? "draw" : humanWon ? "win" : "loss", "bot"));
    return true;
  }

  private onHuman(m: Move) {
    if (this.viewPly !== null || this.thinking || this.game.position.sideToMove !== this.human) return;
    if (!this.firstMoveTracked) { this.firstMoveTracked = true; track("first_move", { mode: "bot" }); }
    this.apply(m);
    this.render();
    if (this.maybeEnd()) return;
    this.triggerBot();
  }

  private triggerBot() {
    this.thinking = true;
    this.render();
    this.worker.postMessage({ type: "go", purpose: "bot", fen: toFen(this.game.position), level: this.level, historyFens: this.historyFens });
  }

  /**
   * Hints are strictly button-driven: one click asks for a hint, another click
   * clears it. It is never shown on its own, and it disappears once you move.
   */
  private askHint() {
    if (this.thinking || this.viewPly !== null) return;
    if (this.hint) { this.hint = null; this.render(); return; } // toggle off
    if (this.game.position.sideToMove !== this.human) return;
    if (this.result() !== GameResult.Ongoing) return;
    track("hint_used", { level: this.level, ply: this.moves.length });
    this.say(t("hintThinking"));
    this.worker.postMessage({ type: "go", purpose: "hint", fen: toFen(this.game.position), level: "hard", historyFens: this.historyFens });
  }

  private onWorker(data: { type?: string; purpose: "bot" | "hint"; move: Move | null; message?: string; stack?: string; fen?: string; level?: string; historyLength?: number }) {
    if (data.type === "failed") {
      // Report it with the stack and the position, which is what makes it
      // reproducible, then keep the game playable.
      reportError(`engine ${data.purpose} failed: ${data.message ?? "unknown"}`,
        [data.stack ?? "", `fen=${data.fen ?? "?"}`, `level=${data.level ?? "?"}`, `history=${data.historyLength ?? 0}`].join("\n"));
      track("engine_failed", { purpose: data.purpose, level: String(data.level ?? "") });
      if (data.purpose === "hint") { this.hint = null; this.say(t("hintFailed")); this.render(); return; }
      this.onEngineFailed();
      return;
    }
    if (data.purpose === "hint") { this.hint = data.move; this.render(); return; }
    this.thinking = false;
    if (data.move) this.apply(data.move);
    this.render();
    this.maybeEnd();
  }

  /**
   * The engine could not produce a move. Play a legal one anyway.
   *
   * A weak move is a far better outcome than a board that never moves again,
   * which is what happened before: the bot stayed "thinking" for the rest of the
   * session and the only way out was to reload. Preferring a capture keeps the
   * fallback from looking completely mindless.
   */
  private onEngineFailed() {
    this.thinking = false;
    if (this.result() !== GameResult.Ongoing) { this.render(); return; }
    const moves = legalMoves(this.game.position);
    if (!moves.length) { this.render(); this.maybeEnd(); return; }
    const capture = moves.find((m) => this.game.position.board[m.to]);
    this.say(t("engineHiccup"));
    this.apply(capture ?? moves[0]!);
    this.render();
    this.maybeEnd();
  }

  /**
   * Hand this game to the replay screen so it can be reviewed.
   *
   * Dressed as a game record because that is what the screen reads; the fields
   * a real record carries and a local game cannot -- ratings, a rated flag --
   * are filled with what is actually true rather than left to look like data.
   */
  private handOverForReview() {
    const res = this.result();
    const botName = `${t("bot")} · ${({ beginner: t("beginner"), easy: t("easy"), medium: t("medium"), hard: t("hard"), master: t("master") })[this.level]}`;
    const meRed = this.human === Color.Red;
    pendingLocalGame = {
      id: LOCAL_REVIEW_ID,
      endedAt: Date.now(),
      redUser: null, blackUser: null,
      redName: meRed ? (nickname || t("player")) : botName,
      blackName: meRed ? botName : (nickname || t("player")),
      redRating: 0, blackRating: 0, ratingDelta: null,
      result: res === GameResult.RedWins ? "red-wins" : res === GameResult.BlackWins ? "black-wins" : "draw",
      reason: isCheckmate(this.game.position) ? "checkmate" : isStalemate(this.game.position) ? "stalemate" : "draw",
      rated: false,
      moves: this.moves.map((m) => ({ from: m.from, to: m.to })),
    };
    navigate(PATH.replay(LOCAL_REVIEW_ID));
  }

  /** A fresh engine. Also how a search in progress is abandoned: see `undo`. */
  private spawnWorker() {
    this.worker = new Worker(`worker.js?v=${__BUILD__}`, { type: "module" });
    this.worker.onmessage = (e) => this.onWorker(e.data);
    // If the worker dies outright it never answers, and the board would wait for
    // a move forever. The engine reports its own failures now, so this is only
    // the last resort -- but the last resort is exactly what was missing.
    this.worker.onerror = (e) => {
      reportError("engine worker died", String((e as ErrorEvent).message ?? e));
      this.onEngineFailed();
    };
  }

  private undo() {
    if (this.moves.length === 0) return;
    // Taking a move back while the computer is still answering it. This used to
    // do nothing at all, with no sign why -- about two hundred repeated presses
    // of 悔棋 on /game/computer in October 2026. A search cannot be interrupted
    // from outside, so the engine is replaced; the reply it was working on is
    // for a position that is about to stop existing anyway.
    if (this.thinking) {
      this.worker.terminate();
      this.spawnWorker();
      this.thinking = false;
      track("undo_during_search", { ply: this.moves.length });
    }
    track("undo_used", { ply: this.moves.length });
    closeModal();
    const drop = this.game.position.sideToMove === this.human ? 2 : 1;
    const replay = this.moves.slice(0, Math.max(0, this.moves.length - drop));
    this.game = new Game(parseFen(START_FEN));
    this.historyFens = [];
    this.moves = [];
    this.viewPly = null;
    this.hint = null;
    for (const m of replay) {
      this.historyFens.push(toFen(this.game.position));
      this.game.play(m);
      this.moves.push(m);
    }
    this.persist();
    this.render();
    // Playing Black and taken all the way back to the start: it is the
    // computer's move again, and nothing else would ask it for one.
    if (this.game.position.sideToMove !== this.human && this.result() === GameResult.Ongoing) this.triggerBot();
  }
}

// ---------------------------------------------------------------- table chat
/**
 * The chat at one table. Both the players and everyone watching speak here, so
 * it belongs to neither screen: the board you are playing on and the board you
 * are watching build the same panel from this.
 *
 * The log lives on the instance rather than in the DOM, so it survives every
 * repaint of the panel — a message must not vanish because a move arrived.
 */
class TableChat {
  private log: { name: string; text: string; mine: boolean; watcher: boolean }[] = [];
  private logEl: HTMLElement | null = null;
  private typingEl: HTMLElement | null = null;
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTypingSent = 0;
  /** Messages that arrived while the log was off screen — the dock's badge. */
  private unread = 0;
  private onUnread: ((n: number) => void) | null = null;

  /** The game to talk in, read at send time: a rematch keeps the same panel. */
  constructor(private gameId: () => string | undefined) {}

  /** Report the unread count, now and whenever it changes. */
  watchUnread(fn: (n: number) => void) {
    this.onUnread = fn;
    fn(this.unread);
  }

  /** The log is on screen again, so nothing in it is unread any more. */
  markSeen() {
    if (!this.unread) return;
    this.unread = 0;
    this.onUnread?.(0);
  }

  onSaid(m: { name: string; text: string; from: string; watcher: boolean }) {
    const mine = m.from === myPlayerId();
    this.log.push({ name: m.name, text: m.text, mine, watcher: m.watcher });
    if (this.log.length > 200) this.log.shift();
    // Someone else spoke: make a sound, and clear any "typing" line from them.
    if (!mine) {
      playNotify();
      // Only a message you cannot see is unread. Beside the board on a desktop
      // the log is always on screen; inside a closed sheet on a phone it is
      // not — and a closed sheet is display:none, which is what this reads.
      if (!this.logEl || this.logEl.offsetParent === null) {
        this.unread += 1;
        this.onUnread?.(this.unread);
      }
    }
    this.clearTyping();
    this.paint();
  }

  /**
   * What was said before this page joined the table, from the server. Replaces
   * the log rather than adding to it, and makes no sound: none of it is new.
   */
  load(lines: { name: string; text: string; from: string; watcher: boolean }[]) {
    const me = myPlayerId();
    this.log = lines.slice(-200).map((l) => ({ name: l.name, text: l.text, mine: l.from === me, watcher: l.watcher }));
    this.paint();
  }

  onTyping(name: string) {
    if (!this.typingEl) return;
    this.typingEl.textContent = t("isTyping").replace("{name}", name);
    this.typingEl.classList.add("on");
    if (this.typingTimer) clearTimeout(this.typingTimer);
    // The ping is a heartbeat, not a state: if it stops arriving, they stopped.
    this.typingTimer = setTimeout(() => this.clearTyping(), 2500);
  }

  private clearTyping() {
    if (this.typingTimer) { clearTimeout(this.typingTimer); this.typingTimer = null; }
    if (this.typingEl) { this.typingEl.textContent = ""; this.typingEl.classList.remove("on"); }
  }

  private paint() {
    if (!this.logEl) return;
    this.logEl.replaceChildren(...this.log.map((c) =>
      h("div", { class: "chat-line" + (c.mine ? " mine" : "") },
        h("span", { class: "chat-who" + (c.watcher ? " watcher" : "") }, c.name),
        h("span", { class: "chat-text" }, c.text),
      ),
    ));
    this.logEl.scrollTop = this.logEl.scrollHeight;
  }

  /** The chat panel: log, typing line, and a composer. */
  panel(): HTMLElement {
    const log = h("div", { class: "chat-log" });
    const typing = h("div", { class: "chat-typing" });
    const input = h("input", {
      class: "chat-input", type: "text", maxlength: "400",
      placeholder: t("chatPlaceholder"), autocomplete: "off",
    }) as HTMLInputElement;

    const send = () => {
      const text = input.value.trim();
      const gid = this.gameId();
      if (!text || !gid) return;
      net?.send({ t: "say", gameId: gid, text });
      input.value = "";
    };

    input.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); send(); return; }
      // Throttle the typing ping: one per second is enough to keep the
      // indicator alive and keeps a fast typist from flooding the socket.
      const now = Date.now();
      const gid = this.gameId();
      if (gid && now - this.lastTypingSent > 1000) {
        this.lastTypingSent = now;
        net?.send({ t: "typing", gameId: gid });
      }
    });

    log.dataset.empty = t("chatEmpty");
    this.logEl = log;
    this.typingEl = typing;
    this.paint();
    return h("div", { class: "chat" },
      log,
      typing,
      h("div", { class: "chat-row" },
        input,
        h("button", { class: "btn sm chat-send", type: "button", title: t("send"), "aria-label": t("send"), onclick: send }, ico("send")),
      ),
    );
  }
}

// ---------------------------------------------------------------- Remote game
class RemoteGame implements HistoryHost {
  constructor(private mode: "online" | "friends", private resumeOnly = false) {}
  private reviewTracked = false;
  private startTracked = false;
  private lowTickAt = 0;
  private drawOfferAt = 0;
  private snapshot: GameSnapshot | null = null;
  private pos: Position | null = null;
  moves: Move[] = [];
  viewPly: number | null = null;
  private prevPieceCount = -1;
  private recvAt = 0;
  private board: BoardView | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private redClockEl?: HTMLElement;
  private blackClockEl?: HTMLElement;
  private statusEl?: HTMLElement;
  private subEl?: HTMLElement;
  private movesEl?: HTMLElement;
  private bannerEl?: HTMLElement;
  private trayTop?: HTMLElement;
  private trayBottom?: HTMLElement;
  /** The same status and moves again, for the strip a phone keeps on screen. */
  private mStatusEl?: HTMLElement;
  private mSubEl?: HTMLElement;
  private mMovesEl?: HTMLElement;
  /** The two player rows, so the side to move can be lit up. */
  private stripTop?: HTMLElement;
  private stripBottom?: HTMLElement;
  /** "Perpetual check: play a different move" -- see paintRepWarn. */
  private repWarnEl?: HTMLElement;
  private endShown = false;
  /**
   * The invite code of the private room this game came from. The server sends
   * it on the snapshot (so it survives a refresh); this copy is what lets the
   * code appear the moment the game opens, on both sides.
   */
  private roomCode: string | null = null;
  private roomCodeGameId: string | null = null;

  /** Remember the code we are joining with, before any snapshot exists. */
  setRoomCode(code: string) { this.roomCode = code.toUpperCase(); }

  /** The code to show for this game, if it is a private one. */
  private codeFor(s: GameSnapshot): string | null {
    if (s.code) return s.code;
    return this.roomCode && this.roomCodeGameId === s.gameId ? this.roomCode : null;
  }

  setView(ply: number | null) {
    this.viewPly = ply === null || ply >= this.moves.length ? null : Math.max(0, ply);
    if (this.viewPly !== null && !this.reviewTracked) { this.reviewTracked = true; track("history_review", { mode: this.mode }); }
    this.render();
  }

  /** The snapshot of a game that is still running, if any. */
  liveSnapshot(): GameSnapshot | null {
    return this.snapshot && this.snapshot.status === "playing" ? this.snapshot : null;
  }

  /** Table chat. Shared with the watch board — see `TableChat`. */
  private chat = new TableChat(() => this.snapshot?.gameId);

  /** How many people are following this game, as the server last reported it. */
  private watchers = 0;
  private watchersEl: HTMLElement | null = null;
  private paintWatchers() {
    if (this.watchersEl) this.watchersEl.textContent = t("watchingCount").replace("{n}", String(this.watchers));
  }

  /**
   * "Anyone with this link can watch" — the count of who already is, and one
   * click to copy it. Shown on every board, because a private-room game is not
   * listed anywhere and this link is the only way to invite someone to watch.
   */
  private watchLinkChip(s: GameSnapshot): HTMLElement {
    const link = `${location.origin}${PATH.watchGame(s.gameId)}`;
    const count = h("span", { class: "code-chip-value" });
    this.watchersEl = count;
    this.paintWatchers();
    return h("div", { class: "code-chip watch-chip" },
      h("span", { class: "code-chip-label" }, t("watchLink")),
      h("span", { class: "watch-count" }, ico("eye"), count),
      copyBtn(t("copyWatchLink"), () => link, () => track("watch_link_copied", {})),
    );
  }

  /** Re-enter a game that is still running (from the banner or the popup). */
  reopen() {
    if (!this.snapshot) return;
    closeModal();
    enter("game_" + this.mode, PATH.game, `${t("liveGame")} · ${this.snapshot.opponentName}`);
    setStuckPath(PATH.game);
    this.renderScreen();
    this.render();
  }

  handle(m: ServerMsg) {
    switch (m.t) {
      case "room": this.roomCode = m.code; this.showWaiting(m.code); break;
      case "start":
      case "state": this.setSnapshot(m.snapshot); break;
      case "opponentLeft":
        this.say(t("opponentDisconnected"), true);
        // They have a grace period to reconnect; if they don't, the server ends
        // the game and showEnd() takes over with a Home button.
        break;
      case "opponentJoined": this.render(); break;
      case "said": if (m.gameId === this.snapshot?.gameId) this.chat.onSaid(m); break;
      case "chatLog": if (m.gameId === this.snapshot?.gameId) this.chat.load(m.lines); break;
      case "typing": this.chat.onTyping(m.name); break;
      case "watchers":
        if (m.gameId === this.snapshot?.gameId) { this.watchers = m.n; this.paintWatchers(); }
        break;
      case "rematchOffered": {
        const gid = this.snapshot?.gameId ?? "";
        showModal(t("rematchTitle"), t("rematchBody"), [
          [t("accept"), () => net?.send({ t: "rematch", gameId: gid }), true],
          [t("decline"), () => {}],
        ], "info");
        break;
      }
      case "drawOffered": {
        const gid = (m as { gameId: string }).gameId;
        showModal(t("drawOfferedTitle"), t("drawOfferedBody"), [
          [t("accept"), () => net?.send({ t: "acceptDraw", gameId: gid }), true],
          [t("decline"), () => net?.send({ t: "declineDraw", gameId: gid }) ],
        ], "info");
        break;
      }
      case "drawDeclined":
        this.say(t("drawDeclined"));
        break;
      case "error":
        if (handledUnverified(m)) break;
        overlay(m.msg); setTimeout(showHome, 1600);
        break;
    }
  }

  private showWaiting(code: string) {
    // The invite is a page of its own: opening it drops the guest straight into
    // this room, signing in first if they need to.
    const link = `${location.origin}${PATH.join(code)}`;
    const linkInput = h("input", { class: "code-input wide", type: "text", value: link, readonly: "readonly" }) as HTMLInputElement;
    overlay(`${t("roomLabel")} ${code}`, h("div", { class: "waiting" },
      h("p", {}, t("shareLink")),
      h("div", { class: "join-row" }, linkInput, h("button", { class: "btn", onclick: () => { linkInput.select(); navigator.clipboard?.writeText(link); } }, t("copy"))),
      h("p", { class: "dim" }, t("waitingOpponent")),
    ));
  }

  private setSnapshot(s: GameSnapshot) {
    const first = !this.snapshot;
    const newMove = s.lastMove && (!this.snapshot?.lastMove || this.snapshot.lastMove.from !== s.lastMove.from || this.snapshot.lastMove.to !== s.lastMove.to);
    if (first) {
      this.endShown = false; this.viewPly = null;
      this.roomCodeGameId = s.gameId; // the code we joined with belongs to THIS game
      // Resuming after a refresh/back: drop straight onto the board — no popup,
      // no home screen. The player stays in their private room until they exit.
      const resuming = this.resumeOnly && s.status === "playing";
      this.resumeOnly = false;
      if (resuming) { clearResumeFallback(); dismissLoader(); track("game_resume", { mode: this.mode }); }
      if (!this.startTracked) {
        this.startTracked = true;
        if (this.mode === "online" && !resuming) track("matchmaking_matched");
        if (!resuming) track("game_start", { mode: this.mode, side: s.yourColor });
      }
      // The board is a page: being paired, resuming after a refresh or
      // re-entering from the banner all land on the same address.
      enter("game_" + this.mode, PATH.game, `${t("liveGame")} · ${s.opponentName}`);
      setStuckPath(PATH.game);
    }
    // The server sends the authoritative move list, so review works after reconnects.
    this.moves = (s.moves ?? []).map((m) => ({ from: m.from, to: m.to }));
    this.snapshot = s;
    const newPos = parseFen(s.fen);

    if (newMove && s.status === "playing") {
      const count = countPieces(newPos);
      const captured = this.prevPieceCount >= 0 && count < this.prevPieceCount;
      if (inCheck(newPos, newPos.sideToMove)) playCheck();
      else if (captured) playCapture();
      else playMove();
      // Announce from the position as it was — the only one that can name the
      // piece that moved. It is rebuilt from the server's move list rather than
      // taken from `this.pos`, which already holds your own move played locally.
      if (s.lastMove && this.moves.length) {
        announceMove(replayTo(this.moves, this.moves.length - 1), { from: s.lastMove.from, to: s.lastMove.to });
      }
    }
    this.prevPieceCount = countPieces(newPos);
    this.pos = newPos;
    this.recvAt = Date.now();
    if (newMove) this.viewPly = null; // a new move always snaps back to live
    if (first || !this.board) this.renderScreen();
    this.render();
    liveBannerRepaint?.();
    if (s.status !== "playing") {
      if (this.clockTimer) { clearInterval(this.clockTimer); this.clockTimer = null; }
      this.showEnd(s);
    } else this.endShown = false;
  }

  private showEnd(s: GameSnapshot) {
    if (this.endShown) return;
    this.endShown = true;
    const draw = s.status === "draw";
    const won = (s.status === "red-wins" && s.yourColor === "red") || (s.status === "black-wins" && s.yourColor === "black");
    playEnd(draw ? null : won);
    if (won) victoryBurst();
    track("game_end", { mode: this.mode, result: draw ? "draw" : won ? "win" : "lose", reason: s.endReason ?? "", moves: this.moves.length });
    const reasons: Record<string, string> = {
      checkmate: t("reasonCheckmate"), stalemate: t("reasonStalemate"), timeout: t("reasonTimeout"),
      resign: t("reasonResign"), abandonment: t("reasonAbandon"), agreement: t("reasonAgreement"),
      "threefold-repetition": t("reasonRepetition"), draw: t("reasonDraw"),
      "perpetual-check": t("reasonPerpetualCheck"), "perpetual-chase": t("reasonPerpetualChase"),
    };
    const delta = s.ratingDelta ? (s.yourColor === "red" ? s.ratingDelta.red : s.ratingDelta.black) : null;
    const deltaTxt = delta === null ? "" : `  ·  ${t("rating")} ${delta >= 0 ? "+" : ""}${delta}`;
    if (delta !== null) void syncRating(); // the number in the header just changed

    const goHome = () => { net?.send({ t: "leave", gameId: s.gameId }); showHome(); };
    // If the opponent has left there is nobody to rematch, and sitting on a dead
    // board is a trap: make Home the confirming action and say why.
    const opponentGone = s.opponentConnected === false || s.endReason === "abandonment";
    const body = (reasons[s.endReason ?? ""] ?? s.endReason ?? "") + deltaTxt;
    // Leave the game before walking off to the review, exactly as Home does:
    // otherwise the server still has this player sitting at a finished board.
    const goReview = () => { net?.send({ t: "leave", gameId: s.gameId }); navigate(PATH.replay(s.gameId)); };
    const actions: Action[] = opponentGone
      ? [[t("home"), goHome, true], [t("reviewGame"), goReview]]
      : [[t("rematch"), () => net?.send({ t: "rematch", gameId: s.gameId }), true],
         [t("reviewGame"), goReview],
         [t("home"), goHome]];
    showModal(
      draw ? t("draw") : won ? t("youWin") : t("youLose"),
      // The abandonment reason already says it; don't say it twice.
      opponentGone && s.endReason !== "abandonment" ? `${body}${body ? " · " : ""}${t("opponentLeftGame")}` : body,
      actions,
      draw ? "draw" : won ? "win" : "lose",
      feedbackFoot(draw ? "draw" : won ? "win" : "loss", "online"),
    );
  }

  private renderScreen() {
    const s = this.snapshot!;
    const boardHost = h("div", { class: "board-wrap" });
    this.statusEl = h("div", { class: "status" });
    this.subEl = h("div", { class: "substatus" });
    this.redClockEl = h("div", { class: "clock red" });
    this.blackClockEl = h("div", { class: "clock black" });
    this.movesEl = h("div", { class: "moves" });
    this.bannerEl = h("div", { class: "review-banner" });
    this.repWarnEl = repWarnBox();
    this.trayTop = h("div", { class: "tray" });
    this.trayBottom = h("div", { class: "tray" });

    // The status and the move list appear twice: once in the panel, and once in
    // the strip a phone keeps under the board while the panel is a closed
    // sheet. Both are painted from the same values on every render, so neither
    // can drift from the other.
    this.mStatusEl = h("div", { class: "status" });
    this.mSubEl = h("div", { class: "substatus" });
    this.mMovesEl = h("div", { class: "moves" });

    const topClock = s.yourColor === "red" ? this.blackClockEl : this.redClockEl;
    const botClock = s.yourColor === "red" ? this.redClockEl : this.blackClockEl;
    this.stripTop = playerStrip({
      at: "top", name: s.opponentName,
      badge: String(s.yourColor === "red" ? s.ratings.black : s.ratings.red),
      tray: this.trayTop, clock: topClock,
    });
    this.stripBottom = playerStrip({
      // The server's name for this seat, not `nickname`: on a cold reload the
      // board can paint before the profile arrives, and the strip said "Guest".
      at: "bottom", name: s.names?.[s.yourColor] || nickname,
      badge: String(s.yourColor === "red" ? s.ratings.red : s.ratings.black),
      tray: this.trayBottom, clock: botClock,
    });

    const playing = this.snapshot?.status === "playing";
    const panel = h("div", { class: "panel gp" },
      h("div", { class: "pg pg-more" },
        h("div", { class: "panel-top" },
          (() => { const pill = h("span", { class: "conn-pill ok" }, t("connected")); connPills.clear(); connPills.add(pill); paintConnPills(); return pill; })(),
          h("span", { class: "tc-pill" }, `${tcLabel(s.tc)} · ${s.rateable === false ? t("casual") : t("rated")}`),
        ),
        // Whose move, first and largest: the one thing the panel is looked at for.
        h("div", { class: "gp-status" }, this.statusEl, this.subEl),
        h("div", { class: "buttons gp-actions" },
          h("button", { class: "btn gp-act", onclick: () => {
            if (this.snapshot?.status === "playing") {
              // One offer, then a pause: the server ignores repeats inside 20s,
              // so pressing again would otherwise look like a dead button.
              if (Date.now() - this.drawOfferAt < 20_000) { this.say(t("drawOfferWait")); return; }
              this.drawOfferAt = Date.now();
              net?.send({ t: "offerDraw", gameId: s.gameId }); this.say(t("drawOfferSent"));
            }
            else { track("rematch_click", { mode: this.mode }); net?.send({ t: "rematch", gameId: s.gameId }); }
          } }, ico(playing ? "draw" : "rematch"), h("span", {}, playing ? t("offerDraw") : t("rematch"))),
          h("button", { class: "btn gp-act gp-resign", onclick: () => {
            track("resign_click", { mode: this.mode });
            const quit = () => net?.send({ t: "resign", gameId: s.gameId });
            // A finished game has nothing to confirm -- the button is only still
            // there so a dead board can be left.
            if (this.snapshot?.status === "playing") confirmResign(quit); else quit();
          } }, ico("flag"), h("span", {}, t("resign"))),
        ),
        h("div", { class: "gp-tools-row" }, boardTools()),
        // Who is across the table, and the two ways to bring someone else to it.
        h("div", { class: "gp-share" },
          // Play someone you like? Ask to be friends without leaving the board.
          ...(s.opponentUser ? [addFriendRow(s.opponentUser, s.opponentName)] : []),
          // A private game keeps its invite code in view, so the host can still
          // read it out (or re-copy the link) after the board has opened.
          ...((code) => (code ? [roomCodeChip(code)] : []))(this.codeFor(s)),
          // The link that lets someone follow this game live. A public game is
          // already on the Watch page; for a private one this link is the only
          // way in, which is exactly why it is offered here and nowhere else.
          this.watchLinkChip(s),
        ),
        h("button", { class: "gp-leave", type: "button", onclick: () => this.leaveToHome() }, ico("home"), h("span", {}, t("home"))),
      ),
      h("div", { class: "pg pg-moves" },
        h("div", { class: "gp-sec-head" },
          h("div", { class: "moves-title" }, t("movesTitle")),
          historyBar(this),
        ),
        this.movesEl,
      ),
      h("div", { class: "pg pg-chat" },
        h("div", { class: "moves-title" }, t("chatTitle")),
        this.chat.panel(),
      ),
    );

    // The fourth dock target: the chat, with a count of what arrived while it
    // was shut — the one thing on this screen that can ask for you mid-game.
    const chatBtn = dockBtn("chat", t("chatTitle"), () => dock.show(sheetTab === "chat" ? null : "chat"));
    const badge = h("i", { class: "dock-badge hide" }, "0");
    chatBtn.append(badge);
    this.chat.watchUnread((n) => {
      badge.textContent = String(Math.min(n, 99));
      badge.classList.toggle("hide", n === 0);
    });

    const dock = boardDock(this, panel, chatBtn, "chat", (tab) => { if (tab === "chat") this.chat.markSeen(); });
    panel.prepend(dock.head);

    app.replaceChildren(
      header(),
      h("div", { class: "game-layout" },
        h("div", { class: "board-col" },
          this.stripTop,
          this.bannerEl,
          this.repWarnEl,
          boardHost,
          this.stripBottom,
          liveStrip(this.mStatusEl, this.mSubEl, this.mMovesEl, () => dock.show("moves")),
        ),
        panel,
        dock.scrim,
        dock.bar,
      ),
    );
    this.paintWatchers();
    this.board = new BoardView(boardHost, (m) => this.onBoardMove(m));
    this.board.orientation = s.yourColor === "red" ? Color.Red : Color.Black;
    repaintBoard = () => this.render();
    navHandler = (c) => {
      const cur = this.viewPly ?? this.moves.length;
      if (c === -1) this.setView(Math.max(0, cur - 1));
      else if (c === 1) this.setView(Math.min(this.moves.length, cur + 1));
      else if (c === "start") this.setView(0);
      else this.setView(null);
    };
    if (!this.clockTimer) this.clockTimer = setInterval(() => this.tickClocks(), 250);
  }

  /**
   * A line about the game that is not the turn — a draw offer, a disconnect.
   * It goes to both copies of the substatus, so it reads the same whether the
   * panel is beside the board or folded into a phone's sheet.
   */
  private say(text: string, alert = false) {
    for (const el of [this.subEl, this.mSubEl]) {
      if (!el) continue;
      el.className = "substatus" + (alert ? " alert" : "");
      el.textContent = text;
    }
  }

  private render() {
    const s = this.snapshot;
    if (!s || !this.pos || !this.board) return;
    const live = this.viewPly === null;
    const yours = s.yourColor === "red" ? Color.Red : Color.Black;
    const myTurn = live && s.status === "playing" && s.turn === s.yourColor;
    const pos = live ? this.pos : replayTo(this.moves, this.viewPly!);
    const shownLast = live
      ? (s.lastMove ? { from: s.lastMove.from, to: s.lastMove.to } : null)
      : this.viewPly! > 0 ? this.moves[this.viewPly! - 1]! : null;
    this.board.setState({ pos, lastMove: shownLast, selectable: myTurn ? yours : null, viewer: yours });
    if (this.trayTop) fillTray(this.trayTop, pos, yours);              // opponent's captures
    if (this.trayBottom) fillTray(this.trayBottom, pos, opponent(yours)); // your captures

    this.bannerEl!.className = "review-banner" + (live ? "" : " on");
    this.bannerEl!.replaceChildren();
    if (!live) {
      this.bannerEl!.append(
        h("span", {}, `${t("reviewMode")} — ${this.viewPly}/${this.moves.length}`),
        h("button", { class: "btn small", onclick: () => this.setView(null) }, t("toLive")),
      );
    }

    const turnTxt = s.turn === "red" ? t("redToMove") : t("blackToMove");
    const statusHtml = `<span class="dot ${s.turn}"></span>${s.status === "playing" ? turnTxt : t("gameOver")}`;
    // Replayed from the server's move list, which is the history the server
    // rules on. Only on your own turn: it is about the move you are to make.
    const warn = myTurn ? repWarningFor(this.moves, yours) : null;
    paintRepWarn(this.repWarnEl, warn, this.mode);
    let subText: string, alert: boolean;
    if (s.status !== "playing") {
      alert = true;
      subText = s.status === "red-wins" ? t("redWins") : s.status === "black-wins" ? t("blackWins") : t("draw");
    } else if (inCheck(pos, pos.sideToMove)) {
      alert = true;
      subText = t("check");
    } else if (warn) {
      alert = true;
      subText = repWarnShort(warn);
    } else {
      alert = false;
      subText = myTurn ? t("yourMove") : t("opponentMove");
    }
    for (const el of [this.statusEl, this.mStatusEl]) if (el) el.innerHTML = statusHtml;
    for (const el of [this.subEl, this.mSubEl]) {
      if (!el) continue;
      el.className = "substatus" + (alert ? " alert" : "");
      el.textContent = subText;
    }
    // Whose turn it is, said once more where the eye already is: the player row
    // of the side to move is lit, so a glance at the clock answers it.
    this.stripTop?.classList.toggle("turn", s.status === "playing" && s.turn !== s.yourColor);
    this.stripBottom?.classList.toggle("turn", s.status === "playing" && s.turn === s.yourColor);
    this.tickClocks();
    renderMoveList(this.movesEl!, this);
    if (this.mMovesEl) renderMoveList(this.mMovesEl, this);
  }

  private tickClocks() {
    const s = this.snapshot;
    if (!s || !this.redClockEl || !this.blackClockEl) return;
    let red = s.clocks.red, black = s.clocks.black;
    if (s.status === "playing") {
      const elapsed = Date.now() - this.recvAt;
      if (s.turn === "red") red = Math.max(0, red - elapsed);
      else black = Math.max(0, black - elapsed);
    }
    this.redClockEl.textContent = clockStr(red);
    this.blackClockEl.textContent = clockStr(black);
    this.redClockEl.classList.toggle("active", s.status === "playing" && s.turn === "red");
    this.blackClockEl.classList.toggle("active", s.status === "playing" && s.turn === "black");
    this.redClockEl.classList.toggle("low", s.status === "playing" && red < 20000);
    this.blackClockEl.classList.toggle("low", s.status === "playing" && black < 20000);
    // Urgent tick on YOUR clock in the final 10 seconds.
    const mine = s.yourColor === "red" ? red : black;
    if (s.status === "playing" && s.turn === s.yourColor && mine < 10000) {
      const sec = Math.ceil(mine / 1000);
      if (sec !== this.lowTickAt) { this.lowTickAt = sec; playTick(); }
    } else this.lowTickAt = 0;
  }

  /** Going Home must never silently forfeit a live game. */
  private leaveToHome() {
    const s = this.snapshot;
    if (s && s.status === "playing") {
      showModal(t("leaveGameTitle"), t("leaveGameBody"), [
        [t("keepPlaying"), () => this.reopen(), true],
        [t("leaveForNow"), () => { showHome(); liveBannerRepaint?.(); }],
        [t("resign"), () => { net?.send({ t: "resign", gameId: s.gameId }); showHome(); }],
      ], "info");
      return;
    }
    net?.send({ t: "leave", gameId: s?.gameId ?? "" });
    showHome();
  }

  private onBoardMove(m: Move) {
    const s = this.snapshot;
    if (!s || this.viewPly !== null || s.status !== "playing" || s.turn !== s.yourColor || !this.pos) return;
    if (this.moves.length <= 1) track("first_move", { mode: this.mode });
    const wasCapture = !!this.pos.pieceAt(m.to);
    this.pos.makeMove(m);
    this.prevPieceCount = countPieces(this.pos);
    if (inCheck(this.pos, this.pos.sideToMove)) playCheck();
    else if (wasCapture) playCapture();
    else playMove();
    this.board!.setState({ pos: this.pos, lastMove: m, selectable: null, viewer: s.yourColor === "red" ? Color.Red : Color.Black });
    net?.send({ t: "move", gameId: s.gameId, from: m.from, to: m.to });
  }
}

// ---------------------------------------------------------------- Watch
/**
 * Spectating.
 *
 * Two pages. `/watch` lists the games in progress that anyone may follow —
 * everything from the public pools, and deliberately nothing that started from a
 * private room code. `/watch/:id` is the board itself, read-only, with the same
 * clocks, move list and table chat the players have.
 *
 * Both are behind the account gate, like everything else here that names a
 * person, and a link to a game that has finished (or was never public) says so
 * rather than pretending.
 */

/** Set while the Watch list is on screen, so a pushed update can repaint it. */
let liveGamesTarget: { list: HTMLElement; count: HTMLElement } | null = null;
/** The last list received, so re-opening the page shows something immediately. */
let liveGamesCache: LiveGame[] = [];

function showWatch() {
  if (gameLive()) { guardLeaveGame(showWatch); return; }
  closeModal();
  navHandler = null;
  enter("watch", PATH.watch, t("watchTitle"));
  ensureNet();
  net!.send({ t: "liveGames", on: true });

  const list = h("div", { class: "seek-list watch-list" }, ...skeletonRows(3));
  const count = h("span", { class: "progress" }, "");
  liveGamesTarget = { list, count };

  app.replaceChildren(
    header("play"),
    h("div", { class: "hub watch-hub" },
      h("div", { class: "lessons-head" }, h("h2", {}, t("watchTitle")), count),
      h("div", { class: "panel-card hub-card" },
        h("div", { class: "hub-card-head" },
          h("div", { class: "moves-title" }, t("gamesInPlayTitle")),
          h("a", { class: "progress link-btn", href: PATH.play }, t("navPlay")),
        ),
        list,
        h("div", { class: "hub-note" }, t("watchNote")),
        h("div", { class: "hub-note bots-note" }, t("botsNotice")),
      ),
    ),
  );
  renderLiveGames(liveGamesCache.length ? liveGamesCache : null);
}

/** Paint the list of games in progress. `null` means "nothing received yet". */
function renderLiveGames(games: LiveGame[] | null) {
  if (games) liveGamesCache = games;
  const target = liveGamesTarget;
  if (!target || !games) return;
  target.count.textContent = `${games.length} ${t("gamesInPlay")}`;
  if (!games.length) {
    target.list.replaceChildren(h("div", { class: "empty-state" },
      h("div", { class: "empty-glyph" }, "觀"),
      h("div", { class: "empty-title" }, t("noLiveGames")),
      h("div", { class: "empty-sub" }, t("noLiveGamesSub")),
      h("a", { class: "btn primary", href: PATH.play }, t("navPlay")),
    ));
    return;
  }
  target.list.replaceChildren(...games.map((g) =>
    h("a", { class: "watch-row", href: PATH.watchGame(g.id) },
      h("span", { class: `seek-color ${g.turn}` }, g.turn === "red" ? "帥" : "將"),
      h("span", { class: "watch-players" },
        h("span", { class: "watch-side red" }, `${g.red.name} ${g.red.rating}`),
        h("span", { class: "watch-vs" }, t("vs")),
        h("span", { class: "watch-side black" }, `${g.black.name} ${g.black.rating}`),
      ),
      h("span", { class: "seek-tc" }, tcLabel(g.tc)),
      h("span", { class: "seek-mode" }, g.rated ? t("rated") : t("casual")),
      h("span", { class: "gh-reason" }, `${t("moveNo")} ${Math.floor(g.ply / 2) + 1}`),
      h("span", { class: "watch-eyes" }, `👁 ${g.watchers}`),
    ),
  ));
}

/** The spectator board. One is alive at a time; `screen()` closes it. */
let watchView: WatchView | null = null;

function showWatchGame(id: string) {
  if (gameLive()) { guardLeaveGame(() => showWatchGame(id)); return; }
  closeModal();
  watchView?.close();
  watchView = new WatchView(id);
}

class WatchView implements HistoryHost {
  private snapshot: GameSnapshot | null = null;
  private pos: Position | null = null;
  moves: Move[] = [];
  viewPly: number | null = null;
  private board: BoardView | null = null;
  private recvAt = 0;
  private prevPly = -1;
  private watchers = 0;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private redClockEl?: HTMLElement;
  private blackClockEl?: HTMLElement;
  private statusEl?: HTMLElement;
  private subEl?: HTMLElement;
  private movesEl?: HTMLElement;
  private bannerEl?: HTMLElement;
  private watchersEl?: HTMLElement;
  private trayTop?: HTMLElement;
  private trayBottom?: HTMLElement;
  private chat = new TableChat(() => this.gameId);
  private closed = false;

  constructor(private gameId: string) {
    enter("watch_game", PATH.watchGame(gameId), t("watchGameTitle"));
    this.renderLoading();
    ensureNet();
    net!.send({ t: "watch", gameId });
    track("watch_open", {});
  }

  /** Stop following: the server must not keep pushing at a screen that is gone. */
  close() {
    if (this.closed) return;
    this.closed = true;
    if (this.clockTimer) { clearInterval(this.clockTimer); this.clockTimer = null; }
    net?.send({ t: "unwatch", gameId: this.gameId });
  }

  handle(m: ServerMsg) {
    if (this.closed) return;
    switch (m.t) {
      case "watching":
        if (m.snapshot.gameId !== this.gameId) return;
        this.watchers = m.watchers;
        this.setSnapshot(m.snapshot);
        break;
      case "watchers":
        if (m.gameId !== this.gameId) return;
        this.watchers = m.n;
        this.paintWatchers();
        break;
      case "said":
        if (m.gameId !== this.gameId) return;
        this.chat.onSaid(m);
        break;
      case "chatLog":
        if (m.gameId !== this.gameId) return;
        this.chat.load(m.lines);
        break;
      case "typing":
        if (m.gameId !== this.gameId) return;
        this.chat.onTyping(m.name);
        break;
      case "error":
        if (handledUnverified(m)) break;
        this.renderGone(m.msg);
        break;
    }
  }

  setView(ply: number | null) {
    this.viewPly = ply === null || ply >= this.moves.length ? null : Math.max(0, ply);
    this.render();
  }

  private setSnapshot(s: GameSnapshot) {
    const first = !this.snapshot;
    this.moves = (s.moves ?? []).map((m) => ({ from: m.from, to: m.to }));
    const newMove = this.prevPly >= 0 && this.moves.length > this.prevPly;
    this.snapshot = s;
    this.pos = parseFen(s.fen);
    this.recvAt = Date.now();
    // A watcher hears the game, but is never read the moves aloud — that is a
    // player's setting for their own board, not a commentary track.
    if (newMove && s.status === "playing") {
      if (inCheck(this.pos, this.pos.sideToMove)) playCheck(); else playMove();
      this.viewPly = null;
    }
    this.prevPly = this.moves.length;
    if (first || !this.board) this.renderScreen();
    this.render();
  }

  private renderLoading() {
    app.replaceChildren(header("play"),
      h("div", { class: "setup" },
        h("h2", {}, t("watchLoading")),
        h("div", { class: "setup-actions" }, h("a", { class: "btn ghost", href: PATH.watch }, t("back"))),
      ),
    );
  }

  /** A game that has finished, or a link to one that never was public. */
  private renderGone(msg: string) {
    this.close();
    navHandler = null;
    app.replaceChildren(header("play"),
      h("div", { class: "lessons" },
        h("div", { class: "panel-card profile-empty" },
          h("div", { class: "lesson-title" }, t("watchGoneTitle")),
          h("div", { class: "lesson-task" }, t("watchGoneBody")),
          h("div", { class: "dim" }, msg),
          h("div", { class: "buttons" },
            h("a", { class: "btn primary", href: PATH.watch }, t("watchTitle")),
            h("a", { class: "btn", href: PATH.replay(this.gameId) }, t("replayTitle")),
            h("a", { class: "btn ghost", href: PATH.home }, t("home")),
          ),
        ),
      ),
    );
  }

  private paintWatchers() {
    if (this.watchersEl) this.watchersEl.textContent = `👁 ${this.watchers} ${t("watching")}`;
  }

  private names(): { red: string; black: string } {
    const s = this.snapshot!;
    // `names` is what a watcher is actually sent; the fall-back covers a server
    // that has not been redeployed yet, where only the "opponent" has a name.
    if (s.names) return s.names;
    return s.yourColor === "red"
      ? { red: nickname, black: s.opponentName }
      : { red: s.opponentName, black: nickname };
  }

  private renderScreen() {
    const s = this.snapshot!;
    const names = this.names();
    const boardHost = h("div", { class: "board-wrap" });
    this.statusEl = h("div", { class: "status" });
    this.subEl = h("div", { class: "substatus" });
    this.redClockEl = h("div", { class: "clock red" });
    this.blackClockEl = h("div", { class: "clock black" });
    this.movesEl = h("div", { class: "moves" });
    this.bannerEl = h("div", { class: "review-banner" });
    this.watchersEl = h("span", { class: "watch-count" });
    this.trayTop = h("div", { class: "tray" });
    this.trayBottom = h("div", { class: "tray" });

    // A published game is always shown from Red's side — the convention, and the
    // one orientation everybody watching agrees on.
    const nameLink = (side: "red" | "black") => {
      const user = s.users?.[side];
      const label = `${names[side]} (${s.ratings[side]})`;
      return user
        ? h("a", { class: "opp-name link", href: PATH.player(user) }, label)
        : h("span", { class: "opp-name" }, label);
    };

    app.replaceChildren(
      header("play"),
      h("div", { class: "game-layout" },
        h("div", { class: "board-col" },
          h("div", { class: "clock-bar" }, nameLink("black"), this.blackClockEl),
          this.trayTop,
          this.bannerEl,
          boardHost,
          this.trayBottom,
          h("div", { class: "clock-bar" }, nameLink("red"), this.redClockEl),
        ),
        h("div", { class: "panel" },
          h("div", { class: "panel-top" },
            (() => { const pill = h("span", { class: "conn-pill ok" }, t("connected")); connPills.clear(); connPills.add(pill); paintConnPills(); return pill; })(),
            h("span", { class: "tc-pill" }, `${tcLabel(s.tc)} · ${s.rateable === false ? t("casual") : t("rated")}`),
          ),
          h("div", { class: "watch-banner" },
            h("span", { class: "watch-eye" }, "👁"),
            h("span", {}, t("watchingReadOnly")),
            this.watchersEl,
          ),
          boardTools(),
          this.shareChip(),
          this.statusEl, this.subEl,
          h("div", { class: "buttons" },
            h("a", { class: "btn", href: PATH.watch }, t("watchTitle")),
            h("a", { class: "btn ghost", href: PATH.home }, t("home")),
          ),
          h("div", { class: "moves-title" }, t("movesTitle")),
          historyBar(this),
          this.movesEl,
          h("div", { class: "moves-title" }, t("chatTitle")),
          this.chat.panel(),
        ),
      ),
    );
    this.paintWatchers();
    // Read-only is enforced by the server, which accepts no move from a watcher.
    // Passing no selectable colour is what makes the board refuse to try.
    this.board = new BoardView(boardHost, () => { /* watching */ });
    this.board.orientation = Color.Red;
    repaintBoard = () => this.render();
    navHandler = (c) => {
      const cur = this.viewPly ?? this.moves.length;
      if (c === -1) this.setView(Math.max(0, cur - 1));
      else if (c === 1) this.setView(Math.min(this.moves.length, cur + 1));
      else if (c === "start") this.setView(0);
      else this.setView(null);
    };
    if (!this.clockTimer) this.clockTimer = setInterval(() => this.tickClocks(), 250);
  }

  private shareChip(): HTMLElement {
    const link = `${location.origin}${PATH.watchGame(this.gameId)}`;
    const copy = h("button", { class: "tool-btn", title: t("copyWatchLink"), "aria-label": t("copyWatchLink") }, "⧉");
    copy.addEventListener("click", async () => {
      try { await navigator.clipboard?.writeText(link); copy.textContent = "✓"; } catch { copy.textContent = "✕"; }
      setTimeout(() => (copy.textContent = "⧉"), 1400);
    });
    return h("div", { class: "code-chip watch-chip" },
      h("span", { class: "code-chip-label" }, t("watchLink")),
      h("span", { class: "code-chip-value" }, t("shareThisGame")),
      copy,
    );
  }

  private render() {
    const s = this.snapshot;
    if (!s || !this.pos || !this.board) return;
    const live = this.viewPly === null;
    const pos = live ? this.pos : replayTo(this.moves, this.viewPly!);
    const shownLast = live
      ? (s.lastMove ? { from: s.lastMove.from, to: s.lastMove.to } : null)
      : this.viewPly! > 0 ? this.moves[this.viewPly! - 1]! : null;
    this.board.setState({ pos, lastMove: shownLast, selectable: null, viewer: Color.Red, hideDanger: true });
    if (this.trayTop) fillTray(this.trayTop, pos, Color.Red);
    if (this.trayBottom) fillTray(this.trayBottom, pos, Color.Black);

    this.bannerEl!.className = "review-banner" + (live ? "" : " on");
    this.bannerEl!.replaceChildren();
    if (!live) {
      this.bannerEl!.append(
        h("span", {}, `${t("reviewMode")} — ${this.viewPly}/${this.moves.length}`),
        h("button", { class: "btn small", onclick: () => this.setView(null) }, t("toLive")),
      );
    }

    const turnTxt = s.turn === "red" ? t("redToMove") : t("blackToMove");
    this.statusEl!.innerHTML = `<span class="dot ${s.turn}"></span>${s.status === "playing" ? turnTxt : t("gameOver")}`;
    if (s.status !== "playing") {
      this.subEl!.className = "substatus alert";
      this.subEl!.textContent = s.status === "red-wins" ? t("redWins") : s.status === "black-wins" ? t("blackWins") : t("draw");
    } else if (inCheck(pos, pos.sideToMove)) {
      this.subEl!.className = "substatus alert";
      this.subEl!.textContent = t("check");
    } else {
      this.subEl!.className = "substatus";
      this.subEl!.textContent = "";
    }
    this.tickClocks();
    renderMoveList(this.movesEl!, this);
  }

  private tickClocks() {
    const s = this.snapshot;
    if (!s || !this.redClockEl || !this.blackClockEl) return;
    let red = s.clocks.red, black = s.clocks.black;
    if (s.status === "playing") {
      const elapsed = Date.now() - this.recvAt;
      if (s.turn === "red") red = Math.max(0, red - elapsed);
      else black = Math.max(0, black - elapsed);
    }
    this.redClockEl.textContent = clockStr(red);
    this.blackClockEl.textContent = clockStr(black);
    this.redClockEl.classList.toggle("active", s.status === "playing" && s.turn === "red");
    this.blackClockEl.classList.toggle("active", s.status === "playing" && s.turn === "black");
    this.redClockEl.classList.toggle("low", s.status === "playing" && red < 20000);
    this.blackClockEl.classList.toggle("low", s.status === "playing" && black < 20000);
  }
}

// ---------------------------------------------------------------- Learn hub
/**
 * Everything study-related in one place: the interactive lessons, tactics
 * puzzles, the analysis board, and a written rules reference. "Learn" used to
 * drop you straight into the lesson list, which hid the rest.
 */
function showLearn() {
  if (gameLive()) { guardLeaveGame(showLearn); return; }
  closeModal();
  navHandler = null;
  enter("learn", PATH.learn, t("learnTitle"));
  const done = doneSet();
  const solved = solvedSet();
  const next = LESSONS.find((l) => !done.has(l.id));

  const card = (title: string, sub: string, glyph: string, path: string) =>
    h("a", { class: "mode-card", href: path, onclick: () => unlockAudio() },
      h("div", { class: "mode-glyph" }, glyph),
      h("div", { class: "mode-text" }, h("div", { class: "mode-title" }, title), h("div", { class: "mode-sub" }, sub)),
    );

  /**
   * Same card, but pointing at the reference site. It is a different origin, so
   * the router must not intercept the click — hence the absolute URL, which
   * `router.start` deliberately leaves to the browser.
   */
  const siteCard = (title: string, sub: string, glyph: string, path: string) =>
    h("a", { class: "mode-card", href: siteUrl(path, "learn-ref"), onclick: () => track("to_site", { page: path }) },
      h("div", { class: "mode-glyph" }, glyph),
      h("div", { class: "mode-text" }, h("div", { class: "mode-title" }, title), h("div", { class: "mode-sub" }, sub)),
    );

  /** A progress ring: the number of finished items is the point, not the bar. */
  const ring = (v: number, total: number, label: string) => {
    const pct = total ? Math.round((v / total) * 100) : 0;
    return h("div", { class: "ring-stat" },
      h("div", { class: "ring", style: `--pct:${pct}` },
        h("span", { class: "ring-num" }, String(v)),
        h("span", { class: "ring-of" }, `/${total}`),
      ),
      h("span", { class: "ring-label" }, label),
    );
  };

  const teaser = (l: Lesson) => summarise(l, 120);

  const doneCount = (items: Lesson[]) => items.filter((l) => done.has(l.id)).length;
  const GROUP_GLYPH: Record<string, string> = {
    "Piece Basics": "兵", "Core Rules": "法", "Tactics": "術", "Endgame": "殘", "Openings": "局",
  };

  app.replaceChildren(
    header("learn"),
    h("div", { class: "hub learn-page" },
      // ---- masthead: what this section is, and where the visitor stands ----
      h("section", { class: "learn-hero" },
        h("div", { class: "learn-hero-copy" },
          h("span", { class: "kicker" }, t("learnKicker")),
          h("h1", { class: "learn-head" }, t("learnHeadline")),
          h("p", { class: "learn-blurb" }, t("learnBlurb")),
          h("div", { class: "learn-cta" },
            h("a", { class: "btn primary", href: next ? PATH.lesson(next.id) : PATH.lessons, onclick: () => unlockAudio() },
              next ? (done.size ? t("continueLearning") : t("startLesson")) : t("allDoneReview")),
            h("a", { class: "btn ghost", href: PATH.puzzles, onclick: () => unlockAudio() }, t("trainNow")),
          ),
        ),
        h("div", { class: "learn-hero-stats" },
          h("div", { class: "stat-card" },
            h("div", { class: "stat-title" }, t("yourProgress")),
            h("div", { class: "ring-row" },
              ring(done.size, LESSONS.length, t("lessonsLabel")),
              ring(solved.size, PUZZLES.length, t("puzzlesLabel")),
            ),
          ),
        ),
      ),

      // ---- the one thing to do next ----
      ...(next
        ? [h("a", { class: "next-card", href: PATH.lesson(next.id), onclick: () => unlockAudio() },
            h("span", { class: "next-glyph" }, GROUP_GLYPH[next.group.en] ?? "課"),
            h("span", { class: "next-body" },
              h("span", { class: "next-kicker" }, `${t("nextUp")} · ${pick(next.group)} · ${t("minLesson")}`),
              h("span", { class: "next-title" }, pick(next.title)),
              h("span", { class: "next-text" }, teaser(next)),
            ),
            h("span", { class: "next-go" }, done.has(next.id) ? t("resumeLesson") : t("startLesson")),
          )]
        : [h("div", { class: "panel-card all-done" },
            h("span", { class: "next-glyph" }, "圓"),
            h("span", { class: "next-body" },
              h("span", { class: "next-title" }, t("allDoneReview")),
              h("span", { class: "next-text" }, t("lessonsAllDone")),
            ),
            h("a", { class: "btn primary", href: PATH.puzzles }, t("trainNow")),
          )]),

      // ---- the curriculum, stage by stage, with the lessons themselves ----
      h("div", { class: "section-head" },
        h("h2", { class: "section-title" }, t("pathTitle")),
        h("span", { class: "section-note" }, t("pathSub")),
      ),
      h("div", { class: "path-grid" },
        ...LESSON_GROUPS().map((g, i) => {
          const total = g.items.length;
          const dn = doneCount(g.items);
          const pct = total ? Math.round((dn / total) * 100) : 0;
          return h("section", { class: "path-card" + (dn === total ? " done" : "") },
            h("div", { class: "path-head" },
              h("span", { class: "path-glyph" }, GROUP_GLYPH[g.group.en] ?? "課"),
              h("span", { class: "path-id" },
                h("span", { class: "path-step" }, `${String(i + 1).padStart(2, "0")} · ${total} ${t(total === 1 ? "groupLesson" : "groupLessons")}`),
                h("span", { class: "path-name" }, pick(g.group)),
              ),
              h("span", { class: "path-count" + (dn === total ? " full" : "") }, dn === total ? t("groupComplete") : `${dn}/${total}`),
            ),
            h("div", { class: "path-bar" }, h("span", { class: "path-fill", style: `width:${pct}%` })),
            h("div", { class: "path-items" },
              ...g.items.map((l) =>
                h("a", { class: "path-item" + (done.has(l.id) ? " done" : ""), href: PATH.lesson(l.id), onclick: () => unlockAudio() },
                  h("span", { class: "path-tick" }, done.has(l.id) ? "✓" : ""),
                  h("span", { class: "path-item-name" }, pick(l.title)),
                ),
              ),
            ),
          );
        }),
      ),

      // ---- everything else worth studying with ----
      h("div", { class: "section-head" },
        h("h2", { class: "section-title" }, t("toolsTitle")),
        h("span", { class: "section-note" }, t("toolsSub")),
      ),
      h("div", { class: "mode-grid cols-3" },
        card(t("puzzles"), `${t("puzzlesSub")} · ${solved.size}/${PUZZLES.length}`, "題", PATH.puzzles),
        card(t("analysis"), t("analysisSub"), "研", PATH.analysis),
        card(t("guideTitle"), t("guideSub"), "書", PATH.rules),
      ),

      // ---- the written reference, which lives on the main site ----
      // The lessons teach by playing; the reference explains in depth, one rule
      // per page. Sending people there deliberately rather than duplicating it
      // here keeps a single source of truth for the rules.
      h("div", { class: "section-head" },
        h("h2", { class: "section-title" }, t("refTitle")),
        h("span", { class: "section-note" }, t("refSub")),
      ),
      h("div", { class: "mode-grid cols-2" },
        siteCard(t("refPieces"), t("refPiecesSub"), "馬", "/rules"),
        siteCard(t("refFlying"), t("refFlyingSub"), "將", "/rules/flying-general"),
        siteCard(t("refRepetition"), t("refRepetitionSub"), "長", "/rules/perpetual-check-and-chase"),
        siteCard(t("refOpenings"), t("refOpeningsSub"), "開", "/openings"),
      ),
      h("div", { class: "setup-actions" },
        h("a", {
          class: "btn ghost",
          href: siteUrl("/rules", "learn-ref-all"),
          onclick: () => track("to_site", { page: "/rules" }),
        }, t("refAll")),
      ),

      // ---- how to actually use all of it ----
      h("div", { class: "section-head" }, h("h2", { class: "section-title" }, t("howTitle"))),
      h("div", { class: "how-grid" },
        ...([["一", "howStep1", "howStep1Sub"], ["二", "howStep2", "howStep2Sub"], ["三", "howStep3", "howStep3Sub"]] as const).map(
          ([n, title, sub]) => h("div", { class: "how-card" },
            h("span", { class: "how-num" }, n),
            h("span", { class: "how-title" }, t(title)),
            h("span", { class: "how-text" }, t(sub)),
          ),
        ),
      ),

      h("div", { class: "setup-actions" },
        h("a", { class: "btn ghost", href: PATH.home }, t("home")),
        h("a", { class: "btn ghost", href: PATH.lessons }, t("allLessons")),
      ),
    ),
  );
}

// ---------------------------------------------------------------- Rules reference
/** The written rules, matching exactly what `@xq/rules` enforces on the board. */
const GUIDE: { title: { zh: string; en: string; vi?: string }; body: { zh: string; en: string; vi?: string }[] }[] = [
  {
    title: { zh: "棋盘", en: "The board", vi: "Bàn cờ" },
    body: [
      { zh: "棋盘为九路十行的交叉点，棋子走在交叉点上，而非方格中。", en: "Pieces sit on the intersections of 9 files and 10 ranks — on the points, not inside the squares.", vi: "Quân cờ đặt trên giao điểm của 9 lộ dọc và 10 hàng ngang — trên điểm, không phải trong ô vuông." },
      { zh: "中间的“楚河汉界”为河界，象/相不可过河，兵/卒过河后才能横走。", en: "The river divides the two halves. Elephants may never cross it, and a soldier only gains sideways movement after crossing.", vi: "Sông chia đôi bàn cờ. Tượng không bao giờ được qua sông, còn Tốt chỉ đi ngang được sau khi qua sông." },
      { zh: "两端各有一个三三见方的九宫，将/帥与士/仕只能在九宫内活动。", en: "Each side has a 3×3 palace. The general and its advisors may never leave it.", vi: "Mỗi bên có một cung 3×3. Tướng và hai Sĩ không bao giờ được rời cung." },
    ],
  },
  {
    title: { zh: "棋子走法", en: "How the pieces move", vi: "Cách đi các quân" },
    body: [
      { zh: "帥/將：九宫内每步一格，只走直线，不可斜行。", en: "General (帥/將): one point per move, orthogonally, never leaving the palace.", vi: "Tướng (帥/將): mỗi nước một ô theo chiều ngang hoặc dọc, không bao giờ rời cung." },
      { zh: "仕/士：九宫内斜走一格。", en: "Advisor (仕/士): one point diagonally, inside the palace only.", vi: "Sĩ (仕/士): đi chéo một ô, chỉ trong cung." },
      { zh: "相/象：走田字，田心有子即“塞象眼”，不能走；且不可过河。", en: "Elephant (相/象): exactly two points diagonally. Blocked if the midpoint is occupied (\"blocking the elephant's eye\"), and it can never cross the river.", vi: "Tượng (相/象): đi chéo đúng hai ô. Bị chặn nếu điểm giữa có quân (“cản mắt Tượng”), và không bao giờ được qua sông." },
      { zh: "馬：走日字，先直后斜；紧贴的直线上有子即“蹩马腿”，不能走。", en: "Horse (馬): one point orthogonally then one diagonally outward. If the orthogonal step is occupied the move is blocked (\"hobbling the horse's leg\").", vi: "Mã (馬): đi một ô thẳng rồi một ô chéo ra ngoài. Nếu ô thẳng đó có quân thì nước đi bị chặn (“cản chân Mã”)." },
      { zh: "車：直线任意距离，不可越子。", en: "Chariot (車): any distance orthogonally, like a rook; it may not jump.", vi: "Xe (車): đi ngang hoặc dọc bao xa tuỳ ý, như Xe cờ vua; không được nhảy qua quân." },
      { zh: "炮/砲：不吃子时走法同车；吃子必须隔一个棋子（炮架）跳过去。", en: "Cannon (炮/砲): moves like a chariot when not capturing, but to capture it must jump exactly one intervening piece (the screen).", vi: "Pháo (炮/砲): đi như Xe khi không ăn quân, nhưng muốn ăn thì phải nhảy qua đúng một quân ở giữa (ngòi)." },
      { zh: "兵/卒：未过河只能向前一格；过河后可前进或左右横走一格，永不后退。", en: "Soldier (兵/卒): one point forward; after crossing the river it may also move one point sideways. It never moves backwards.", vi: "Tốt (兵/卒): tiến một ô; sau khi qua sông thì đi ngang một ô được. Không bao giờ đi lùi." },
    ],
  },
  {
    title: { zh: "特殊规则", en: "Special rules", vi: "Luật đặc biệt" },
    body: [
      { zh: "白脸将：两个将/帥不可在同一直线上直接照面，中间没有任何棋子。", en: "Flying general: the two generals may never face each other down an open file with no piece between them.", vi: "Lộ mặt tướng: hai Tướng không bao giờ được đối mặt nhau trên một lộ trống, không có quân nào ở giữa." },
      { zh: "不可送将：任何使自己被将军的走法都不合法。", en: "You may never leave (or place) your own general in check.", vi: "Không bao giờ được để (hay đưa) Tướng mình vào thế bị chiếu." },
      { zh: "被将军时必须应将。", en: "When in check you must answer the check.", vi: "Khi bị chiếu, bạn phải đỡ chiếu." },
    ],
  },
  {
    title: { zh: "胜负与和棋", en: "Winning and drawing", vi: "Thắng và hoà" },
    body: [
      { zh: "将死对方即胜。", en: "Checkmate wins the game.", vi: "Chiếu bí là thắng ván." },
      { zh: "困毙——轮到走棋一方无合法着法——判负，与国际象棋不同。", en: "Stalemate is a LOSS for the player with no legal move — unlike international chess.", vi: "Hết nước đi là THUA cho bên không còn nước hợp lệ — khác với cờ vua." },
      { zh: "超时判负；认输判负；长时间掉线判负。", en: "Running out of time, resigning, or abandoning the game all lose.", vi: "Hết giờ, xin thua hoặc bỏ ván đều bị xử thua." },
      { zh: "三次重复同一局面而双方均无长将长捉，判和。", en: "A position repeated three times, with neither side forcing it by perpetual check or chase, is a draw.", vi: "Thế cờ lặp lại ba lần mà không bên nào ép lặp bằng chiếu lặp hay đuổi lặp thì hoà." },
      { zh: "长将不允许：一方连续将军而局面重复，必须变着，否则判负。", en: "Perpetual check is forbidden: the checking side must vary, or lose.", vi: "Cấm chiếu lặp: bên chiếu phải đổi nước, không đổi thì thua." },
    ],
  },
  {
    title: { zh: "记谱", en: "Notation", vi: "Ký hiệu nước đi" },
    body: [
      { zh: "本站采用坐标记谱（如 h2e2）与中文记谱（如 炮二平五）两种方式，分析棋盘上均可查看。", en: "This site uses both coordinate notation (e.g. h2e2) and Chinese notation (e.g. 炮二平五); the analysis board shows either.", vi: "Trang này dùng cả ký hiệu toạ độ (vd. h2e2) lẫn ký hiệu Trung Quốc (vd. 炮二平五); bàn phân tích hiển thị được cả hai." },
      { zh: "中文记谱中，红方从右向左数一至九，黑方用 1-9，“平”为横走，“进/退”为前后。", en: "In Chinese notation Red counts files right-to-left with 一…九 and Black uses 1…9; 平 means sideways, 进/退 forward and back.", vi: "Trong ký hiệu Trung Quốc, Đỏ đếm lộ từ phải sang trái bằng 一…九, Đen dùng 1…9; 平 (bình) là đi ngang, 进/退 (tiến/thoái) là tiến và lùi." },
    ],
  },
];

function showGuide() {
  closeModal();
  navHandler = null;
  enter("guide", PATH.rules, t("guideTitle"));
  app.replaceChildren(
    header("learn"),
    h("div", { class: "hub guide" },
      h("div", { class: "lessons-head" }, h("h2", {}, t("guideTitle")), h("span", { class: "progress" }, t("guideSub"))),
      ...GUIDE.map((sec) =>
        h("div", { class: "panel-card hub-card" },
          h("div", { class: "moves-title" }, pick(sec.title)),
          ...sec.body.map((p) => h("p", { class: "guide-p" }, pick(p))),
        ),
      ),
      h("div", { class: "setup-actions" },
        h("a", { class: "btn primary", href: PATH.lessons, onclick: () => unlockAudio() }, t("lessonsTitle")),
        h("a", { class: "btn ghost", href: PATH.learn }, t("back")),
      ),
    ),
  );
}

// ---------------------------------------------------------------- Lessons
function showLessons() {
  closeModal();
  navHandler = null;
  enter("lessons_list", PATH.lessons, t("lessonsTitle"));
  const done = doneSet();
  const next = LESSONS.find((l) => !done.has(l.id));
  const pct = Math.round((done.size / LESSONS.length) * 100);
  // Numbering runs across the whole curriculum, so "lesson 11" means the same
  // thing here as it does on the lesson page itself.
  const numberOf = (l: Lesson) => LESSONS.indexOf(l) + 1;
  const teaser = (l: Lesson) => summarise(l, 105);

  const groups = LESSON_GROUPS().map((g) => {
    const dn = g.items.filter((l) => done.has(l.id)).length;
    return h("section", { class: "lesson-group" },
      h("div", { class: "lesson-group-head" },
        h("h3", { class: "lesson-group-title" }, pick(g.group)),
        h("span", { class: "lesson-group-count" + (dn === g.items.length ? " full" : "") },
          dn === g.items.length ? t("groupComplete") : `${dn}/${g.items.length}`),
      ),
      h("div", { class: "lesson-items" },
        ...g.items.map((l) =>
          h("a", {
            class: "lesson-card" + (done.has(l.id) ? " done" : "") + (l === next ? " next" : ""),
            href: PATH.lesson(l.id), onclick: () => unlockAudio(),
          },
            h("span", { class: "lc-num" }, done.has(l.id) ? "✓" : String(numberOf(l)).padStart(2, "0")),
            h("span", { class: "lc-body" },
              h("span", { class: "lc-title" }, pick(l.title)),
              h("span", { class: "lc-text" }, teaser(l)),
            ),
            h("span", { class: "lc-arrow" }, "→"),
          ),
        ),
      ),
    );
  });

  app.replaceChildren(
    header("learn"),
    h("div", { class: "lessons lessons-page" },
      h("div", { class: "lessons-head" },
        h("h2", {}, t("lessonsTitle")),
        h("span", { class: "progress" }, `${done.size} / ${LESSONS.length} ${t("progressDone")}`),
      ),
      // One bar across the top: the list below is long, and this is the answer
      // to "how far am I" without counting ticks.
      h("div", { class: "lessons-progress" },
        h("div", { class: "prog-bar" }, h("div", { class: "prog-fill", style: `width:${pct}%` })),
        ...(next
          ? [h("a", { class: "btn primary small", href: PATH.lesson(next.id), onclick: () => unlockAudio() },
              `${done.size ? t("continueLearning") : t("startLesson")} · ${pick(next.title)}`)]
          : [h("span", { class: "lessons-alldone" }, t("lessonsAllDone"))]),
      ),
      ...groups,
      h("div", { class: "setup-actions" },
        h("a", { class: "btn ghost", href: PATH.learn }, t("back")),
        h("a", { class: "btn ghost", href: PATH.puzzles }, t("puzzles")),
      ),
    ),
  );
}

class LessonView {
  private lesson: Lesson;
  private index: number;
  private pos!: Position;
  private board!: BoardView;
  private solved = false;
  private student: Color = Color.Red;
  private feedbackEl!: HTMLElement;
  private lastMove: Move | null = null;

  constructor(id: string) {
    this.index = LESSONS.findIndex((l) => l.id === id);
    this.lesson = LESSONS[this.index]!;
    this.load();
  }

  private load() {
    // Each lesson is its own page, so a link to one opens that lesson.
    enter("lesson", PATH.lesson(this.lesson.id), pick(this.lesson.title));
    track("lesson_start", { lesson_id: this.lesson.id, group: this.lesson.group.en });
    this.pos = parseFen(this.lesson.fen);
    this.student = this.pos.sideToMove;
    this.solved = this.lesson.task.kind === "info";
    this.lastMove = null;
    this.renderScreen();
    if (this.lesson.task.kind === "info") {
      // Reading one of these IS finishing it. They were marked done but never
      // reported, so the console showed 0 completions for all twelve.
      if (!doneSet().has(this.lesson.id)) track("lesson_complete", { lesson_id: this.lesson.id, group: this.lesson.group.en });
      markDone(this.lesson.id);
    }
  }

  private interactive(): boolean {
    return (this.lesson.task.kind !== "info" && !this.solved) || this.explorable();
  }

  /**
   * An info lesson that points at a piece draws that piece's moves as dots, and
   * dots look like an invitation: one lesson alone drew 101 clicks on a board
   * that did not answer. So the piece can be tried -- it moves, says so, and
   * goes back -- without anything being graded.
   */
  private explorable(): boolean {
    return this.lesson.task.kind === "info" && !!this.lesson.focus;
  }
  private exploreTimer = 0;
  private boardHost: HTMLElement | null = null;

  private renderScreen() {
    const l = this.lesson;
    const boardHost = h("div", { class: "board-wrap" });
    this.boardHost = boardHost;
    this.feedbackEl = h("div", { class: "lesson-feedback" });
    const taskLabel =
      l.task.kind === "info" ? (this.explorable() ? t("taskExplore") : t("taskInfo")) :
      l.task.kind === "any-legal" ? t("taskAnyLegal") :
      l.task.kind === "achieve-mate" ? t("taskMate") : t("taskMove");

    app.replaceChildren(
      header(),
      h("div", { class: "game-layout" },
        h("div", { class: "board-col" }, boardHost),
        h("div", { class: "panel lesson-panel" },
          h("div", { class: "panel-top" },
            h("span", { class: "vs-label" }, `${pick(l.group)} · ${this.index + 1}/${LESSONS.length}`),
            soundBtn(),
          ),
          h("div", { class: "lesson-title" }, pick(l.title)),
          h("div", { class: "lesson-text" }, pick(l.text)),
          h("div", { class: "lesson-task" }, taskLabel),
          this.feedbackEl,
          ...(l.quiz ? [this.quizBlock(l.quiz)] : []),
          h("div", { class: "buttons" },
            h("button", { class: "btn ghost", onclick: () => this.go(-1) }, t("prevLesson")),
            h("button", { class: "btn primary", onclick: () => this.go(1) }, t("nextLesson")),
          ),
          h("div", { class: "buttons" },
            h("button", { class: "btn", onclick: () => this.load() }, t("reset")),
            h("a", { class: "btn ghost", href: PATH.lessons }, t("allLessons")),
          ),
        ),
      ),
    );
    this.board = new BoardView(boardHost, (m) => this.onMove(m));
    this.board.orientation = l.orientation === "black" ? Color.Black : l.orientation === "red" ? Color.Red : this.student;
    navHandler = null;
    this.paint();
  }

  private paint() {
    const l = this.lesson;
    this.board.setState({
      pos: this.pos,
      lastMove: this.lastMove,
      selectable: this.interactive() ? this.student : null,
      viewer: this.student,
      showMovesFor: l.focus && !this.lastMove ? coordToSquare(l.focus) : null,
      hideDanger: true,
    });
  }

  private onMove(m: Move) {
    const l = this.lesson;
    if (!this.interactive()) return;
    if (this.explorable()) {
      const captured = !!this.pos.pieceAt(m.to);
      this.pos.makeMove(m);
      this.lastMove = m;
      if (captured) playCapture(); else playMove();
      this.feedbackEl.className = "lesson-feedback good";
      this.feedbackEl.textContent = t("lessonExplore");
      this.paint();
      track("lesson_explore", { lesson_id: l.id });
      // Back to the start after a beat, so the next square can be tried.
      clearTimeout(this.exploreTimer);
      const board = this.board;
      this.exploreTimer = window.setTimeout(() => {
        if (this.board !== board || !this.boardHost?.isConnected) return; // left, or reloaded
        this.pos = parseFen(l.fen);
        this.lastMove = null;
        this.paint();
      }, 1100);
      return;
    }
    const uci = moveToUci(m);
    const before = toFen(this.pos);
    const captured = !!this.pos.pieceAt(m.to);
    this.pos.makeMove(m);
    this.lastMove = m;

    let ok = false;
    if (l.task.kind === "any-legal") ok = true;
    else if (l.task.kind === "play-move") ok = l.task.solution.includes(uci);
    else if (l.task.kind === "achieve-mate") ok = isCheckmate(this.pos);

    if (ok) {
      this.solved = true;
      markDone(l.id);
      track("lesson_complete", { lesson_id: l.id, group: l.group.en });
      if (captured) playCapture(); else playMove();
      playEnd(true);
      this.feedbackEl.className = "lesson-feedback good";
      this.feedbackEl.textContent = "✓ " + (l.success ? pick(l.success) : t("correct"));
      this.paint();
    } else {
      playCheck();
      track("lesson_wrong", { lesson_id: l.id, group: l.group.en, move: uci });
      this.feedbackEl.className = "lesson-feedback bad";
      // "Try again" teaches nothing. Say what this move actually did — from the
      // lesson's own notes where it has one, and from the position itself where
      // it does not, so every wrong move gets a real answer.
      this.feedbackEl.textContent = this.explain(uci, parseFen(before), m);
      this.paint();
      setTimeout(() => { this.pos = parseFen(before); this.lastMove = null; this.paint(); }, 1600);
    }
  }

  /**
   * A check of understanding, after the board work.
   *
   * Every option explains itself when picked, including the right one — being
   * told you are correct without being told why teaches only the answer, not
   * the idea. Wrong answers stay clickable so the whole set can be explored.
   */
  private quizBlock(quiz: Quiz): HTMLElement {
    const say = h("div", { class: "quiz-why" });
    const opts = h("div", { class: "quiz-options" });
    const paint = () => opts.replaceChildren(...quiz.options.map((o) =>
      h("button", {
        class: "quiz-opt",
        type: "button",
        onclick: () => {
          say.className = "quiz-why " + (o.ok ? "good" : "bad");
          say.textContent = `${o.ok ? "✓ " + t("quizRight") : "· " + t("quizWrong")} — ${pick(o.why)}`;
          track(o.ok ? "quiz_right" : "quiz_wrong", { lesson_id: this.lesson.id });
          if (o.ok) playEnd(true);
        },
      }, pick(o.text)),
    ));
    paint();
    return h("div", { class: "quiz" },
      h("div", { class: "quiz-q" }, `${t("quizTitle")} · ${pick(quiz.q)}`),
      opts,
      say,
    );
  }

  /**
   * Why that move was not the answer.
   *
   * An authored note wins when the lesson has one for this exact move — those
   * are the near-misses worth naming. Otherwise the reply is derived from the
   * position, which means it is always available and always true.
   */
  private explain(uci: string, before: Position, m: Move): string {
    const authored = this.lesson.misses?.find((x) => x.uci === uci);
    if (authored) return pick(authored.say);

    const moved = before.pieceAt(m.from);
    const captured = before.pieceAt(m.to);
    const foe = opponent(this.student);

    // The position after the move is already on this.pos.
    if (isCheckmate(this.pos)) return "✓ " + t("correct");
    if (inCheck(this.pos, foe)) return t("whyCheckOnly");
    if (isSquareAttacked(this.pos, m.to, foe) && !isSquareAttacked(this.pos, m.to, this.student)) {
      return t("whyHangs").replace("{piece}", moved ? pieceName(moved.type) : "");
    }
    if (captured) return t("whyWinsButNot");
    return t("whyQuiet");
  }

  private go(delta: number) {
    const next = this.index + delta;
    if (next < 0 || next >= LESSONS.length) { showLessons(); return; }
    this.index = next;
    this.lesson = LESSONS[next]!;
    this.load();
  }
}


// ---------------------------------------------------------------- Analysis board
function showAnalysis() {
  closeModal();
  enter("analysis", PATH.analysis, t("analysis"));
  track("select_mode", { mode: "analysis" });
  new AnalysisView();
}

class AnalysisView implements HistoryHost {
  private game = new Game(parseFen(START_FEN));
  moves: Move[] = [];
  viewPly: number | null = null;
  private worker: Worker;
  private board!: BoardView;
  private engineOn = true;
  private showBest = true;
  private redEval = 0;            // centipawns, Red's perspective
  private mate: number | null = null;
  private best: Move | null = null;
  private thinking = false;
  private barFill!: HTMLElement; private scoreEl!: HTMLElement; private bestEl!: HTMLElement; private movesEl!: HTMLElement;

  constructor() {
    this.worker = new Worker(`worker.js?v=${__BUILD__}`, { type: "module" });
    this.worker.onmessage = (e) => this.onEngine(e.data);
    this.renderScreen();
    this.analyze();
  }
  setView(ply: number | null) { this.viewPly = ply === null || ply >= this.moves.length ? null : Math.max(0, ply); this.render(); }
  private live() { return this.viewPly === null; }
  private curPos(): Position { return this.live() ? this.game.position : replayTo(this.moves, this.viewPly!); }

  private renderScreen() {
    const boardHost = h("div", { class: "board-wrap" });
    this.scoreEl = h("div", { class: "eval-score" }, "0.0");
    this.barFill = h("div", { class: "eval-fill" });
    this.bestEl = h("div", { class: "substatus" });
    this.movesEl = h("div", { class: "moves" });
    navHandler = (c) => { const cur = this.viewPly ?? this.moves.length; if (c === -1) this.setView(Math.max(0, cur - 1)); else if (c === 1) this.setView(Math.min(this.moves.length, cur + 1)); else if (c === "start") this.setView(0); else this.setView(null); };
    app.replaceChildren(header(),
      h("div", { class: "game-layout" },
        h("div", { class: "board-col" }, boardHost),
        h("div", { class: "panel" },
          h("div", { class: "panel-top" }, h("span", { class: "vs-label" }, t("analysis")), soundBtn()),
          h("div", { class: "eval-wrap" }, h("div", { class: "eval-bar" }, this.barFill), this.scoreEl),
          this.bestEl,
          h("div", { class: "buttons" },
            h("button", { class: "btn", onclick: () => { this.engineOn = !this.engineOn; this.analyze(); } }, t("engineOn")),
            h("button", { class: "btn", onclick: () => { this.showBest = !this.showBest; this.render(); } }, t("showBest")),
          ),
          h("div", { class: "buttons" },
            h("button", { class: "btn", onclick: () => this.undo() }, t("undo")),
            h("button", { class: "btn", onclick: () => { this.board.orientation = this.board.orientation === Color.Red ? Color.Black : Color.Red; this.render(); } }, t("flip")),
          ),
          h("div", { class: "buttons" },
            h("button", { class: "btn", onclick: () => { this.game = new Game(parseFen(START_FEN)); this.moves = []; this.viewPly = null; this.analyze(); } }, t("newGame")),
            h("button", { class: "btn ghost", onclick: () => { this.worker.terminate(); showHome(); } }, t("home")),
          ),
          h("div", { class: "moves-title" }, t("movesTitle")),
          historyBar(this), this.movesEl,
        ),
      ),
    );
    this.board = new BoardView(boardHost, (m) => this.onMove(m));
    this.board.orientation = Color.Red;
    this.render();
  }

  private render() {
    const pos = this.curPos();
    const live = this.live();
    const shownLast = live ? this.moves[this.moves.length - 1] ?? null : this.viewPly! > 0 ? this.moves[this.viewPly! - 1]! : null;
    const noMoves = legalMoves(pos).length === 0;
    this.board.setState({ pos, lastMove: shownLast, selectable: live && !noMoves ? pos.sideToMove : null, viewer: pos.sideToMove, hint: live && this.engineOn && this.showBest ? this.best : null });
    // eval bar (Red on top)
    const pct = Math.max(3, Math.min(97, 50 - this.redEval / 16));
    this.barFill.style.height = `${pct}%`;
    this.scoreEl.textContent = this.mate !== null ? `${this.redEval >= 0 ? "+" : "-"}M${this.mate}` : `${this.redEval >= 0 ? "+" : ""}${(this.redEval / 100).toFixed(1)}`;
    this.bestEl.className = "substatus";
    this.bestEl.textContent = this.engineOn ? (this.best ? `${t("bestMoveLabel")}: ${coord(this.best)}` : this.thinking ? t("botThinking") : "") : t("engineOff");
    renderMoveList(this.movesEl, this);
  }

  private onMove(m: Move) {
    if (!this.live()) return;
    const cap = !!this.game.position.pieceAt(m.to);
    this.game.play(m); this.moves.push(m); this.viewPly = null;
    if (this.game.status() === GameResult.Ongoing) { if (inCheck(this.game.position, this.game.position.sideToMove)) playCheck(); else if (cap) playCapture(); else playMove(); }
    this.analyze();
  }
  private analyze() {
    if (!this.engineOn) { this.best = null; this.redEval = 0; this.mate = null; this.render(); return; }
    this.thinking = true; this.render();
    this.worker.postMessage({ type: "go", purpose: "analyze", fen: toFen(this.game.position), level: "hard" });
  }
  private onEngine(data: { purpose: string; move: Move | null; score: number; mate: number | null }) {
    if (data.purpose !== "analyze") return;
    this.thinking = false;
    const red = this.game.position.sideToMove === Color.Red ? 1 : -1;
    this.redEval = (data.score ?? 0) * red;
    this.mate = data.mate != null ? data.mate : null;
    this.best = data.move;
    this.render();
  }
  private undo() {
    if (this.moves.length === 0) return;
    this.moves.pop();
    this.game = new Game(parseFen(START_FEN));
    for (const m of this.moves) this.game.play(m);
    this.viewPly = null; this.analyze();
  }
}

// ---------------------------------------------------------------- Puzzles
function showPuzzles(id?: string) {
  closeModal();
  track("select_mode", { mode: "puzzles" });
  new PuzzleView(id);
}

class PuzzleView {
  private idx = 0;
  private pos!: Position;
  private board!: BoardView;
  private solved = false;
  private lastMove: Move | null = null;
  private streak = 0;
  private hintSquare: number | null = null;
  private feedbackEl!: HTMLElement; private streakEl!: HTMLElement;

  /**
   * With no id, open the first puzzle the player has not solved yet.
   *
   * `daily` is the same screen pointed at the one puzzle the date chooses. It
   * is a mode rather than a second class because everything below -- the board,
   * the hint, the wrong-answer shake -- is identical, and two copies of that
   * would drift apart within a month.
   */
  constructor(id?: string, private isDaily = false) {
    const done = solvedSet();
    const wanted = isDaily ? puzzleFor().id : id;
    const asked = wanted ? PUZZLES.findIndex((p) => p.id === wanted) : -1;
    const firstUnsolved = PUZZLES.findIndex((p) => !done.has(p.id));
    this.idx = asked >= 0 ? asked : firstUnsolved < 0 ? 0 : firstUnsolved;
    if (isDaily) this.solvedBefore = solvedToday();
    this.load();
  }
  /** Whether today's was already done before this visit, so the screen can say so. */
  private solvedBefore = false;
  private puzzle() { return PUZZLES[this.idx]!; }

  private load() {
    this.pos = parseFen(this.puzzle().fen);
    this.solved = false; this.lastMove = null; this.hintSquare = null;
    this.renderScreen();
  }

  private renderScreen() {
    const pz = this.puzzle();
    // Every puzzle is a page of its own — one can be linked to or bookmarked.
    // The daily one keeps its own address instead, so it can be shared without
    // giving the answer away in the URL.
    if (this.isDaily) enter("daily", PATH.daily, `${t("dailyTitle")} · ${today()}`);
    else enter("puzzles", PATH.puzzle(pz.id), `${t("puzzles")} · ${pick(pz.theme)}`);
    const boardHost = h("div", { class: "board-wrap" });
    this.feedbackEl = h("div", { class: "substatus" });
    this.streakEl = h("div", { class: "vs-label" });
    navHandler = null;
    app.replaceChildren(header(),
      h("div", { class: "game-layout" },
        h("div", { class: "board-col" }, boardHost),
        h("div", { class: "panel" },
          h("div", { class: "panel-top" }, this.streakEl, soundBtn()),
          h("div", { class: "lesson-title" }, this.isDaily ? `${t("dailyTitle")} · ${pick(pz.theme)}` : pick(pz.theme)),
          h("div", { class: "lesson-task" }, this.isDaily ? t("dailyPrompt") : t("puzzlePrompt")),
          this.feedbackEl,
          h("div", { class: "buttons" },
            h("button", { class: "btn", onclick: () => this.hint() }, t("hint")),
            // The daily has no "next": that is the whole point of it. Once it is
            // done the button opens the rest of the corpus instead.
            this.isDaily
              ? h("a", { class: "btn", href: PATH.puzzles }, t("morePuzzles"))
              : h("button", { class: "btn", onclick: () => this.next() }, this.solved ? t("nextPuzzle") : t("skip")),
          ),
          h("div", { class: "buttons" },
            h("button", { class: "btn", onclick: () => this.load() }, t("reset")),
            ...(this.isDaily ? [h("button", { class: "btn ghost", onclick: () => this.share() }, t("shareStreak"))] : []),
            h("a", { class: "btn ghost", href: PATH.learn }, t("back")),
          ),
        ),
      ),
    );
    this.board = new BoardView(boardHost, (m) => this.onMove(m));
    this.board.orientation = Color.Red;
    this.render();
  }

  private render() {
    this.board.setState({ pos: this.pos, lastMove: this.lastMove, selectable: this.solved ? null : Color.Red, viewer: Color.Red, showMovesFor: this.hintSquare, hideDanger: true });
    this.streakEl.textContent = this.isDaily
      ? `${t("dailyStreak")} ${liveStreak()} · ${t("bestStreak")} ${dailyState().best}`
      : `${t("solvedCount")} ${solvedSet().size}/${PUZZLES.length} · ${t("streak")} ${this.streak}`;
    // Skip/next is relabelled as the puzzle is solved -- but the daily has no
    // next, and its second button is a link to the rest of the corpus. Writing
    // over that was turning it back into "Skip" on every repaint.
    if (!this.isDaily) {
      const btns = app.querySelectorAll(".panel .buttons .btn");
      if (btns[1]) (btns[1] as HTMLElement).textContent = this.solved ? t("nextPuzzle") : t("skip");
    }
  }

  private onMove(m: Move) {
    if (this.solved) return;
    const pz = this.puzzle();
    const before = toFen(this.pos);
    const cap = !!this.pos.pieceAt(m.to);
    this.pos.makeMove(m); this.lastMove = m;
    const ok = pz.task.kind === "mate" ? isCheckmate(this.pos) : pz.task.solution.includes(moveToUci(m));
    if (ok) {
      this.solved = true; this.streak += 1; markSolved(pz.id);
      if (cap) playCapture(); else playMove(); playEnd(true); victoryBurst();
      track("puzzle_solved", { id: pz.id });
      let msg = t("correctPuzzle");
      if (this.isDaily) {
        // Idempotent, so re-solving after a reload cannot inflate the streak.
        const st = recordDailySolve();
        track("daily_solved", { streak: String(st.streak) });
        msg = this.solvedBefore ? t("dailyAlready") : `${t("dailyDone")} ${t("dailyStreak")} ${st.streak}`;
      }
      this.feedbackEl.className = "substatus"; this.feedbackEl.style.color = "#8fdc7a"; this.feedbackEl.textContent = "\u2713 " + msg;
      this.render();
    } else {
      this.streak = 0; playCheck();
      this.feedbackEl.className = "substatus alert"; this.feedbackEl.style.color = ""; this.feedbackEl.textContent = "\u2717 " + t("wrongPuzzle");
      this.render();
      setTimeout(() => { this.pos = parseFen(before); this.lastMove = null; this.render(); }, 650);
    }
  }
  private hint() {
    const pz = this.puzzle();
    let from: number | null = null;
    if (pz.task.kind === "move") from = coordToSquare(pz.task.solution[0]!.slice(0, 2));
    else { const mv = legalMoves(this.pos).find((m) => { const n = this.pos.clone(); n.makeMove(m); return isCheckmate(n); }); from = mv ? mv.from : null; }
    this.hintSquare = from;
    if (pz.hint) { this.feedbackEl.className = "substatus"; this.feedbackEl.style.color = ""; this.feedbackEl.textContent = pick(pz.hint); }
    this.render();
  }
  private next() {
    this.idx = (this.idx + 1) % PUZZLES.length;
    this.load();
  }

  /**
   * Copy a one-line result. No board, no solution -- a result somebody can post
   * without spoiling the puzzle for the person reading it.
   */
  private async share() {
    const line = `${t("dailyTitle")} ${today()} — ${t("dailyStreak")} ${liveStreak()}\n${location.origin}${PATH.daily}`;
    let done = false;
    try { await navigator.clipboard.writeText(line); done = true; } catch { /* no clipboard permission */ }
    track("daily_share", {});
    this.feedbackEl.className = "substatus";
    this.feedbackEl.style.color = "";
    this.feedbackEl.textContent = done ? t("copied") : line;
  }
}

// ---------------------------------------------------------------- Profile & game history
const REASON_KEY: Record<string, string> = {
  checkmate: "rCheckmate", resign: "rResign", timeout: "rTimeout",
  stalemate: "rStalemate", agreement: "rAgreement", abandonment: "rAbandonment",
  "threefold-repetition": "rRepetition", draw: "rDraw",
  "perpetual-check": "rPerpetualCheck", "perpetual-chase": "rPerpetualChase",
};
function timeAgo(ts: number): string {
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  const m = Math.floor(s / 60), hh = Math.floor(m / 60), d = Math.floor(hh / 24);
  if (d > 0) return `${d}d`;
  if (hh > 0) return `${hh}h`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function showProfile(username?: string) {
  closeModal();
  enter("profile", username ? PATH.player(username) : PATH.profile, username ?? t("profile"));
  navHandler = null;
  new ProfileView(username ?? null);
}

/** Rating bands. The names are a courtesy, not a promise: the maths is the same Elo. */
const RANKS: { min: number; key: string }[] = [
  { min: 0, key: "rankNovice" }, { min: 1100, key: "rankApprentice" }, { min: 1300, key: "rankAdept" },
  { min: 1500, key: "rankExpert" }, { min: 1700, key: "rankMaster" }, { min: 1900, key: "rankGrand" },
];
function tierOf(rating: number) {
  let i = 0;
  for (let k = 0; k < RANKS.length; k++) if (rating >= RANKS[k]!.min) i = k;
  const nx = RANKS[i + 1];
  return {
    key: RANKS[i]!.key,
    next: nx ? { key: nx.key, need: nx.min - rating, frac: (rating - RANKS[i]!.min) / (nx.min - RANKS[i]!.min) } : null,
  };
}

type Outcome = "win" | "loss" | "draw" | "other";
type ProfFilter = "all" | Outcome;

class ProfileView {
  private prof: account.Profile | null = null;
  private games: account.GameSummary[] = [];
  /** how I relate to the person I am looking at */
  private rel: "self" | "friend" | "requested" | "incoming" | "stranger" = "self";
  private filter: ProfFilter = "all";
  private listEl: HTMLElement | null = null;
  private tabsEl: HTMLElement | null = null;

  constructor(private username: string | null) {
    this.render(true);
    void this.load();
  }
  private me() { return this.username == null; }
  private targetName(): string | null {
    if (this.username) return this.username.toLowerCase();
    const p = account.currentProfile();
    return p ? p.username : null;
  }
  private async load() {
    if (this.me() && !account.isLoggedIn()) { this.render(false); return; }
    this.prof = await account.fetchProfile(this.username ?? undefined);
    this.games = await account.fetchGames(this.username ?? undefined, 25);
    const target = this.username?.toLowerCase();
    if (target && account.isLoggedIn() && target !== account.currentProfile()?.username) {
      const g = await account.fetchFriends();
      this.rel = g.friends.some((f) => f.username === target) ? "friend"
        : g.outgoing.some((f) => f.username === target) ? "requested"
        : g.incoming.some((f) => f.username === target) ? "incoming"
        : "stranger";
    } else this.rel = "self";
    this.render(false);
  }

  /** What a finished game was, from this person's point of view. */
  private outcome(g: account.GameSummary): Outcome {
    const u = this.targetName();
    const red = u != null && g.redUser === u, black = u != null && g.blackUser === u;
    if (!red && !black) return "other";
    if (g.result === "draw") return "draw";
    return (g.result === "red-wins") === red ? "win" : "loss";
  }
  private deltaOf(g: account.GameSummary): number | null {
    const u = this.targetName();
    if (!g.ratingDelta || u == null) return null;
    return g.redUser === u ? g.ratingDelta.red : g.blackUser === u ? g.ratingDelta.black : null;
  }

  /** The rating after each rated game, oldest first, ending at today's rating. */
  private trend(): number[] {
    if (!this.prof) return [];
    let r = this.prof.rating;
    const pts = [r];
    for (const g of this.games) { // newest first
      const d = this.deltaOf(g);
      if (d == null) continue;
      r -= d;
      pts.push(r);
    }
    return pts.reverse();
  }

  private sparkline(pts: number[]): string {
    const W = 200, H = 52, P = 4;
    const lo = Math.min(...pts), hi = Math.max(...pts), span = Math.max(1, hi - lo);
    const xy = pts.map((v, i) => [P + (i / (pts.length - 1)) * (W - 2 * P), H - P - ((v - lo) / span) * (H - 2 * P)] as const);
    const line = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
    const [lx, ly] = xy[xy.length - 1]!;
    return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true">
      <defs><linearGradient id="pfg" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="currentColor" stop-opacity=".32"/><stop offset="1" stop-color="currentColor" stop-opacity="0"/></linearGradient></defs>
      <path d="${line} L${W - P} ${H} L${P} ${H} Z" fill="url(#pfg)"/>
      <path d="${line}" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>
      <circle cx="${lx.toFixed(1)}" cy="${ly.toFixed(1)}" r="3.4" fill="currentColor"/></svg>`;
  }

  private render(loading: boolean) {
    if (this.me() && !account.isLoggedIn()) {
      app.replaceChildren(header("profile"),
        h("div", { class: "lessons" },
          h("div", { class: "panel-card profile-empty" },
            h("div", { class: "lesson-title" }, t("profile")),
            h("div", { class: "lesson-task" }, t("signInForProfile")),
            h("div", { class: "buttons" },
              h("a", { class: "btn primary", href: PATH.login }, t("logIn")),
              h("a", { class: "btn", href: PATH.signup }, t("signUp")),
              h("a", { class: "btn ghost", href: PATH.home }, t("home")),
            ),
          ),
        ),
      );
      return;
    }
    const p = this.prof;
    const uname = this.username?.toLowerCase() ?? p?.username ?? "";
    const total = p ? p.wins + p.losses + p.draws : 0;
    const wr = total > 0 ? Math.round((p!.wins / total) * 100) : 0;
    const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

    // ---- what I can do about this person ----
    const actions: Node[] = this.me()
      ? [h("a", { class: "btn primary", href: PATH.play }, t("navPlay")),
         h("a", { class: "btn ghost", href: PATH.friends }, t("friends")),
         h("a", { class: "btn ghost", href: PATH.settings }, t("settings"))]
      : [
          ...(this.rel === "friend"
            ? [h("button", { class: "btn primary", onclick: () => challengeFriend(uname) }, t("challenge")),
               h("a", { class: "btn", href: PATH.thread(uname) }, t("message"))]
            : this.rel === "requested"
              ? [h("button", { class: "btn ghost", disabled: "true" }, t("waitingForAccept"))]
              : this.rel === "incoming"
                ? [h("button", { class: "btn primary", onclick: async () => { await account.acceptFriend(uname); void this.load(); } }, t("accept"))]
                : account.isLoggedIn()
                  ? [h("button", { class: "btn primary", onclick: async () => { await account.requestFriend(uname); void this.load(); } }, t("addFriend"))]
                  : [h("a", { class: "btn primary", href: PATH.login }, t("logIn"))]),
          h("a", { class: "btn ghost", href: PATH.leaderboard }, t("leaderboard")),
        ];

    // ---- derived numbers ----
    const outcomes = this.games.map((g) => this.outcome(g));
    let streak = 0, streakKind: Outcome = "other";
    for (const o of outcomes) {
      if (o !== "win" && o !== "loss") break;
      if (streak === 0) streakKind = o;
      if (o !== streakKind) break;
      streak++;
    }
    const last10 = this.games.slice(0, 10).map((g) => this.deltaOf(g)).filter((d): d is number => d != null);
    const last10Sum = last10.reduce((a, b) => a + b, 0);
    const pts = this.trend();
    const rank = p ? tierOf(p.rating) : null;
    const since = p?.memberSince
      ? new Date(p.memberSince).toLocaleDateString(getLang() === "zh" ? "zh-CN" : getLang() === "vi" ? "vi-VN" : "en-GB", { year: "numeric", month: "short", day: "numeric" })
      : "";

    // ---- hero ----
    const hero = h("div", { class: "panel-card pf-hero" },
      h("div", { class: "pf-avatar" }, h("span", {}, (p?.displayName ?? "?").slice(0, 1).toUpperCase())),
      h("div", { class: "pf-id" },
        h("div", { class: "pf-name" }, p?.displayName ?? (loading ? "…" : t("profile"))),
        h("div", { class: "pf-sub" },
          ...(uname ? [h("span", { class: "pf-handle" }, `@${uname}`)] : []),
          ...(rank ? [h("span", { class: "pf-rank" }, t(rank.key))] : []),
        ),
        h("div", { class: "pf-since" }, since ? `${t("memberSince")} ${since}` : ""),
        h("div", { class: "pf-actions" }, ...actions),
      ),
      h("div", { class: "pf-rating" },
        h("div", { class: "pf-rating-top" },
          h("div", { class: "pf-rating-val" }, p ? String(p.rating) : "—"),
          h("div", { class: "pf-rating-label" }, t("rating")),
        ),
        pts.length >= 2
          ? h("div", { class: "pf-spark", title: t("profTrend"), html: this.sparkline(pts) })
          : h("div", { class: "pf-spark empty" }),
        rank
          ? h("div", { class: "pf-next" },
              h("div", { class: "pf-next-bar" }, h("span", { style: `width:${Math.round((rank.next?.frac ?? 1) * 100)}%` })),
              h("div", { class: "pf-next-text" },
                rank.next ? t("profToNext").replace("{n}", String(rank.next.need)).replace("{tier}", t(rank.next.key)) : t("profTopRank")))
          : h("span", {}),
      ),
    );

    // ---- key numbers ----
    const kpi = (cls: string, label: string, ...body: (Node | string)[]) =>
      h("div", { class: "pf-kpi " + cls }, h("div", { class: "pf-kpi-body" }, ...body), h("div", { class: "pf-kpi-label" }, label));
    const kpis = h("div", { class: "pf-kpis" },
      kpi("", t("games"), h("span", { class: "pf-kpi-val" }, p ? String(p.games) : "—")),
      kpi("pct", t("winRate"),
        h("span", { class: "pf-ring", html: `<svg viewBox="0 0 36 36" aria-hidden="true"><circle cx="18" cy="18" r="15.9155" class="trk"/><circle cx="18" cy="18" r="15.9155" class="val" stroke-dasharray="${wr} ${100 - wr}"/></svg>` }),
        h("span", { class: "pf-kpi-val" }, total > 0 ? `${wr}%` : "—")),
      kpi(streak > 0 ? (streakKind === "win" ? "up" : "down") : "", streakKind === "loss" ? t("profStreakLoss") : t("profStreakWin"),
        h("span", { class: "pf-kpi-val" }, streak > 0 ? String(streak) : "—")),
      kpi(last10.length ? (last10Sum >= 0 ? "up" : "down") : "", t("profLast10"),
        h("span", { class: "pf-kpi-val" }, last10.length ? (last10Sum > 0 ? `+${last10Sum}` : String(last10Sum)) : "—")),
    );

    // ---- record + form ----
    const legend = (cls: string, label: string, n: number) =>
      h("div", { class: "pf-leg " + cls },
        h("span", { class: "pf-leg-dot" }),
        h("span", { class: "pf-leg-label" }, label),
        h("span", { class: "pf-leg-n" }, p ? String(n) : "—"),
        h("span", { class: "pf-leg-pct" }, total > 0 ? `${Math.round(pct(n))}%` : ""));
    const form = outcomes.slice(0, 10).reverse();
    const record = h("div", { class: "panel-card pf-card" },
      h("div", { class: "pf-card-title" }, t("record")),
      h("div", { class: "wld-bar" },
        h("span", { class: "wld w", style: `width:${pct(p?.wins ?? 0)}%` }),
        h("span", { class: "wld d", style: `width:${pct(p?.draws ?? 0)}%` }),
        h("span", { class: "wld l", style: `width:${pct(p?.losses ?? 0)}%` }),
      ),
      h("div", { class: "pf-legend" },
        legend("w", t("wins"), p?.wins ?? 0), legend("d", t("draws"), p?.draws ?? 0), legend("l", t("losses"), p?.losses ?? 0)),
      ...(form.length ? [
        h("div", { class: "pf-form-title" }, t("profForm")),
        h("div", { class: "pf-form" }, ...form.map((o) => h("span", { class: "pf-dot " + o, title: o === "win" ? t("wins") : o === "loss" ? t("losses") : t("draws") }))),
      ] : []),
    );

    // ---- study progress, own profile only ----
    const meter = (label: string, done: number, of: number) =>
      h("div", { class: "pf-meter" },
        h("div", { class: "pf-meter-row" }, h("span", {}, label), h("b", {}, `${done}/${of}`)),
        h("div", { class: "pf-meter-bar" }, h("span", { style: `width:${of ? Math.round((done / of) * 100) : 0}%` })));
    const study = this.me() ? h("div", { class: "panel-card pf-card" },
      h("div", { class: "pf-card-title" }, t("profStudy")),
      meter(t("lessonsTitle"), doneSet().size, LESSONS.length),
      meter(t("puzzles"), solvedSet().size, PUZZLES.length),
      h("a", { class: "btn ghost pf-study-link", href: PATH.learn }, t("profContinue")),
    ) : null;

    // ---- history ----
    this.listEl = h("div", { class: "pf-games" });
    this.tabsEl = h("div", { class: "pf-tabs", role: "tablist" });
    const listSlot = this.listEl;
    if (loading) listSlot.append(...skeletonRows(3));
    else this.paintList();
    this.paintTabs();

    app.replaceChildren(header(this.me() ? "profile" : ""),
      h("div", { class: "profile-page pf" },
        hero,
        kpis,
        h("div", { class: "pf-cols" },
          h("div", { class: "pf-side" }, record, ...(study ? [study] : [])),
          h("div", { class: "pf-main" },
            h("div", { class: "pf-main-head" },
              h("h2", { class: "pf-main-title" }, t("recentGames")),
              this.tabsEl,
            ),
            listSlot,
          ),
        ),
      ),
    );
  }

  private paintTabs() {
    if (!this.tabsEl) return;
    const counts: Record<ProfFilter, number> = { all: this.games.length, win: 0, loss: 0, draw: 0, other: 0 };
    for (const g of this.games) counts[this.outcome(g)]++;
    const tabs: [ProfFilter, string][] = [["all", t("profAll")], ["win", t("wins")], ["loss", t("losses")], ["draw", t("draws")]];
    this.tabsEl.replaceChildren(...tabs.map(([k, label]) =>
      h("button", {
        class: "pf-tab" + (this.filter === k ? " on" : ""), type: "button", role: "tab", "aria-selected": String(this.filter === k),
        onclick: () => { this.filter = k; this.paintTabs(); this.paintList(); },
      }, label, h("i", {}, String(counts[k])))));
  }

  private paintList() {
    if (!this.listEl) return;
    const shown = this.games.filter((g) => this.filter === "all" || this.outcome(g) === this.filter);
    if (this.games.length === 0) {
      this.listEl.replaceChildren(h("div", { class: "empty-state" },
        h("div", { class: "empty-glyph" }, "棋"),
        h("div", { class: "empty-title" }, t("noGames")),
        ...(this.me() ? [h("a", { class: "btn primary", href: PATH.play }, t("playNow"))] : []),
      ));
    } else if (shown.length === 0) {
      this.listEl.replaceChildren(h("div", { class: "pf-none" }, t("profNoMatch")));
    } else this.listEl.replaceChildren(...shown.map((g) => this.gameRow(g)));
  }

  private gameRow(g: account.GameSummary): HTMLElement {
    const u = this.targetName();
    const iAmRed = u != null && g.redUser === u;
    const iAmBlack = u != null && g.blackUser === u;
    const o = this.outcome(g);
    const badge = o === "win" ? "W" : o === "loss" ? "L" : o === "draw" ? "½" : g.result === "red-wins" ? "红" : g.result === "black-wins" ? "黑" : "½";
    const cls = o === "other" ? (g.result === "red-wins" ? "win" : g.result === "black-wins" ? "loss" : "draw") : o;
    const reason = t(REASON_KEY[g.reason] ?? g.reason);
    const delta = this.deltaOf(g);
    // Against whom: the other side, when this person played. Otherwise both.
    const who = iAmRed || iAmBlack
      ? [h("span", { class: "pf-side-disc " + (iAmRed ? "red" : "black"), title: iAmRed ? t("profAsRed") : t("profAsBlack") }),
         h("span", { class: "pf-vs" }, t("vs")),
         h("span", { class: "pf-opp" }, iAmRed ? g.blackName : g.redName)]
      : [h("span", { class: "pf-opp" }, g.redName), h("span", { class: "pf-vs" }, t("vs")), h("span", { class: "pf-opp" }, g.blackName)];
    return h("a", { class: `pf-game ${cls}`, href: PATH.replay(g.id) },
      h("span", { class: "pf-badge" }, badge),
      h("span", { class: "pf-game-main" },
        h("span", { class: "pf-game-who" }, ...who),
        h("span", { class: "pf-game-meta" },
          ...(g.tc ? [h("span", { class: "pf-chip" }, g.tc)] : []),
          h("span", {}, reason)),
      ),
      delta != null ? h("span", { class: "pf-delta " + (delta > 0 ? "up" : delta < 0 ? "down" : "flat") }, delta > 0 ? `+${delta}` : `${delta}`) : h("span", {}),
      h("span", { class: "pf-ago" }, timeAgo(g.endedAt)),
    );
  }
}

// ---------------------------------------------------------------- Replay a stored game
/**
 * A finished computer game, waiting to be reviewed.
 *
 * Games against the computer are played entirely in the browser and never sent
 * anywhere, so there is nothing for the replay screen to fetch. It is handed
 * the game directly instead. Kept in memory only: this is a game the player has
 * just finished, not a record, and it lasts exactly as long as the tab does.
 */
let pendingLocalGame: account.GameRecord | null = null;
export const LOCAL_REVIEW_ID = "local";

function showReplay(id: string) {
  closeModal();
  enter("game_replay", PATH.replay(id), t("replayTitle"));
  navHandler = null;
  new ReplayView(id);
}

class ReplayView implements HistoryHost {
  moves: Move[] = [];
  viewPly: number | null = 0;
  private board!: BoardView;
  private rec: account.GameRecord | null = null;
  private movesEl!: HTMLElement;
  private titleEl!: HTMLElement;
  private statusEl!: HTMLElement;
  // ---- the review ----
  private worker: Worker | null = null;
  private review: GameReview | null = null;
  private reviewing = false;
  private progress = 0;
  private reviewEl!: HTMLElement;

  constructor(private id: string) {
    this.render(true);
    void this.load();
  }

  /** The marks the move list draws, once there is a review to draw them from. */
  private marks(): Map<number, Verdict> | undefined {
    if (!this.review) return undefined;
    return new Map(this.review.plies.map((p) => [p.ply, p.verdict]));
  }

  /**
   * Review the game in the browser, on demand.
   *
   * On demand because it costs real seconds and most replays are opened to see
   * what happened, not to be marked. In the browser because the alternative is
   * a queue on a server that would then have to be sized for everyone's games
   * at once, to answer a question only the player asks.
   */
  private startReview() {
    if (this.reviewing || this.review || this.moves.length === 0) return;
    this.reviewing = true;
    this.progress = 0;
    this.paintReview();
    track("game_review_start", { moves: String(this.moves.length) });

    // Every position of the game, start included, so the worker scores each one
    // exactly once -- the position after your move is the position before your
    // opponent's, and scoring it twice would double the wait for the same
    // numbers.
    const fens: string[] = [];
    for (let i = 0; i <= this.moves.length; i++) fens.push(toFen(replayTo(this.moves, i)));

    const scores: number[] = [];
    const bests: (string | null)[] = [];
    const w = new Worker(`worker.js?v=${__BUILD__}`, { type: "module" });
    this.worker = w;
    w.onerror = () => { this.reviewing = false; this.paintReview(); };
    w.onmessage = (e) => {
      const d = e.data as { type: string; i?: number; total?: number; score?: number; best?: string | null; message?: string; stack?: string };
      if (d.type === "reviewAt") {
        scores[d.i!] = d.score ?? 0;
        bests[d.i!] = d.best ?? null;
        this.progress = (d.i! + 1) / (d.total || fens.length);
        // Repaint the bar, not the whole screen: this fires once per position.
        this.paintReview();
      } else if (d.type === "reviewDone") {
        this.reviewing = false;
        this.review = reviewGame(scores, this.moves.map(moveToUci), bests);
        track("game_review_done", {
          red: String(this.review.red.accuracy), black: String(this.review.black.accuracy),
        });
        w.terminate(); this.worker = null;
        this.paintReview();
        renderMoveList(this.movesEl, this, this.marks());
      } else if (d.type === "reviewFailed") {
        reportError(`game review failed: ${d.message ?? "unknown"}`, d.stack ?? "");
        this.reviewing = false;
        w.terminate(); this.worker = null;
        this.paintReview();
      }
    };
    // "hard" rather than "master": a whole game at master strength is minutes,
    // and the difference does not change whether a move was a blunder.
    w.postMessage({ type: "review", fens, budgetMs: 240 });
  }

  /** The review panel, redrawn on its own so progress does not repaint the board. */
  private paintReview() {
    if (!this.reviewEl) return;
    const r = this.review;
    if (this.reviewing) {
      this.reviewEl.replaceChildren(
        h("div", { class: "rv-progress" },
          h("span", {}, `${t("reviewing")} ${Math.round(this.progress * 100)}%`),
          h("span", { class: "rv-bar" }, h("span", { class: "rv-fill", style: `width:${Math.round(this.progress * 100)}%` })),
        ),
      );
      return;
    }
    if (!r) {
      this.reviewEl.replaceChildren(
        h("button", { class: "btn primary wide", onclick: () => this.startReview() }, t("reviewGame")),
        h("div", { class: "auth-hint" }, t("reviewNote")),
      );
      return;
    }
    const side = (label: string, s: GameReview["red"]) =>
      h("div", { class: "rv-side" },
        h("div", { class: "rv-name" }, label),
        h("div", { class: "rv-acc" }, `${s.accuracy}%`),
        h("div", { class: "rv-sub" }, `${t("avgLoss")} ${s.avgLoss}`),
        h("div", { class: "rv-counts" },
          ...(["best", "inaccuracy", "mistake", "blunder"] as Verdict[])
            .filter((k) => s.counts[k] > 0)
            .map((k) => h("span", { class: `rv-chip ${VERDICT_MARK[k].cls}` },
              `${VERDICT_MARK[k].sym || "\u2713"} ${s.counts[k]}`)),
        ),
      );
    // What the currently-shown move cost, so the panel answers the question the
    // player is actually asking as they step through.
    const ply = (this.viewPly ?? this.moves.length) - 1;
    const cur = r.plies.find((p) => p.ply === ply);
    this.reviewEl.replaceChildren(
      h("div", { class: "rv-sides" },
        side(this.rec?.redName ?? t("red"), r.red),
        side(this.rec?.blackName ?? t("black"), r.black),
      ),
      cur
        ? h("div", { class: "rv-move" },
            h("b", { class: VERDICT_MARK[cur.verdict].cls }, t(`verdict_${cur.verdict}`)),
            ...(cur.best ? [h("span", {}, ` · ${t("betterWas")} ${uciCoord(cur.best)}`)] : []),
            ...(cur.loss > 0 ? [h("span", { class: "rv-sub" }, ` (${t("cost")} ${cur.loss})`)] : []),
          )
        : h("div", { class: "rv-move rv-sub" }, t("reviewPickMove")),
    );
  }
  setView(ply: number | null) {
    this.viewPly = ply === null ? this.moves.length : Math.max(0, Math.min(this.moves.length, ply));
    this.paint();
  }
  private async load() {
    this.rec = this.id === LOCAL_REVIEW_ID ? pendingLocalGame : await account.fetchGame(this.id);
    this.moves = this.rec ? this.rec.moves.map((m) => ({ from: m.from, to: m.to } as Move)) : [];
    this.viewPly = 0;
    this.render(false);
  }
  private headline(): string {
    const r = this.rec!;
    return `${r.redName} (${r.redRating}) ${t("vs")} ${r.blackName} (${r.blackRating})`;
  }
  private resultLine(): string {
    const r = this.rec!;
    const res = r.result === "red-wins" ? t("resultRedWin") : r.result === "black-wins" ? t("resultBlackWin") : t("resultDraw");
    return `${res} · ${t(REASON_KEY[r.reason] ?? r.reason)} · ${r.rated ? t("ratedGame") : t("casualGame")}`;
  }
  private render(loading: boolean) {
    const boardHost = h("div", { class: "board-wrap" });
    this.movesEl = h("div", { class: "moves" });
    this.titleEl = h("div", { class: "vs-label" }, loading ? "…" : this.rec ? this.headline() : t("noGames"));
    this.statusEl = h("div", { class: "substatus" }, this.rec ? this.resultLine() : "");
    this.reviewEl = h("div", { class: "review-panel" });
    navHandler = (c) => {
      const cur = this.viewPly ?? this.moves.length;
      if (c === -1) this.setView(Math.max(0, cur - 1));
      else if (c === 1) this.setView(cur + 1);
      else if (c === "start") this.setView(0);
      else this.setView(null);
    };
    app.replaceChildren(header(),
      h("div", { class: "game-layout" },
        h("div", { class: "board-col" }, boardHost),
        h("div", { class: "panel" },
          h("div", { class: "panel-top" }, h("span", { class: "vs-label" }, t("replayTitle")), soundBtn()),
          this.titleEl,
          this.statusEl,
          historyBar(this),
          this.reviewEl,
          h("div", { class: "moves-title" }, t("movesTitle")),
          this.movesEl,
          h("div", { class: "buttons" },
            h("button", { class: "btn", onclick: () => this.setView(0) }, t("reset")),
            h("a", { class: "btn", href: PATH.profile }, t("backToProfile")),
            h("a", { class: "btn ghost", href: PATH.home }, t("home")),
          ),
        ),
      ),
    );
    this.board = new BoardView(boardHost, () => {});
    this.board.orientation = Color.Red;
    this.paint();
  }
  private paint() {
    if (!this.board) return;
    const ply = this.viewPly ?? this.moves.length;
    const pos = replayTo(this.moves, ply);
    const last = ply > 0 ? this.moves[ply - 1]! : null;
    this.board.setState({ pos, lastMove: last, selectable: null, viewer: Color.Red, hideDanger: true });
    renderMoveList(this.movesEl, this, this.marks());
    // The panel names the move now on screen, so it has to follow the board.
    this.paintReview();
  }
}

// ---------------------------------------------------------------- Friends & direct challenges
let outgoingChallengeId: string | null = null;
let friendsRefresh: (() => void) | null = null;

/**
 * Ask before resigning, in the app's own modal.
 *
 * This was the browser's confirm(): a grey system box with OK and Cancel, no
 * styling, no translation of the buttons, and on some platforms a "prevent this
 * page from creating more dialogs" checkbox that can disable it outright. It
 * also blocks the whole thread, which stops the clocks being painted while it
 * is open.
 *
 * Carrying on is the confirming action and resigning is not, because the cost
 * of the two mistakes is not symmetric: dismissing this by reflex should leave
 * the game running, not end it.
 */
function confirmResign(onResign: () => void) {
  showModal(t("resignConfirm"), t("resignConfirmBody"), [
    [t("keepPlaying"), () => {}, true],
    [t("resign"), onResign],
  ], "lose");
}

function showNotice(msg: string) {
  showModal(t("friends"), msg, [[t("cancel"), () => {}, true]], "info");
}

/** Global handler for challenge lifecycle + friend updates, independent of any active game. */
function handleGlobal(m: ServerMsg) {
  switch (m.t) {
    case "start":
      closeModal();
      outgoingChallengeId = null;
      if (!remote) { remote = new RemoteGame("friends"); remote.handle(m); }
      break;
    case "lobby":
      renderLobby(m);
      break;
    case "liveGames":
      renderLiveGames(m.games);
      break;
    case "leaderboard":
      renderLeaderboard(m.rows);
      break;
    case "challenged":
      showModal(t("incomingChallenge"), `${m.fromName} ${t("challengedYou")} · ${tcLabel(m.tc)}`, [
        [t("accept"), () => { ensureNet(); net!.send({ t: "acceptChallenge", id: m.id }); }, true],
        [t("decline"), () => net?.send({ t: "declineChallenge", id: m.id })],
      ], "info");
      break;
    case "challengeSent":
      outgoingChallengeId = m.id;
      break;
    case "challengeDeclined":
      closeModal(); outgoingChallengeId = null;
      showNotice(t("challengeDeclinedMsg"));
      break;
    case "challengeCanceled":
      closeModal(); outgoingChallengeId = null;
      friendsRefresh?.();
      break;
    case "friendsUpdate":
      friendsRefresh?.();
      break;
    case "notify":
      inbox.notifications.unshift(m.n);
      inbox.unread = m.unread;
      paintBellBadges();
      break;
    case "dm":
      inbox.unreadMessages = m.unread;
      threadRefresh?.(m.m);
      break;
    case "error":
      if (handledUnverified(m)) break;
      // Only surface here when no game controller is present to show it — the
      // playing board and the watch board both report their own errors.
      if (!remote && !watchView) { closeModal(); showNotice(m.msg); }
      break;
  }
}

function challengeFriend(username: string) {
  ensureNet();
  outgoingChallengeId = null;
  net!.send({ t: "challenge", to: username, tc: getTc() });
  track("challenge_sent", { tc: getTc() });
  showModal(t("challenge"), `${t("waitingForAccept")} · ${tcLabel(getTc())}`, [
    [t("cancelChallenge"), () => { if (outgoingChallengeId) net?.send({ t: "cancelChallenge", id: outgoingChallengeId }); outgoingChallengeId = null; }],
  ], "info");
}

// ---------------------------------------------------------------- messages
/** Set while a thread is open, so an incoming message lands in it live. */
let threadRefresh: ((m: account.DirectMessage) => void) | null = null;

function showMessages() {
  closeModal();
  navHandler = null;
  threadRefresh = null;
  enter("messages", PATH.messages, t("messages"));
  if (!account.isLoggedIn()) { showAuthFor(() => showMessages()); return; }

  const list = h("div", { class: "conv-list" }, ...skeletonRows(4));
  app.replaceChildren(
    header(),
    h("div", { class: "lessons" },
      h("div", { class: "lessons-head" }, h("h2", {}, t("messages")), h("span", { class: "progress" }, t("messagesSub"))),
      list,
      h("div", { class: "setup-actions" },
        h("a", { class: "btn ghost", href: PATH.friends }, t("friends")),
        h("a", { class: "btn ghost", href: PATH.home }, t("home")),
      ),
    ),
  );

  void account.fetchConversations().then(({ conversations }) => {
    inbox.unreadMessages = conversations.reduce((n, c) => n + c.unread, 0);
    if (!conversations.length) {
      list.replaceChildren(h("div", { class: "empty-state" },
        h("div", { class: "empty-glyph" }, "信"),
        h("div", { class: "empty-title" }, t("messages")),
        h("div", { class: "empty-sub" }, t("noConversations")),
        h("a", { class: "btn primary", href: PATH.friends }, t("friends")),
      ));
      return;
    }
    list.replaceChildren(...conversations.map((c) =>
      h("a", { class: "conv" + (c.unread ? " unread" : ""), href: PATH.thread(c.user) },
        h("span", { class: "conv-avatar" }, (c.displayName || "?").slice(0, 1).toUpperCase()),
        h("span", { class: "conv-body" },
          h("span", { class: "conv-top" },
            h("span", { class: "conv-name" }, c.displayName),
            h("span", { class: "conv-ago" }, timeAgo(c.lastAt)),
          ),
          h("span", { class: "conv-last" }, (c.outgoing ? "↩ " : "") + c.lastText),
        ),
        ...(c.unread ? [h("span", { class: "conv-badge" }, String(c.unread))] : []),
      ),
    ));
  });
}

function showThread(user: string) {
  closeModal();
  navHandler = null;
  enter("thread", PATH.thread(user), t("messages"));
  if (!account.isLoggedIn()) { showAuthFor(() => showThread(user)); return; }

  let me = account.currentProfile()?.username ?? "";
  const log = h("div", { class: "thread-log" }, h("div", { class: "dim" }, "…"));
  const input = h("input", { class: "name-input", type: "text", maxlength: "1000", placeholder: t("writeMessage") }) as HTMLInputElement;
  const err = h("div", { class: "substatus" });
  const title = h("h2", {}, user);

  const bubble = (m: account.DirectMessage) =>
    h("div", { class: "bubble" + (m.from === me ? " mine" : "") },
      h("span", { class: "bubble-text" }, m.text),
      h("span", { class: "bubble-ago" }, timeAgo(m.at)),
    );
  const append = (m: account.DirectMessage) => {
    if (log.querySelector(".dim")) log.replaceChildren();
    log.append(bubble(m));
    log.scrollTop = log.scrollHeight;
  };

  const send = async () => {
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    const r = await account.postMessage(user, text);
    if (!r.ok) { err.className = "substatus alert"; err.textContent = r.error ?? ""; return; }
    err.textContent = "";
    if (r.message) append(r.message);
  };
  input.addEventListener("keydown", (e: KeyboardEvent) => { if (e.key === "Enter") void send(); });

  app.replaceChildren(
    header(),
    h("div", { class: "lessons thread-page" },
      h("div", { class: "lessons-head" }, title, h("a", { class: "progress link-btn", href: PATH.messages }, t("backToMessages"))),
      log,
      h("div", { class: "add-friend-row" }, input, h("button", { class: "btn primary", onclick: () => void send() }, t("sendMessage"))),
      err,
      h("div", { class: "setup-actions" },
        h("a", { class: "btn ghost", href: PATH.player(user) }, t("profile")),
        h("a", { class: "btn ghost", href: PATH.messages }, t("backToMessages")),
      ),
    ),
  );

  // Anything arriving for this person while the thread is open shows at once.
  threadRefresh = (m) => { if (m.from === user.toLowerCase() || m.to === user.toLowerCase()) append(m); };

  void (async () => {
    // Who I am has to be settled before drawing, or my own messages come out
    // looking like the other person's.
    await whenProfileKnown();
    me = account.currentProfile()?.username ?? me;
    const { messages, with: who } = await account.fetchThread(user);
    if (who) title.textContent = who.displayName;
    inbox.unreadMessages = Math.max(0, inbox.unreadMessages - messages.filter((m) => m.to === me && !m.read).length);
    paintBellBadges();
    if (!messages.length) { log.replaceChildren(h("div", { class: "dim" }, t("noConversations"))); return; }
    log.replaceChildren(...messages.map(bubble));
    log.scrollTop = log.scrollHeight;
  })();
}

function showFriends() {
  closeModal();
  enter("friends", PATH.friends, t("friends"));
  navHandler = null;
  ensureNet(); // so presence + challenges flow while this screen is open
  new FriendsView();
}

class FriendsView {
  private graph: account.FriendGraph = { friends: [], incoming: [], outgoing: [] };
  private root: HTMLElement | null = null;
  private addInput!: HTMLInputElement;
  private msgEl!: HTMLElement;

  constructor() {
    this.renderShell();
    friendsRefresh = () => void this.load();
    void this.load();
  }
  private async load() {
    this.graph = await account.fetchFriends();
    this.paint();
  }
  private renderShell() {
    if (!account.isLoggedIn()) {
      app.replaceChildren(header(),
        h("div", { class: "lessons" },
          h("div", { class: "panel-card profile-empty" },
            h("div", { class: "lesson-title" }, t("friends")),
            h("div", { class: "lesson-task" }, t("signInForFriends")),
            h("div", { class: "buttons" },
              h("a", { class: "btn primary", href: PATH.login }, t("logIn")),
              h("a", { class: "btn", href: PATH.signup }, t("signUp")),
              h("a", { class: "btn ghost", href: PATH.home }, t("home")),
            ),
          ),
        ),
      );
      return;
    }
    this.addInput = h("input", { class: "name-input", type: "text", maxlength: "16", placeholder: t("friendUsername") }) as HTMLInputElement;
    this.addInput.addEventListener("keydown", (e: KeyboardEvent) => { if (e.key === "Enter") void this.add(); });
    this.msgEl = h("div", { class: "substatus" });
    this.root = h("div", { class: "friends-body" });
    app.replaceChildren(header(),
      h("div", { class: "lessons profile-wrap" },
        h("div", { class: "moves-title" }, t("addFriend")),
        h("div", { class: "add-friend-row" }, this.addInput, h("button", { class: "btn primary", onclick: () => void this.add() }, t("send"))),
        this.msgEl,
        this.root,
        h("div", { class: "buttons" }, h("a", { class: "btn ghost", href: PATH.home }, t("home"))),
      ),
    );
    this.paint();
  }
  private async add() {
    const u = this.addInput.value.trim();
    if (!u) return;
    const r = await account.requestFriend(u);
    this.msgEl.className = "substatus" + (r.ok ? "" : " alert");
    this.msgEl.textContent = r.ok ? t("requestSent") : (r.error ?? "");
    if (r.ok) this.addInput.value = "";
    void this.load();
  }
  private reqRow(r: account.FriendReq, actions: HTMLElement[]): HTMLElement {
    return h("div", { class: "gh-row friend-row" },
      h("span", { class: "gh-badge" }, (r.displayName || "?").slice(0, 1).toUpperCase()),
      h("span", { class: "gh-players" }, r.displayName),
      ...actions,
    );
  }
  private paint() {
    if (!this.root) return;
    const g = this.graph;
    const out: Node[] = [];
    if (g.incoming.length) {
      out.push(h("div", { class: "moves-title" }, `${t("requests")} (${g.incoming.length})`));
      for (const r of g.incoming) out.push(this.reqRow(r, [
        h("button", { class: "btn small primary", onclick: async () => { await account.acceptFriend(r.username); void this.load(); } }, t("accept")),
        h("button", { class: "btn small ghost", onclick: async () => { await account.declineFriend(r.username); void this.load(); } }, t("decline")),
      ]));
    }
    out.push(h("div", { class: "moves-title" }, `${t("friends")} (${g.friends.length})`));
    if (!g.friends.length) out.push(h("div", { class: "dim" }, t("noFriends")));
    for (const f of g.friends) {
      const status = f.inGame ? t("inGameNow") : f.online ? t("online") : t("offline");
      const dot = f.inGame ? "busy" : f.online ? "on" : "off";
      out.push(h("div", { class: "gh-row friend-row" },
        h("span", { class: `pres-dot ${dot}` }),
        h("span", { class: "gh-players" },
          h("a", { class: "gh-red-name link", href: PATH.player(f.username) }, f.displayName),
          h("span", { class: "gh-reason" }, ` · ${f.rating}`)),
        h("span", { class: "gh-reason" }, status),
        f.online && !f.inGame
          ? h("button", { class: "btn small primary", onclick: () => challengeFriend(f.username) }, t("challenge"))
          : h("span", {}),
        h("a", { class: "btn small", href: PATH.thread(f.username) }, t("message")),
        h("button", { class: "btn small ghost", onclick: async () => { await account.unfriend(f.username); void this.load(); } }, t("remove")),
      ));
    }
    if (g.outgoing.length) {
      out.push(h("div", { class: "moves-title" }, `${t("sentRequests")} (${g.outgoing.length})`));
      for (const r of g.outgoing) out.push(this.reqRow(r, [
        h("span", { class: "gh-reason" }, "…"),
        h("button", { class: "btn small ghost", onclick: async () => { await account.declineFriend(r.username); void this.load(); } }, t("cancelChallenge")),
      ]));
    }
    this.root.replaceChildren(...out);
  }
}

initCinematic();
initAnalytics({ language: getLang(), piece_style: getPieceStyle(), site: "play" });
armDiscordPrompt();
// If a token exists, load the profile (and adopt the display name) before boot.
void account.refreshProfile().then((p) => {
  // The nav chip and the home tiles both read the profile, so repaint home once
  // it lands (the token alone already unlocked play — this only fills in names).
  if (!p) return;
  nickname = p.displayName;
  // Someone who declined or wrote to us on another device must not be asked
  // here. Adopted before the scheduler can reach fifteen active minutes.
  adoptServerChoice(p.feedbackPrompt ?? null);
  for (const id of p.lessonsDone) markDoneLocal(id);
  // Every screen draws the header, so patch it in place rather than only fixing
  // home: otherwise the chip reads "Guest" anywhere else until you navigate.
  for (const el of document.querySelectorAll(".acct-chip-name")) el.textContent = p.displayName;
  // The initial too: it was drawn from "Guest" and stayed a "G" beside the
  // right name until the next navigation.
  for (const el of document.querySelectorAll(".acct-avatar")) el.textContent = [...p.displayName.trim()][0]?.toUpperCase() ?? "?";
  for (const el of document.querySelectorAll(".acct-chip-rating")) {
    el.textContent = String(p.rating);
    (el as HTMLElement).classList.remove("hide");
  }
  // Both pages carry the unconfirmed-email banner, and neither can draw it
  // until the profile has actually landed, so repaint whichever is on screen.
  if (app.querySelector(".home")) showHome();
  // The leaderboard highlights your own row, which it could not know before now.
  else if (leaderboardTarget && lastLeaderRows) renderLeaderboard(lastLeaderRows);
  else if (app.querySelector(".play-hub")) showPlay();
  profileReady?.();
  // Push subscriptions are stored against an account, so whoever is signed in
  // now must be the one this browser's subscription belongs to.
  void syncPushSubscription();
});

/** Screens that need to know who "I" am can wait for the boot profile fetch. */
let profileReady: (() => void) | null = null;
function whenProfileKnown(): Promise<void> {
  if (account.currentProfile() || !account.isLoggedIn()) return Promise.resolve();
  return new Promise((res) => {
    const prev = profileReady;
    profileReady = () => { prev?.(); res(); };
    setTimeout(res, 4000); // never hang a screen on it
  });
}

// ---------------------------------------------------------------- pages
let resumeFallback: ReturnType<typeof setTimeout> | null = null;
function clearResumeFallback() { if (resumeFallback) { clearTimeout(resumeFallback); resumeFallback = null; } }

/**
 * /game — the board you are playing on right now. Reached from a link or from a
 * cold load (a refresh mid-game lands here), so it may have to wait for the
 * server to hand the game back before it can paint anything.
 */
function showLiveGame() {
  if (remote?.liveSnapshot()) { remote.reopen(); return; }
  if (!account.isLoggedIn()) { showHome(); return; }
  screen("resuming");
  app.replaceChildren(header("play"), h("div", { class: "setup" }, h("h2", {}, t("resumingGame"))));
  if (!remote) remote = new RemoteGame(localStorage.getItem(LAST_MODE) === "friends" ? "friends" : "online", true);
  ensureNet();
  clearResumeFallback();
  resumeFallback = setTimeout(() => {
    resumeFallback = null;
    dismissLoader();
    if (!remote?.liveSnapshot()) navigate(PATH.home, true); // nothing to resume
  }, 2500);
}

/** /game/computer — rebuilt from the moves saved in localStorage after a refresh. */
function showBotGame() {
  try {
    const raw = localStorage.getItem(RESUME_BOT);
    const st = raw ? JSON.parse(raw) as { level: Level; human: Color; moves: Move[] } : null;
    if (st?.moves?.length) { new LocalGame(st.level, st.human, st.moves); return; }
  } catch { /* nothing to resume */ }
  showBotSetup(); // no saved game: pick a level first
}

/** A real 404: the address stays, and crawlers are told not to index it. */
function showNotFound(path: string) {
  closeModal();
  navHandler = null;
  enter("not_found", path, t("notFoundTitle"), true);
  app.replaceChildren(header(),
    h("div", { class: "lessons" },
      h("div", { class: "panel-card profile-empty" },
        h("div", { class: "lesson-title" }, t("notFoundTitle")),
        h("div", { class: "lesson-task" }, t("notFoundBody")),
        h("div", { class: "dim" }, path),
        h("div", { class: "buttons" },
          h("a", { class: "btn primary", href: PATH.home }, t("home")),
          h("a", { class: "btn", href: PATH.play }, t("navPlay")),
          h("a", { class: "btn ghost", href: PATH.learn }, t("navLearn")),
        ),
      ),
    ),
  );
}

/**
 * The site map. Order matters only where a fixed segment would otherwise be
 * eaten by a parameter (/game/computer before /game/:id).
 */
const ROUTES: Route[] = [
  { path: PATH.home, render: () => showHome() },
  // Not gated: the lobby renders its own sign-up prompt for a guest and
  // gates the two actions that need an account, and the bot game needs none.
  { path: PATH.play, render: () => showPlay() },
  { path: PATH.bot, render: () => showBotSetup() },
  { path: PATH.private, render: gated(() => showFriendsSetup()) },
  { path: PATH.botGame, render: () => showBotGame() },
  { path: PATH.game, render: gated(() => showLiveGame()) },
  // Gated for a stored game, which belongs to an account -- but not for the
  // local one, which is a game the player just finished in this tab and never
  // sent anywhere. Sending them to sign in to look at their own game would be
  // absurd, and they may well have no account at all.
  { path: "/game/:id", render: (p) => {
    if (p.id === LOCAL_REVIEW_ID) { showReplay(p.id); return; }
    gated(() => showReplay(p.id!))(p);
  } },
  { path: "/join/:code", render: gated((p) => { page(PATH.join(p.code!), t("friendsTitle")); joinRoom((p.code ?? "").toUpperCase()); }) },
  // Spectating. Both pages name the players, so both are behind the gate: a
  // shared watch link signs you in first and then opens the board.
  { path: PATH.watch, render: gated(() => showWatch()) },
  { path: "/watch/:id", render: gated((p) => showWatchGame(p.id!)) },
  // The academy is open to everyone — lessons, puzzles, analysis and the rules
  // reference read and run entirely on the device, and progress is kept in
  // localStorage. Signing in only adds syncing that progress to an account, so
  // there is nothing here worth stopping a visitor for.
  { path: PATH.learn, render: () => showLearn() },
  { path: PATH.lessons, render: () => showLessons() },
  { path: "/learn/lessons/:id", render: (p) => {
      if (!LESSONS.some((l) => l.id === p.id)) { showNotFound(PATH.lesson(p.id!)); return; }
      new LessonView(p.id!);
    } },
  { path: PATH.puzzles, render: () => showPuzzles() },
  // One puzzle, chosen by the date. Deliberately not gated: the point is
  // that somebody can be sent the link and just do it.
  { path: PATH.daily, render: () => { closeModal(); track("select_mode", { mode: "daily" }); new PuzzleView(undefined, true); } },
  { path: "/learn/puzzles/:id", render: (p) => {
      if (!PUZZLES.some((z) => z.id === p.id)) { showNotFound(PATH.puzzle(p.id!)); return; }
      showPuzzles(p.id);
    } },
  { path: PATH.rules, render: () => showGuide() },
  { path: PATH.analysis, render: () => showAnalysis() },
  { path: PATH.leaderboard, render: gated(() => showLeaderboard()) },
  { path: PATH.friends, render: gated(() => showFriends()) },
  { path: PATH.profile, render: gated(() => showProfile()) },
  { path: "/player/:name", render: gated((p) => showProfile(p.name)) },
  // Settings is an account page: signed out it is not rendered at all, the same
  // as /profile above. The device preferences it also carries (piece style,
  // scene, sound) come back the moment you sign in; dark mode stays reachable
  // signed out from the header's own toggle, and language still follows
  // `?lang=` and the browser's own setting.
  // Open to guests: nothing on the page is an account's except the email and
  // push group, which it already leaves out when nobody is signed in. A guest
  // can play the computer, the lessons and the puzzles, so a guest has boards
  // to choose a look for.
  { path: PATH.settings, render: () => showSettings() },
  { path: PATH.messages, render: gated(() => showMessages()) },
  { path: "/messages/:user", render: gated((p) => showThread(p.user!)) },
  { path: PATH.login, render: () => showAuth("login") },
  { path: PATH.signup, render: () => showAuth("signup") },
  { path: PATH.forgot, render: () => showForgot() },
  // Signed out this is meaningless -- there is no account to confirm -- so the
  // usual gate sends them to sign in first and brings them straight back.
  { path: PATH.verify, render: gated(() => showVerify()) },
];

/**
 * A message the sign-in screen should show as soon as it paints — set when a
 * Google round-trip came back with something to say. Read once, then cleared.
 */
let oauthMessage = "";

/**
 * Pick up a return from "Continue with Google".
 *
 * The callback lands on /login carrying a one-time handoff in the fragment.
 * Fragments never reach a server, which is why the code travels there and the
 * session token does not travel at all — it is fetched over POST in exchange.
 *
 * The fragment is stripped from the address bar before anything else: the code
 * is spent on first use, so leaving it in the URL only means that a reload, a
 * bookmark or a shared link fails with a confusing error.
 */
function consumeOAuthReturn(): void {
  let raw = "";
  try { raw = location.hash.replace(/^#/, ""); } catch { return; }
  if (!raw) return;
  const p = new URLSearchParams(raw);
  const code = p.get("g");
  const err = p.get("gerr");
  const cancelled = p.get("gcancel");
  if (!code && !err && !cancelled) return;

  try { history.replaceState(null, "", location.pathname + location.search); } catch { /* ignore */ }

  // Where the visitor was headed before the gate sent them here. It could not
  // survive the trip through Google in memory, so the button parked it.
  let next = "";
  try {
    next = sessionStorage.getItem("xq-oauth-next") || "";
    sessionStorage.removeItem("xq-oauth-next");
  } catch { /* ignore */ }

  if (cancelled) { oauthMessage = t("googleCancelled"); return; }
  if (err) { oauthMessage = err; return; }

  void (async () => {
    try {
      const prof = await account.googleExchange(code!, [...doneSet()]);
      for (const id of prof.lessonsDone) markDoneLocal(id);
      nickname = prof.displayName;
      net?.reconnectFresh(); // re-hello with the token so rating binds to the account
      track("login", { method: "google" });
      gateTarget = next || null;
      afterAuth();
    } catch (e) {
      oauthMessage = (e as Error).message;
      showAuth("login");
    }
  })();
}

/**
 * Boot. The URL decides what is painted, and a game in progress is never lost:
 *  - a bot game is rebuilt from the moves saved in localStorage (/game/computer);
 *  - an online/friend game resumes from the server, which resends the position
 *    as soon as we say hello with the same player id, and puts the player back
 *    on /game wherever they had wandered to.
 */
function boot() {
  // Read the Google return before anything paints: it decides whether this load
  // is a sign-in finishing rather than an ordinary visit to /login.
  consumeOAuthReturn();

  // Any lesson text edited in the console. Not awaited: the built-in catalogue
  // renders immediately and the overrides land a moment later, so a slow or
  // failed request costs nothing.
  void applyLessonOverrides();
  void applyPuzzleOverrides();

  // Signed out you cannot play at all, so a saved board must not be restored —
  // clear it and let the home screen ask for an account.
  if (!account.isLoggedIn()) {
    try { localStorage.removeItem(RESUME_BOT); } catch { /* ignore */ }
  }

  startRouter({
    routes: ROUTES,
    notFound: showNotFound,
    brand: () => t("brandName"),
    before: () => { closeMenus(); unlockAudio(); },
    // A game in progress is a private, sticky room: every way out asks first,
    // including the back button. Returning to the board itself is always fine.
    canLeave: (to) => {
      if (to === PATH.game || to === PATH.botGame || !gameLive()) return true;
      guardLeaveGame(() => navigate(to));
      return false;
    },
    onPage: pageView,
  });

  // Refreshing or closing the tab is a way out too. The browser only lets us
  // show its own generic wording, but it does stop the accident.
  window.addEventListener("beforeunload", (e) => {
    if (gameLive()) { e.preventDefault(); e.returnValue = ""; }
  });
  // Closing or reloading Settings mid-edit must not keep what was never saved.
  window.addEventListener("pagehide", () => {
    if (settingsBaseline && JSON.stringify(captureSettings()) !== JSON.stringify(settingsBaseline)) revertSettings(settingsBaseline);
  });

  // Ask the server whether a game of ours is still running. If one is,
  // RemoteGame drops the player straight back onto /game (see setSnapshot).
  if (account.isLoggedIn() && !remote) {
    remote = new RemoteGame(localStorage.getItem(LAST_MODE) === "friends" ? "friends" : "online", true);
    ensureNet();
  }
  /* Ask for feedback, eventually, and only at a decent moment. Started last so
     nothing about it can delay the first paint; it does nothing at all for the
     first fifteen ACTIVE minutes, and nothing ever for somebody who has
     already answered. */
  if (!feedbackAnswered()) {
    startFeedbackPrompt({ isBusy: feedbackMomentIsBad, show: showFeedbackAsk });
  }

  dismissLoader();
}
boot();
setTimeout(dismissLoader, 2600); // safety net
